import { AfterViewInit, Component, ElementRef, Input, Output, EventEmitter, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type { HttpErrorResponse } from '@angular/common/http';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom, Subscription, timeout } from 'rxjs';
import { ICreditUsageEvent, IEvalAnnotation } from '@platfio/interfaces/platfio';
import { ChatDeviceCommandTarget, ChatDisplayMode, ChatMessageUpdateEvent, ChatPreviewKind, ChatPreviewOutlet, ChatPreviewPresentation, ChatThreadAssetItem, ModelOption, TransformedChatMessage } from '../interfaces/chat-component.interfaces';
import { formatModelDisplayName, formatUsd, getMessageCostBreakdown } from '../utilities/token-usage.utils';
import { UiModalButton, UiModalService } from 'src/app/components/elements/ui-modal/ui-modal.service';
import { UiPopoverService } from 'src/app/components/elements/ui-popover/ui-popover.service';
import { ICta } from 'src/app/interfaces/cta';
import { ISuperCta } from 'src/app/interfaces/super-cta';
import { IUtility, IUtilityChange } from 'src/app/interfaces/utility';
import { ThemeService } from 'src/app/services/theme.service';
import { AGENT_BACKEND_URLS } from 'src/app/services/agent-backend-target.service';
import { ArtifactZipPreviewComponent, ZipPreviewViewerState } from '../artifact-zip-preview/artifact-zip-preview.component';
import { AlertService } from 'src/app/services/alert.service';
import { EvalAnnotationSavedEvent, EvalAnnotationService, EvalAnnotationThumbRating, EvalAnnotationTarget } from 'src/app/services/eval-annotation.service';

interface ToolArtifactResource {
  id: string;
  title: string;
  subtitle: string;
  sizeBytes?: number;
  artifactId?: string;
  relativePath?: string;
  mimeType?: string;
  kind?: string;
  archiveFormat?: string;
  uri?: string;
  storageUri?: string;
  persistedPath?: string;
}

interface ToolPreviewResource {
  id: string;
  title: string;
  subtitle?: string;
  url: string;
  safeUrl: SafeResourceUrl;
  height: number;
  previewType?: string;
  emulatorMode?: 'twocol' | 'manager' | 'demo';
  defaultZoom?: number;
}

interface ArtifactPreviewResponse {
  artifact?: {
    id?: string;
    name?: string;
    relativePath?: string;
    mimeType?: string;
    kind?: string;
    archiveFormat?: string;
    downloadUrl?: string;
    storageUri?: string;
  };
  content?: {
    text?: string;
    truncated?: boolean;
    sizeBytes?: number;
    encoding?: string;
    source?: string;
  };
}

interface ArtifactUpdateResponse {
  artifact?: {
    id?: string;
    name?: string;
    relativePath?: string;
    mimeType?: string;
    kind?: string;
    archiveFormat?: string;
    downloadUrl?: string;
    storageUri?: string;
    previewText?: string;
    previewTruncated?: boolean;
    sizeBytes?: number;
  };
  content?: {
    text?: string;
    truncated?: boolean;
    sizeBytes?: number;
    encoding?: string;
    source?: string;
  };
  savedAt?: string;
  warnings?: string[];
}

interface ArtifactPreviewState {
  type: 'markdown' | 'csv' | 'html' | 'svg' | 'text' | 'image' | 'audio' | 'video' | 'zip' | 'file';
  content: string;
  truncated: boolean;
  sizeBytes: number;
  rows?: string[][];
  fileUrl?: string;
}

interface InlineArtifactPreviewState {
  artifact: ToolArtifactResource;
  preview: ArtifactPreviewState | null;
  loading: boolean;
  error: string | null;
  apiBaseUrl: string | null;
}

type ArtifactPreviewMode = 'preview' | 'raw';

interface ZipArtifactSavedEvent {
  downloadUrl: string;
  sizeBytes: number;
}

interface ZipArtifactDeletedEvent {
  storageTarget: string;
}

interface ArtifactDeletedEvent {
  storageTarget: string;
}

interface MinimalToolHistoryItem {
  message: any;
  index: number;
}

interface GeneratedAudioMedia {
  id?: string;
  url: string;
  name?: string;
  mimeType?: string;
  transcript?: string;
  stateKey: string;
}

interface GeneratedAudioState {
  currentTime: number;
  duration: number | null;
  isPlaying: boolean;
  playbackRate: number;
  loadFailed: boolean;
}

interface GeneratedImageMedia {
  id?: string;
  url: string;
  name?: string;
  altText?: string;
  width?: number;
  height?: number;
  sourceLabel?: string;
  sourceHref?: string;
  attribution?: string;
  photographerName?: string;
  photographerUsername?: string;
}

interface ActiveMessageCreditDebug {
  iteration: number | null;
  associationLabel: string;
  associationHint: string;
  modelKey: string;
  modelDisplayName: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  computeMilliseconds: number;
  computeSeconds: number;
  modelCredits: number;
  computeCredits: number;
  totalCredits: number;
  modelCostUsd: number;
  computeCostUsd: number;
  totalCostUsd: number;
  hasCreditEvent: boolean;
}

interface ComputeMonitoringBackgroundProcess {
  pid?: number;
  command: string;
  cwd?: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  status: 'running' | 'stopped' | 'missing' | 'unknown';
}

interface ComputeMonitoringForegroundCommand {
  command: string;
  executionMode: 'sequential' | 'parallel';
  cwd: string;
  startedAt?: string | null;
  updatedAt?: string | null;
  finishedAt?: string | null;
  status: 'running' | 'completed' | 'failed' | 'timed_out';
  totalCommands: number;
  completedCommands: number;
  exitCode?: number | null;
  outputTail?: string;
}

interface ComputeMonitoringSession {
  threadId: string;
  sandboxId: string;
  status: string;
  currentWorkingDirectory?: string;
  lastCommandAt?: string | null;
  lastPersistedAt?: string | null;
  lastResumedAt?: string | null;
  pausedAt?: string | null;
  updatedAt?: string | null;
  activeBackgroundProcesses: ComputeMonitoringBackgroundProcess[];
  foregroundCommand: ComputeMonitoringForegroundCommand | null;
}

interface ComputeMonitoringResponse {
  threadId: string;
  available: boolean;
  session: ComputeMonitoringSession | null;
}

type MessageDetailsTabId = 'usage' | 'details' | 'terminal';

interface MessageDetailsTab {
  id: MessageDetailsTabId;
  label: string;
}

interface TerminalHistorySection {
  id: string;
  eyebrow: string;
  title: string;
  subtitle?: string;
  output: string;
  tone?: 'default' | 'muted';
}

interface MessageAnnotationSummary {
  thumbRating: EvalAnnotationThumbRating | '';
  starRating: number | null;
  hasNote: boolean;
  note: string;
}

function normalizeArtifactResourceKey(value: string | undefined): string {
  return String(value || '').trim().toLowerCase();
}

function mergeArtifactResource(
  existing: ToolArtifactResource | undefined,
  next: ToolArtifactResource
): ToolArtifactResource {
  return {
    ...(existing || {} as ToolArtifactResource),
    ...next,
    id: next.id || existing?.id || '',
    title: next.title || existing?.title || 'Artifact',
    subtitle: next.subtitle || existing?.subtitle || 'Artifact'
  };
}

function getOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function getOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

@Component({
  selector: 'app-chat-messages-area',
  templateUrl: './chat-messages-area.component.html',
  styleUrl: './chat-messages-area.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatMessagesAreaComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() history: any[] = []; // Raw messages from database
  @Input() currentThreadId: string | null = null;
  @Input() currentThread: any | null = null;
  @Input() loading = false;
  @Input() backendUrl: string | null = null;
  @Input() backendEnvironment: 'production' | 'staging' = 'production';
  @Input() availableModels: ModelOption[] = [];
  @Input() parseMarkdown!: (text: string) => string;
  @Input() displayMode: ChatDisplayMode = 'standard';
  @Input() requestedDetachedPreviewKind: ChatPreviewKind | null = null;
  @Input() requestedDetachedPreviewId: string | null = null;
  @Input() previewPresentation: ChatPreviewPresentation = 'modal';
  @Input() renderPreviewsInline = false;
  @Output() deleteMessage = new EventEmitter<{ messageIndex: number, messageId?: string }>();
  @Output() updateMessage = new EventEmitter<ChatMessageUpdateEvent>();
  @Output() deviceCommandRequested = new EventEmitter<ChatDeviceCommandTarget>();
  @Output() previewOutletChange = new EventEmitter<ChatPreviewOutlet | null>();
  @Output() suggestedFollowUpSelected = new EventEmitter<string>();
  @Output() threadAssetsChanged = new EventEmitter<ChatThreadAssetItem[]>();

  @ViewChild('messageDetailsTemplate') messageDetailsTemplate?: TemplateRef<any>;
  @ViewChild('artifactPreviewTemplate') artifactPreviewTemplate?: TemplateRef<any>;
  @ViewChild('artifactPreviewActionsTemplate') artifactPreviewActionsTemplate?: TemplateRef<any>;
  @ViewChild('generatedImagePreviewTemplate') generatedImagePreviewTemplate?: TemplateRef<any>;
  @ViewChild('generatedImagePreviewActionsTemplate') generatedImagePreviewActionsTemplate?: TemplateRef<any>;
  @ViewChild('generatedImageOptionsPopoverTemplate') generatedImageOptionsPopoverTemplate?: TemplateRef<any>;
  @ViewChild('generatedAudioPreviewTemplate') generatedAudioPreviewTemplate?: TemplateRef<any>;
  @ViewChild('generatedAudioPreviewActionsTemplate') generatedAudioPreviewActionsTemplate?: TemplateRef<any>;
  @ViewChild('generatedAudioOptionsPopoverTemplate') generatedAudioOptionsPopoverTemplate?: TemplateRef<any>;
  @ViewChild('artifactSelectorPopoverTemplate') artifactSelectorPopoverTemplate?: TemplateRef<any>;
  @ViewChild('minimalToolHistoryPopoverTemplate') minimalToolHistoryPopoverTemplate?: TemplateRef<any>;
  @ViewChild('previewExpandedTemplate') previewExpandedTemplate?: TemplateRef<any>;
  @ViewChild('previewHeaderActionsTemplate') previewHeaderActionsTemplate?: TemplateRef<any>;
  @ViewChild(ArtifactZipPreviewComponent) artifactZipPreviewComponent?: ArtifactZipPreviewComponent;
  // Track which details modal is currently open
  openPopoverIndex: number | null = null;
  activeMessage: any | null = null;
  activeMessageDetailsTab: MessageDetailsTabId = 'details';
  activeMessageDetailTabs: MessageDetailsTab[] = [];
  activeMessageTraceLoading = false;
  activeMessageTraceError = '';
  annotationSavingKeys = new Set<string>();
  annotationSavedKey = '';
  annotationErrorKey = '';
  messageAnnotationSummaries = new Map<string, MessageAnnotationSummary>();
  activePreviewResource: ToolPreviewResource | null = null;
  activeArtifact: ToolArtifactResource | null = null;
  activeGeneratedImage: GeneratedImageMedia | null = null;
  activeGeneratedImagePopover: GeneratedImageMedia | null = null;
  activeGeneratedAudio: GeneratedAudioMedia | null = null;
  activeGeneratedAudioPopover: GeneratedAudioMedia | null = null;
  activeArtifactPopoverResources: ToolArtifactResource[] = [];
  activeMinimalToolHistoryItems: MinimalToolHistoryItem[] = [];
  editingMessageKey: string | null = null;
  editingMessageDraft = '';
  artifactPreview: ArtifactPreviewState | null = null;
  artifactPreviewError: string | null = null;
  artifactPreviewLoading = false;
  artifactPreviewMode: ArtifactPreviewMode = 'preview';
  artifactRawDraft = '';
  artifactRawOriginal = '';
  artifactRawContentLoaded = false;
  artifactRawLoading = false;
  artifactRawSaving = false;
  artifactRawSaveError: string | null = null;
  artifactRawLastSavedAt: string | null = null;
  artifactDeleteError: string | null = null;
  computeMonitoring: ComputeMonitoringSession | null = null;
  computeMonitoringLoading = false;
  computeMonitoringError: string | null = null;
  previewModalZoom = 1;
  private readonly localAgentBackendUrl = AGENT_BACKEND_URLS.local;
  private readonly productionAgentBackendUrl = AGENT_BACKEND_URLS.production;
  private readonly stagingAgentBackendUrl = AGENT_BACKEND_URLS.staging;
  private readonly artifactPreviewRequestTimeoutMs = 10000;
  private readonly artifactSaveRequestTimeoutMs = 30000;
  private readonly allowedPreviewHosts = new Set(['account.platfio.com', 'test.account.platfio.com', 'localhost', '127.0.0.1']);
  private readonly previewUrlCache = new Map<string, SafeResourceUrl>();
  private readonly previewZoomById = new Map<string, number>();
  private readonly inlineArtifactPreviewStates = new Map<string, InlineArtifactPreviewState>();
  private readonly inlineArtifactPreviewLoadingKeys = new Set<string>();
  private readonly collapsedInlinePreviewKeys = new Set<string>();
  private readonly artifactDeletingKeys = new Set<string>();
  private readonly minPreviewZoom = 0.5;
  private readonly maxPreviewZoom = 1.2;
  private readonly previewZoomStep = 0.04;
  private readonly computeMonitoringPollIntervalMs = 4000;
  private readonly detachedPreviewZoomOptions: IUtility['options'] = [
    { label: '50%', value: 0.5 },
    { label: '75%', value: 0.75 },
    { label: '100%', value: 1 },
    { label: '110%', value: 1.1 },
    { label: '120%', value: 1.2 }
  ];
  readonly generatedAudioPlaybackRates = [1, 1.25, 1.5, 2];
  private artifactApiBaseUrl: string | null = null;
  private readonly generatedAudioStateByKey = new Map<string, GeneratedAudioState>();
  private activeGeneratedAudioElement: HTMLAudioElement | null = null;
  private activeGeneratedImagePopoverKey: string | null = null;
  private activeGeneratedAudioPopoverElement: HTMLAudioElement | null = null;
  private activeGeneratedAudioPopoverKey: string | null = null;
  private activeArtifactPopoverKey: string | null = null;
  private isMinimalToolHistoryPopoverOpen = false;
  private hasViewInitialized = false;
  private computeMonitoringPollTimer: ReturnType<typeof setInterval> | null = null;
  private computeMonitoringRequestInFlight = false;
  private activeDetachedPreviewId: string | null = null;
  private activeDetachedPreviewKind: ChatPreviewKind | null = null;
  private annotationSummaryLoadKey = '';
  private annotationSavedSubscription: Subscription;
  private readonly annotationTargetKeysByMessageKey = new Map<string, string[]>();
  private zipPreviewViewerState: ZipPreviewViewerState = {
    canTogglePreviewMode: false,
    canSaveArchive: false,
    archiveSaving: false,
    previewMode: null,
    explorerVisibility: 'expanded'
  };
  constructor(
    private hostElement: ElementRef<HTMLElement>,
    private uiModalService: UiModalService,
    private uiPopoverService: UiPopoverService,
    private http: HttpClient,
    private afs: AngularFirestore,
    private afStorage: AngularFireStorage,
    private router: Router,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private theme: ThemeService,
    private alertService: AlertService,
    private evalAnnotationService: EvalAnnotationService
  ) {
    this.annotationSavedSubscription = this.evalAnnotationService.annotationSaved$.subscribe(event => {
      this.onAnnotationSaved(event);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentThreadId'] && !changes['currentThreadId'].firstChange && changes['currentThreadId'].previousValue !== changes['currentThreadId'].currentValue) {
      this.closeDetachedPreview();
      this.clearInlineArtifactPreviewStates();
      this.clearInlinePreviewCollapseStates();
      this.stopComputeMonitoringPolling(true);
      this.computeMonitoring = null;
      this.computeMonitoringError = null;
    }

    if (changes['previewPresentation'] && !changes['previewPresentation'].firstChange && changes['previewPresentation'].currentValue !== 'sidecar') {
      this.clearDetachedPreviewState();
    }

    if (
      (changes['backendUrl'] && !changes['backendUrl'].firstChange)
      || (changes['backendEnvironment'] && !changes['backendEnvironment'].firstChange)
    ) {
      this.clearInlineArtifactPreviewStates();
    }

    if (changes['history']) {
      this.threadAssetsChanged.emit(this.getThreadAssetItems());
    } else if (changes['currentThreadId']) {
      this.threadAssetsChanged.emit([]);
    }

    if (
      changes['currentThreadId']
      || changes['history']
      || changes['requestedDetachedPreviewKind']
      || changes['requestedDetachedPreviewId']
    ) {
      this.syncRequestedDetachedPreview();
    }

    if (
      changes['currentThreadId']
      || changes['history']
      || changes['renderPreviewsInline']
      || changes['backendUrl']
      || changes['backendEnvironment']
    ) {
      this.syncInlineArtifactPreviews();
    }

    if (
      changes['currentThreadId']
      || changes['currentThread']
      || changes['history']
    ) {
      this.refreshActiveMessageDetailTabs();
      this.syncComputeMonitoring();
    }

    if (
      changes['currentThreadId']
      || changes['currentThread']
      || changes['history']
      || changes['displayMode']
    ) {
      void this.syncVisibleMessageAnnotationSummary();
    }
  }

  ngAfterViewInit(): void {
    this.hasViewInitialized = true;
    this.syncRequestedDetachedPreview();
  }

  ngOnDestroy(): void {
    if (this.activeGeneratedAudioElement) {
      this.activeGeneratedAudioElement.pause();
      this.activeGeneratedAudioElement = null;
    }

    this.closeGeneratedImageOptionsPopover();
    this.closeGeneratedAudioOptionsPopover();
    this.closeArtifactSelectorPopover();
    this.stopComputeMonitoringPolling(true);
    this.annotationSavedSubscription.unsubscribe();

    this.clearDetachedPreviewState();
    this.threadAssetsChanged.emit([]);
  }

  trackByMessage(index: number, message: any): string {
    return message.id || index.toString();
  }

  trackBySuggestedFollowUp(index: number, followUp: string): string {
    return `${followUp}:${index}`;
  }

  trackByImageAttachment(index: number, attachment: { id?: string; url?: string }): string {
    return attachment.id || attachment.url || index.toString();
  }

  trackByGeneratedMedia(index: number, media: { id?: string; url?: string; kind?: string; stateKey?: string }): string {
    return media.stateKey || media.id || `${media.kind || 'media'}:${media.url || index.toString()}`;
  }

  trackByContextAttachment(index: number, attachment: { id?: string; entityId?: string; type?: string }): string {
    return attachment.id || `${attachment.type || 'context'}:${attachment.entityId || index.toString()}`;
  }

  getThreadAssetItems(): ChatThreadAssetItem[] {
    if (!this.currentThreadId || !Array.isArray(this.history) || this.history.length === 0) {
      return [];
    }

    const assets = new Map<string, ChatThreadAssetItem>();

    for (let messageIndex = this.history.length - 1; messageIndex >= 0; messageIndex -= 1) {
      const message = this.history[messageIndex];
      const messageAssets = this.getMessageThreadAssetItems(message, messageIndex);

      for (const asset of messageAssets) {
        const assetKey = `${asset.kind}:${asset.id}`;
        if (!assets.has(assetKey)) {
          assets.set(assetKey, asset);
        }
      }
    }

    return Array.from(assets.values());
  }

  openThreadAsset(asset: ChatThreadAssetItem): void {
    switch (asset.kind) {
      case 'tool-preview': {
        const preview = this.findThreadPreviewById(asset.id);
        if (preview) {
          this.openPreviewModal(preview);
          this.scrollThreadAssetMessageIntoView(asset);
          return;
        }
        break;
      }
      case 'artifact': {
        const artifact = this.findThreadArtifactById(asset.id);
        if (artifact) {
          if (this.canPreviewArtifact(artifact)) {
            void this.openArtifactPreview(artifact);
          } else {
            this.openHrefInNewTab(this.getPrimaryArtifactOpenHref(artifact));
          }
          this.scrollThreadAssetMessageIntoView(asset);
          return;
        }
        break;
      }
      case 'generated-image': {
        const media = this.findGeneratedImageById(asset.id);
        if (media) {
          this.openGeneratedImagePreview(media);
          this.scrollThreadAssetMessageIntoView(asset);
          return;
        }
        break;
      }
      case 'generated-audio': {
        const media = this.findGeneratedAudioById(asset.id);
        if (media) {
          this.openGeneratedAudioPreview(media);
          this.scrollThreadAssetMessageIntoView(asset);
          return;
        }
        break;
      }
    }

    this.openHrefInNewTab(asset.href || null);
  }

  private syncRequestedDetachedPreview(): void {
    if (!this.hasViewInitialized || !this.usesDetachedPreviewSurface()) {
      return;
    }

    const requestedPreviewKind = this.requestedDetachedPreviewKind;
    const requestedPreviewId = this.requestedDetachedPreviewId?.trim() || '';

    if (!requestedPreviewKind || !requestedPreviewId) {
      if (this.activeDetachedPreviewKind || this.activeDetachedPreviewId) {
        this.closeDetachedPreview();
      }
      return;
    }

    if (!this.currentThreadId || !Array.isArray(this.history) || this.history.length === 0) {
      return;
    }

    if (this.isDetachedPreviewActive(requestedPreviewKind, requestedPreviewId)) {
      return;
    }

    const requestedAsset = this.getThreadAssetItems().find(asset => asset.kind === requestedPreviewKind && asset.id === requestedPreviewId);

    if (requestedAsset) {
      this.openThreadAsset(requestedAsset);
      return;
    }

    if (this.activeDetachedPreviewKind || this.activeDetachedPreviewId) {
      this.closeDetachedPreview();
    }
  }

  private scrollThreadAssetMessageIntoView(asset: ChatThreadAssetItem): void {
    if (typeof document === 'undefined') {
      return;
    }

    const selector = asset.messageId
      ? `[data-message-id="${this.escapeAttributeValue(asset.messageId)}"]`
      : `[data-message-index="${asset.messageIndex}"]`;

    requestAnimationFrame(() => {
      const messageElement = this.hostElement.nativeElement.querySelector<HTMLElement>(selector);
      if (!messageElement) {
        return;
      }

      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    });
  }

  private escapeAttributeValue(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  shouldRenderMessage(message: any, messageIndex?: number): boolean {
    if (!this.shouldRenderMessageBase(message)) {
      return false;
    }

    if (this.shouldHideToolStatusForMinimalMode(message, messageIndex)) {
      return false;
    }

    return true;
  }

  private shouldRenderMessageBase(message: any): boolean {
    if (this.isCompactionMarker(message)) {
      return true;
    }

    if (this.displayMode === 'debug') {
      return this.hasRenderableBody(message) || this.hasMessageDetails(message);
    }

    if (message.role === 'system') {
      return false;
    }

    return this.hasRenderableBody(message);
  }

  shouldUseToolLayout(message: any): boolean {
    return message.role === 'tool' || this.hasToolSummaryMetadata(message);
  }

  isCompactionMarker(message: any): boolean {
    const ui = message?.ui && typeof message.ui === 'object' && !Array.isArray(message.ui)
      ? message.ui
      : {};

    return message?.role === 'system'
      && message?.name === 'context_compaction'
      && ui.kind === 'context_compaction';
  }

  getCompactionMarkerLabel(message: any): string {
    const count = Number(message?.ui?.compactedMessageCount || 0);
    return count > 0 ? `Context compacted · ${count} messages` : 'Context compacted';
  }

  getCompactionMarkerTitle(message: any): string {
    const model = typeof message?.ui?.summaryModel === 'string' ? message.ui.summaryModel : '';
    const count = Number(message?.ui?.compactedMessageCount || 0);
    return [
      count > 0 ? `${count} older messages were summarized.` : 'Older conversation history was summarized.',
      model ? `Summary model: ${model}` : ''
    ].filter(Boolean).join(' ');
  }

  canOpenCompactionMarker(message: any): boolean {
    return this.displayMode === 'debug' && this.isCompactionMarker(message);
  }

  getCompactionMarkerAriaLabel(message: any): string {
    return `${this.getCompactionMarkerLabel(message)}. Open compaction details.`;
  }

  openCompactionMarkerDetails(messageIndex: number, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.displayMode !== 'debug') {
      return;
    }

    this.openToolPopoverByIndex(messageIndex);
  }

  shouldUseAiLayout(message: any): boolean {
    return !this.shouldUseToolLayout(message) && (message.role === 'model' || message.role === 'assistant');
  }

  shouldUseCompactToolLayout(message: any): boolean {
    return this.displayMode === 'minimal' && this.shouldUseToolLayout(message);
  }

  shouldHighlightToolUsage(message: any): boolean {
    return false;
  }

  shouldShowToolCallSummary(message: any): boolean {
    return message.role !== 'tool' && this.hasToolSummaryMetadata(message);
  }

  shouldShowToolResult(message: any): boolean {
    return message.role === 'tool';
  }

  private isToolStatusMessage(message: any): boolean {
    return this.shouldShowToolCallSummary(message) || this.shouldShowToolResult(message);
  }

  private shouldHideToolStatusForMinimalMode(message: any, messageIndex?: number): boolean {
    if (this.displayMode !== 'minimal' || typeof messageIndex !== 'number') {
      return false;
    }

    if (!this.isToolStatusMessage(message) || this.doesToolStatusEmitFiles(message, messageIndex)) {
      return false;
    }

    return !this.isLatestMinimalToolHistoryStatus(messageIndex);
  }

  private doesToolStatusEmitFiles(message: any, messageIndex: number): boolean {
    return this.shouldShowToolResult(message) && this.getVisibleArtifactResources(message, messageIndex).length > 0;
  }

  private getMinimalToolHistoryItems(messageIndex: number): MinimalToolHistoryItem[] {
    if (this.displayMode !== 'minimal') {
      return [];
    }

    const latestToolStatusIndex = this.getLatestMinimalToolStatusIndex(messageIndex);
    if (latestToolStatusIndex < 0) {
      return [];
    }

    const startIndex = this.getMinimalToolSetStartIndex(messageIndex);
    const items: MinimalToolHistoryItem[] = [];
    for (let index = startIndex; index <= latestToolStatusIndex; index += 1) {
      const message = this.history[index];
      if (
        this.shouldRenderMessageBase(message)
        && this.isToolStatusMessage(message)
      ) {
        items.push({ message, index });
      }
    }

    return items;
  }

  private getLatestMinimalToolStatusIndex(messageIndex: number): number {
    const endIndex = this.getMinimalToolSetEndIndex(messageIndex);
    const startIndex = this.getMinimalToolSetStartIndex(messageIndex);
    for (let index = endIndex; index >= startIndex; index -= 1) {
      const message = this.history[index];
      if (this.shouldRenderMessageBase(message) && this.isToolStatusMessage(message)) {
        return index;
      }
    }

    return -1;
  }

  private getMinimalToolSetStartIndex(messageIndex: number): number {
    for (let index = messageIndex - 1; index >= 0; index -= 1) {
      if (this.history[index]?.role === 'user') {
        return index + 1;
      }
    }

    return 0;
  }

  private getMinimalToolSetEndIndex(messageIndex: number): number {
    for (let index = messageIndex + 1; index < this.history.length; index += 1) {
      if (this.history[index]?.role === 'user') {
        return index - 1;
      }
    }

    return this.history.length - 1;
  }

  private isLatestMinimalToolHistoryStatus(messageIndex: number): boolean {
    const items = this.getMinimalToolHistoryItems(messageIndex);
    const latestItem = items[items.length - 1];
    return !!latestItem && latestItem.index === messageIndex;
  }

  getMinimalToolHistoryCount(messageIndex: number): number {
    return this.getMinimalToolHistoryItems(messageIndex).length;
  }

  shouldShowMinimalToolHistoryTrigger(message: any, messageIndex: number): boolean {
    return this.displayMode === 'minimal'
      && this.isToolStatusMessage(message)
      && this.isLatestMinimalToolHistoryStatus(messageIndex)
      && this.getMinimalToolHistoryCount(messageIndex) > 1;
  }

  getMinimalToolHistoryAriaLabel(messageIndex: number): string {
    const count = this.getMinimalToolHistoryCount(messageIndex);
    return count === 1 ? 'Show tool call' : `Show ${count} tool calls`;
  }

  getMinimalToolHistoryItemLabel(item: MinimalToolHistoryItem): string {
    const label = this.shouldShowToolCallSummary(item.message)
      ? this.getToolCallDisplayLabel(item.message)
      : this.getToolResultDisplayLabel(item.message);

    return this.doesToolStatusEmitFiles(item.message, item.index) ? `${label} - files` : label;
  }

  hasArtifactResources(message: any): boolean {
    return this.getArtifactResources(message).length > 0;
  }

  hasPreviewBlocks(message: any): boolean {
    return this.getPreviewBlocks(message).length > 0;
  }

  hasVisiblePreviewBlocks(message: any, messageIndex: number): boolean {
    return this.getVisiblePreviewBlocks(message, messageIndex).length > 0;
  }

  getVisiblePreviewBlocks(message: any, messageIndex: number): ToolPreviewResource[] {
    return this.getPreviewBlocks(message).filter((preview) => this.isLatestPreviewResource(preview, messageIndex));
  }

  getPreviewBlocks(message: any): ToolPreviewResource[] {
    const payload = this.getToolPayload(message);
    const uiBlocks = Array.isArray(payload?.ui) ? payload.ui : [];

    return uiBlocks.flatMap((block: any, index: number) => {
      if (block?.type !== 'custom' || block?.renderer !== 'iframe_preview') {
        return [];
      }

      const rawUrl = typeof block?.payload?.url === 'string' ? block.payload.url.trim() : '';
      const canonicalUrl = this.normalizePreviewUrl(rawUrl, { rewriteForLocalBackend: false });
      const url = this.normalizePreviewUrl(rawUrl);
      if (!canonicalUrl || !url) {
        return [];
      }

      const rawHeight = typeof block?.payload?.height === 'number'
        ? block.payload.height
        : Number(block?.payload?.height);
      const height = Number.isFinite(rawHeight)
        ? Math.max(320, Math.min(960, Math.round(rawHeight)))
        : 560;
      const title = typeof block?.title === 'string' && block.title.trim().length > 0
        ? block.title.trim()
        : typeof block?.payload?.title === 'string' && block.payload.title.trim().length > 0
          ? block.payload.title.trim()
          : 'Preview';
      const subtitle = typeof block?.subtitle === 'string' && block.subtitle.trim().length > 0
        ? block.subtitle.trim()
        : typeof block?.payload?.subtitle === 'string' && block.payload.subtitle.trim().length > 0
          ? block.payload.subtitle.trim()
          : undefined;

      return [{
        id: `${canonicalUrl}:${index}`,
        title,
        ...(subtitle ? { subtitle } : {}),
        url,
        safeUrl: this.getSafePreviewUrl(url),
        height,
        ...(typeof block?.payload?.previewType === 'string' ? { previewType: block.payload.previewType } : {}),
        ...(typeof block?.payload?.defaultZoom === 'number'
          ? { defaultZoom: this.clampPreviewZoom(block.payload.defaultZoom) }
          : Number.isFinite(Number(block?.payload?.defaultZoom))
            ? { defaultZoom: this.clampPreviewZoom(Number(block.payload.defaultZoom)) }
            : {}),
        ...(this.normalizeEmulatorMode(block?.payload?.emulatorMode)
          ? { emulatorMode: this.normalizeEmulatorMode(block.payload.emulatorMode) }
          : {})
      }];
    });
  }

  trackByPreview(index: number, preview: ToolPreviewResource): string {
    return preview.id || index.toString();
  }

  getPreviewLinkHref(preview: ToolPreviewResource): string {
    return this.getPreviewHrefWithoutPreviewShell(preview.url);
  }

  private getPreviewHrefWithoutPreviewShell(href: string): string {
    try {
      const parsed = new URL(href);
      parsed.searchParams.delete('previewShell');
      return parsed.toString();
    } catch {
      return href;
    }
  }

  private getMessageThreadAssetItems(message: any, messageIndex: number): ChatThreadAssetItem[] {
    const messageId = typeof message?.id === 'string' ? message.id : undefined;

    const previewAssets = this.getPreviewBlocks(message).map((preview) => ({
      id: preview.id,
      kind: 'tool-preview' as const,
      title: preview.title,
      ...(preview.subtitle ? { subtitle: preview.subtitle } : {}),
      ...(this.getThreadAssetHrefDetail(preview.url) ? { detail: this.getThreadAssetHrefDetail(preview.url) } : {}),
      iconClass: 'fa-regular fa-window-maximize',
      typeLabel: 'Preview',
      href: this.getPreviewLinkHref(preview),
      ...(messageId ? { messageId } : {}),
      messageIndex
    }));

    const artifactAssets = this.getArtifactResources(message).map((artifact) => ({
      id: artifact.id,
      kind: 'artifact' as const,
      title: artifact.title,
      ...(artifact.subtitle ? { subtitle: artifact.subtitle } : {}),
      detail: this.getArtifactLocationLabel(artifact),
      iconClass: this.getThreadArtifactIconClass(artifact),
      typeLabel: 'File',
      ...(this.getPrimaryArtifactOpenHref(artifact) ? { href: this.getPrimaryArtifactOpenHref(artifact)! } : {}),
      ...(messageId ? { messageId } : {}),
      messageIndex
    }));

    const imageAssets = this.getMessageGeneratedImages(message).map((media) => ({
      id: this.getGeneratedImagePreviewId(media),
      kind: 'generated-image' as const,
      title: media.name || 'Generated image',
      subtitle: this.getGeneratedImageAssetSubtitle(media),
      ...(this.getGeneratedImageAssetDetail(media) ? { detail: this.getGeneratedImageAssetDetail(media)! } : {}),
      iconClass: 'fa-regular fa-image',
      typeLabel: 'Image',
      href: media.url,
      ...(messageId ? { messageId } : {}),
      messageIndex
    }));

    const audioAssets = this.getMessageGeneratedAudio(message).map((media) => ({
      id: this.getGeneratedAudioPreviewId(media),
      kind: 'generated-audio' as const,
      title: media.name || 'Generated audio',
      subtitle: 'Generated audio',
      ...(this.getThreadAssetHrefDetail(media.url) ? { detail: this.getThreadAssetHrefDetail(media.url) } : {}),
      iconClass: 'fa-regular fa-file-audio',
      typeLabel: 'Audio',
      href: media.url,
      ...(messageId ? { messageId } : {}),
      messageIndex
    }));

    return [...previewAssets, ...artifactAssets, ...imageAssets, ...audioAssets];
  }

  private getThreadAssetHrefDetail(href: string | null | undefined): string | undefined {
    if (!href) {
      return undefined;
    }

    try {
      const parsed = new URL(href);
      const path = `${parsed.hostname}${parsed.pathname}`;
      return path.length > 0 ? path : href;
    } catch {
      return href;
    }
  }

  getThreadArtifactIconClass(artifact: ToolArtifactResource): string {
    switch (this.getArtifactPreviewType(artifact)) {
      case 'html':
      case 'svg':
      case 'markdown':
      case 'csv':
      case 'text':
        return 'fa-regular fa-file-code';
      case 'image':
        return 'fa-regular fa-file-image';
      case 'audio':
        return 'fa-regular fa-file-audio';
      case 'video':
        return 'fa-regular fa-file-video';
      case 'zip':
        return 'fa-regular fa-file-zipper';
      default:
        return 'fa-regular fa-file-lines';
    }
  }

  private findThreadPreviewById(previewId: string): ToolPreviewResource | null {
    for (let messageIndex = this.history.length - 1; messageIndex >= 0; messageIndex -= 1) {
      const preview = this.getPreviewBlocks(this.history[messageIndex]).find((candidate) => candidate.id === previewId);
      if (preview) {
        return preview;
      }
    }

    return null;
  }

  private findThreadArtifactById(artifactId: string): ToolArtifactResource | null {
    for (let messageIndex = this.history.length - 1; messageIndex >= 0; messageIndex -= 1) {
      const artifact = this.getArtifactResources(this.history[messageIndex]).find((candidate) => candidate.id === artifactId);
      if (artifact) {
        return artifact;
      }
    }

    return null;
  }

  private findGeneratedImageById(previewId: string): GeneratedImageMedia | null {
    for (let messageIndex = this.history.length - 1; messageIndex >= 0; messageIndex -= 1) {
      const media = this.getMessageGeneratedImages(this.history[messageIndex]).find((candidate) => this.getGeneratedImagePreviewId(candidate) === previewId);
      if (media) {
        return media;
      }
    }

    return null;
  }

  private findGeneratedAudioById(previewId: string): GeneratedAudioMedia | null {
    for (let messageIndex = this.history.length - 1; messageIndex >= 0; messageIndex -= 1) {
      const media = this.getMessageGeneratedAudio(this.history[messageIndex]).find((candidate) => this.getGeneratedAudioPreviewId(candidate) === previewId);
      if (media) {
        return media;
      }
    }

    return null;
  }

  canZoomPreview(preview: ToolPreviewResource): boolean {
    return preview.previewType === 'emulator';
  }

  getPreviewZoom(preview: ToolPreviewResource): number {
    return this.previewZoomById.get(preview.id) ?? this.getDefaultPreviewZoom(preview);
  }

  usesDetachedPreviewSurface(): boolean {
    return this.previewPresentation === 'sidecar';
  }

  private getPreviewPanelPadding(defaultPadding: 'none' | 'standard' = 'standard'): 'none' | 'standard' {
    return this.usesDetachedPreviewSurface() ? 'none' : defaultPadding;
  }

  isDetachedPreviewActive(kind: ChatPreviewKind, id: string): boolean {
    return this.usesDetachedPreviewSurface()
      && this.activeDetachedPreviewKind === kind
      && this.activeDetachedPreviewId === id;
  }

  shouldRenderInlineAssets(): boolean {
    return this.renderPreviewsInline && !this.hasActiveDetachedPreview();
  }

  shouldRenderInlinePreview(preview: ToolPreviewResource): boolean {
    return !!preview && this.shouldRenderInlineAssets() && !this.isToolInlinePreviewCollapsed(preview);
  }

  shouldRenderInlineArtifacts(artifacts: ToolArtifactResource[]): boolean {
    return artifacts.some((artifact) => this.canPreviewArtifact(artifact)) && this.shouldRenderInlineAssets();
  }

  shouldUseInlinePreviewLayout(chat: any, messageIndex: number): boolean {
    if (!this.shouldShowToolResult(chat) || !this.shouldRenderInlineAssets()) {
      return false;
    }

    if (this.hasVisiblePreviewBlocks(chat, messageIndex)) {
      return true;
    }

    return this.getVisibleArtifactResources(chat, messageIndex).some((artifact) => this.canPreviewArtifact(artifact));
  }

  getInlineArtifactPreviewState(artifact: ToolArtifactResource): InlineArtifactPreviewState | null {
    if (!this.shouldRenderInlineAssets() || !this.canPreviewArtifact(artifact)) {
      return null;
    }

    const key = this.getInlineArtifactPreviewKey(artifact);
    const state = this.inlineArtifactPreviewStates.get(key) || null;

    if (!state && !this.inlineArtifactPreviewLoadingKeys.has(key)) {
      this.ensureInlineArtifactPreview(artifact);
    }

    return state;
  }

  isToolInlinePreviewCollapsed(preview: ToolPreviewResource): boolean {
    return this.collapsedInlinePreviewKeys.has(this.getToolInlinePreviewCollapseKey(preview));
  }

  isArtifactInlinePreviewCollapsed(artifact: ToolArtifactResource): boolean {
    return this.collapsedInlinePreviewKeys.has(this.getArtifactInlinePreviewCollapseKey(artifact));
  }

  getToolInlinePreviewToggleLabel(preview: ToolPreviewResource): string {
    return this.isToolInlinePreviewCollapsed(preview) ? 'Show inline preview' : 'Hide inline preview';
  }

  getArtifactInlinePreviewToggleLabel(artifact: ToolArtifactResource): string {
    return this.isArtifactInlinePreviewCollapsed(artifact) ? 'Show inline preview' : 'Hide inline preview';
  }

  toggleToolInlinePreview(preview: ToolPreviewResource, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.toggleInlinePreviewCollapseKey(this.getToolInlinePreviewCollapseKey(preview));
  }

  toggleArtifactInlinePreview(artifact: ToolArtifactResource, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.toggleInlinePreviewCollapseKey(this.getArtifactInlinePreviewCollapseKey(artifact));
  }

  private toggleInlinePreviewCollapseKey(key: string): void {
    if (this.collapsedInlinePreviewKeys.has(key)) {
      this.collapsedInlinePreviewKeys.delete(key);
    } else {
      this.collapsedInlinePreviewKeys.add(key);
    }

    this.cdr.markForCheck();
  }

  getArtifactPreviewRowsFor(preview: ArtifactPreviewState | null | undefined): string[][] {
    return preview?.rows || [];
  }

  getArtifactPreviewHeaderFor(preview: ArtifactPreviewState | null | undefined): string[] {
    return this.getArtifactPreviewRowsFor(preview)[0] || [];
  }

  getArtifactPreviewBodyFor(preview: ArtifactPreviewState | null | undefined): string[][] {
    return this.getArtifactPreviewRowsFor(preview).slice(1);
  }

  private hasActiveDetachedPreview(): boolean {
    return this.usesDetachedPreviewSurface()
      && (
        (!!this.activeDetachedPreviewKind && !!this.activeDetachedPreviewId)
        || (!!this.requestedDetachedPreviewKind && !!this.requestedDetachedPreviewId)
      );
  }

  private syncInlineArtifactPreviews(): void {
    if (!this.renderPreviewsInline || !this.currentThreadId || !Array.isArray(this.history)) {
      return;
    }

    const nextArtifactKeys = new Set<string>();

    for (const message of this.history) {
      for (const artifact of this.getArtifactResources(message)) {
        if (!this.canPreviewArtifact(artifact)) {
          continue;
        }

        const key = this.getInlineArtifactPreviewKey(artifact);
        nextArtifactKeys.add(key);
        this.ensureInlineArtifactPreview(artifact);
      }
    }

    for (const key of Array.from(this.inlineArtifactPreviewStates.keys())) {
      if (!nextArtifactKeys.has(key)) {
        this.inlineArtifactPreviewStates.delete(key);
      }
    }
  }

  private ensureInlineArtifactPreview(artifact: ToolArtifactResource): void {
    const key = this.getInlineArtifactPreviewKey(artifact);

    if (this.inlineArtifactPreviewStates.has(key) || this.inlineArtifactPreviewLoadingKeys.has(key)) {
      return;
    }

    this.inlineArtifactPreviewStates.set(key, {
      artifact,
      preview: null,
      loading: true,
      error: null,
      apiBaseUrl: null
    });
    this.inlineArtifactPreviewLoadingKeys.add(key);
    this.cdr.markForCheck();

    void this.loadInlineArtifactPreview(artifact, key);
  }

  private async loadInlineArtifactPreview(artifact: ToolArtifactResource, key: string): Promise<void> {
    const updateState = (patch: Partial<InlineArtifactPreviewState>): void => {
      const current = this.inlineArtifactPreviewStates.get(key);
      if (!current) {
        return;
      }

      this.inlineArtifactPreviewStates.set(key, { ...current, ...patch });
      this.cdr.markForCheck();
    };

    try {
      const previewType = this.getArtifactPreviewType(artifact);
      if (!previewType) {
        throw new Error('Preview is not available for this artifact type.');
      }

      if (previewType === 'markdown' || previewType === 'csv' || previewType === 'html' || previewType === 'svg' || previewType === 'text') {
        const { response, baseUrl } = await this.loadArtifactText(artifact);
        const responseArtifact: ToolArtifactResource = {
          ...artifact,
          ...(typeof response?.artifact?.downloadUrl === 'string' ? { uri: response.artifact.downloadUrl } : {}),
          ...(typeof response?.artifact?.mimeType === 'string' ? { mimeType: response.artifact.mimeType } : {}),
          ...(typeof response?.artifact?.kind === 'string' ? { kind: response.artifact.kind } : {}),
          ...(typeof response?.artifact?.archiveFormat === 'string' ? { archiveFormat: response.artifact.archiveFormat } : {}),
          ...(typeof response?.artifact?.relativePath === 'string' ? { relativePath: response.artifact.relativePath } : {}),
          ...(typeof response?.artifact?.storageUri === 'string' ? { storageUri: response.artifact.storageUri } : {})
        };
        const content = typeof response?.content?.text === 'string' ? response.content.text : '';
        const remoteUrl = previewType === 'html' || previewType === 'svg'
          ? this.getArtifactPreviewSourceUrl(responseArtifact, baseUrl, Date.now())
          : null;

        updateState({
          artifact: responseArtifact,
          preview: this.buildArtifactPreviewState(responseArtifact, content, {
            truncated: response?.content?.truncated === true,
            sizeBytes: typeof response?.content?.sizeBytes === 'number' ? response.content.sizeBytes : content.length
          }, {
            ...(remoteUrl ? { preferRemoteUrl: remoteUrl } : {})
          }),
          loading: false,
          error: null,
          apiBaseUrl: baseUrl
        });
        return;
      }

      let previewArtifact = artifact;
      let remoteUrl = this.getArtifactPreviewSourceUrl(previewArtifact, undefined, Date.now());
      let baseUrl: string | null = null;

      if (!remoteUrl && this.getArtifactStorageReferenceTarget(previewArtifact)) {
        remoteUrl = this.appendCacheBust(await this.resolveArtifactDownloadUrl(previewArtifact), Date.now());
      }

      if (!remoteUrl && previewArtifact.artifactId && this.currentThreadId && this.getArtifactApiBaseCandidates().length > 0) {
        const backendPreview = await this.readArtifactFromBackend(previewArtifact, 1);
        baseUrl = backendPreview.baseUrl;
        const responseArtifact: ToolArtifactResource = {
          ...previewArtifact,
          ...(typeof backendPreview.response?.artifact?.downloadUrl === 'string' ? { uri: backendPreview.response.artifact.downloadUrl } : {}),
          ...(typeof backendPreview.response?.artifact?.mimeType === 'string' ? { mimeType: backendPreview.response.artifact.mimeType } : {}),
          ...(typeof backendPreview.response?.artifact?.kind === 'string' ? { kind: backendPreview.response.artifact.kind } : {}),
          ...(typeof backendPreview.response?.artifact?.archiveFormat === 'string' ? { archiveFormat: backendPreview.response.artifact.archiveFormat } : {}),
          ...(typeof backendPreview.response?.artifact?.relativePath === 'string' ? { relativePath: backendPreview.response.artifact.relativePath } : {}),
          ...(typeof backendPreview.response?.artifact?.storageUri === 'string' ? { storageUri: backendPreview.response.artifact.storageUri } : {})
        };
        remoteUrl = this.getArtifactPreviewSourceUrl(responseArtifact, baseUrl, Date.now());
        previewArtifact = responseArtifact;
      }

      if (!remoteUrl) {
        throw new Error('Artifact preview is not available for this file yet.');
      }

      updateState({
        artifact: previewArtifact,
        preview: this.buildArtifactPreviewState(previewArtifact, '', {
          truncated: false,
          sizeBytes: typeof previewArtifact.sizeBytes === 'number' ? previewArtifact.sizeBytes : 0
        }, {
          preferRemoteUrl: remoteUrl
        }),
        loading: false,
        error: null,
        apiBaseUrl: baseUrl
      });
    } catch (error) {
      updateState({
        preview: null,
        loading: false,
        error: this.getArtifactPreviewErrorMessage(error)
      });
    } finally {
      this.inlineArtifactPreviewLoadingKeys.delete(key);
      this.cdr.markForCheck();
    }
  }

  private getInlineArtifactPreviewKey(artifact: ToolArtifactResource): string {
    return artifact.id
      || artifact.artifactId
      || artifact.relativePath
      || artifact.persistedPath
      || artifact.uri
      || artifact.title;
  }

  private getToolInlinePreviewCollapseKey(preview: ToolPreviewResource): string {
    return `tool-preview:${preview.id}`;
  }

  private getArtifactInlinePreviewCollapseKey(artifact: ToolArtifactResource): string {
    return `artifact:${this.getInlineArtifactPreviewKey(artifact)}`;
  }

  private clearInlineArtifactPreviewStates(): void {
    this.inlineArtifactPreviewStates.clear();
    this.inlineArtifactPreviewLoadingKeys.clear();
  }

  private clearInlinePreviewCollapseStates(): void {
    this.collapsedInlinePreviewKeys.clear();
  }

  getToolPreviewActionLabel(preview: ToolPreviewResource): string {
    if (!this.usesDetachedPreviewSurface()) {
      return 'Open preview';
    }

    return this.isDetachedPreviewActive('tool-preview', preview.id)
      ? 'Close side panel'
      : 'Open side panel';
  }

  getArtifactPreviewActionLabel(artifact: ToolArtifactResource): string {
    if (!this.usesDetachedPreviewSurface()) {
      return 'Preview';
    }

    return this.isDetachedPreviewActive('artifact', artifact.id)
      ? 'Close panel'
      : 'Open side panel';
  }

  getArtifactInlineActionLabel(artifact: ToolArtifactResource): string {
    if (!this.usesDetachedPreviewSurface()) {
      return artifact.title;
    }

    return this.isDetachedPreviewActive('artifact', artifact.id)
      ? `Close ${artifact.title}`
      : `Open ${artifact.title}`;
  }

  getArtifactSelectorLabel(artifacts: ToolArtifactResource[]): string {
    const count = artifacts.length;
    const countLabel = `${count} file${count === 1 ? '' : 's'}`;
    const activeArtifact = this.getActiveArtifactFromSet(artifacts);

    if (!activeArtifact) {
      return countLabel;
    }

    return `${countLabel} (${activeArtifact.title} open)`;
  }

  getArtifactSelectorTitle(artifacts: ToolArtifactResource[]): string {
    const count = artifacts.length;
    const countLabel = `${count} file${count === 1 ? '' : 's'}`;
    const activeArtifact = this.getActiveArtifactFromSet(artifacts);

    if (!activeArtifact) {
      return countLabel;
    }

    return `${activeArtifact.title} is already open. Choose another file, or click it again in the list to close the panel.`;
  }

  hasActiveArtifactInSet(artifacts: ToolArtifactResource[]): boolean {
    return this.getActiveArtifactFromSet(artifacts) !== null;
  }

  getArtifactPopoverItemStatusLabel(artifact: ToolArtifactResource): string {
    if (!this.usesDetachedPreviewSurface()) {
      return 'Preview';
    }

    return this.isDetachedPreviewActive('artifact', artifact.id)
      ? 'Already open - click to close'
      : 'Open side panel';
  }

  isArtifactSelectorOpen(artifacts: ToolArtifactResource[]): boolean {
    return this.activeArtifactPopoverKey !== null
      && this.activeArtifactPopoverKey === this.getArtifactPopoverKey(artifacts);
  }

  openArtifactResource(artifact: ToolArtifactResource, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.closeArtifactSelectorPopover();

    if (this.canPreviewArtifact(artifact)) {
      void this.openArtifactPreview(artifact);
      return;
    }

    this.openHrefInNewTab(this.getPrimaryArtifactOpenHref(artifact));
  }

  private getActiveArtifactFromSet(artifacts: ToolArtifactResource[]): ToolArtifactResource | null {
    if (!this.usesDetachedPreviewSurface() || this.activeDetachedPreviewKind !== 'artifact' || !this.activeDetachedPreviewId) {
      return null;
    }

    return artifacts.find((artifact) => artifact.id === this.activeDetachedPreviewId) ?? null;
  }

  private buildToolPreviewOutlet(preview: ToolPreviewResource): ChatPreviewOutlet {
    return {
      id: preview.id,
      kind: 'tool-preview',
      title: preview.title,
      ...(preview.subtitle ? { subtitle: preview.subtitle } : {}),
      bodyTemplate: this.previewExpandedTemplate!,
      ...(this.previewHeaderActionsTemplate ? { headerActionsTemplate: this.previewHeaderActionsTemplate } : {}),
      panelPadding: this.getPreviewPanelPadding(),
      utilities: this.getToolPreviewUtilities(preview),
      onUtilityChange: (change) => {
        this.handleToolPreviewUtilityChange(preview, change);
      },
      onClose: () => {
        this.closeDetachedPreview();
      }
    };
  }

  private getToolPreviewUtilities(preview: ToolPreviewResource): IUtility[] {
    const utilities: IUtility[] = [];

    if (this.canZoomPreview(preview)) {
      utilities.push({
        id: 'preview-zoom',
        label: 'Zoom',
        icon: 'fa-solid fa-magnifying-glass-plus',
        type: 'dropdown',
        value: this.getPreviewZoom(preview),
        options: this.detachedPreviewZoomOptions,
        essential: true,
        tooltip: 'Adjust preview zoom'
      });
    }

    utilities.push({
      id: 'preview-open',
      label: '',
      icon: 'fa-regular fa-up-right-from-square',
      type: 'button',
      essential: true,
      tooltip: 'Open preview in a new tab'
    });

    utilities.push({
      id: 'preview-close',
      label: '',
      icon: 'fa-solid fa-xmark',
      type: 'button',
      essential: true,
      tooltip: 'Close side preview'
    });

    return utilities;
  }

  private handleToolPreviewUtilityChange(preview: ToolPreviewResource, change: IUtilityChange): void {
    switch (change.utility.id) {
      case 'preview-zoom':
        this.setPreviewZoom(preview, Number(change.newValue));
        break;
      case 'preview-open':
        this.openHrefInNewTab(this.getPreviewLinkHref(preview));
        break;
      case 'preview-close':
        this.closeDetachedPreview();
        break;
    }
  }

  private buildArtifactPreviewOutlet(artifact: ToolArtifactResource): ChatPreviewOutlet {
    const previewType = this.getArtifactPreviewType(artifact);

    return {
      id: artifact.id,
      kind: 'artifact',
      title: artifact.title,
      ...(artifact.subtitle ? { subtitle: artifact.subtitle } : {}),
      bodyTemplate: this.artifactPreviewTemplate!,
      ...(this.artifactPreviewActionsTemplate ? { headerActionsTemplate: this.artifactPreviewActionsTemplate } : {}),
      ctas: this.getArtifactPreviewCtas(),
      superCta: this.getArtifactPreviewSuperCta(artifact),
      panelPadding: this.getPreviewPanelPadding(previewType === 'zip' ? 'none' : 'standard'),
      utilities: this.getArtifactPreviewUtilities(artifact),
      onUtilityChange: (change) => {
        void this.handleArtifactPreviewUtilityChange(artifact, change);
      },
      onClose: () => {
        this.closeDetachedPreview();
      }
    };
  }

  private getArtifactPreviewUtilities(artifact: ToolArtifactResource): IUtility[] {
    const utilities: IUtility[] = [];
    const previewType = this.getArtifactPreviewType(artifact);

    if (previewType === 'zip') {
      if (this.zipPreviewViewerState.canTogglePreviewMode && this.zipPreviewViewerState.explorerVisibility === 'hidden') {
        utilities.push({
          id: 'preview-mode',
          label: 'View',
          icon: 'fa-regular fa-eye',
          type: 'dropdown',
          value: this.zipPreviewViewerState.previewMode || 'preview',
          options: [
            { label: 'Preview', value: 'preview', icon: 'fa-regular fa-eye' },
            { label: 'Code', value: 'code', icon: 'fa-regular fa-file-code' }
          ],
          essential: true,
          tooltip: 'Switch between rendered preview and code view'
        });
      }
    } else if (this.canEditArtifactRaw()) {
      utilities.push({
        id: 'preview-mode',
        label: 'Mode',
        icon: 'fa-regular fa-eye',
        type: 'dropdown',
        value: this.artifactPreviewMode,
        options: [
          { label: 'Preview', value: 'preview', icon: 'fa-regular fa-eye' },
          { label: 'Raw', value: 'raw', icon: 'fa-regular fa-file-lines' }
        ],
        essential: true,
        tooltip: 'Switch between rendered preview and editable raw source'
      });
    }

    if (this.shouldShowStandaloneArtifactDelete(artifact)) {
      utilities.push({
        id: 'artifact-delete',
        label: '',
        icon: 'fa-regular fa-trash-can',
        type: 'button',
        essential: true,
        tooltip: 'Delete artifact'
      });
    }

    const primaryOpenHref = this.getPrimaryArtifactOpenHref(artifact);
    if (primaryOpenHref) {
      utilities.push({
        id: 'preview-open',
        label: '',
        icon: 'fa-regular fa-up-right-from-square',
        type: 'button',
        essential: true,
        tooltip: 'Open artifact in a new tab'
      });
    }

    utilities.push({
      id: 'preview-close',
      label: '',
      icon: 'fa-solid fa-xmark',
      type: 'button',
      essential: true,
      tooltip: 'Close side preview'
    });

    return utilities;
  }

  private getArtifactPreviewSuperCta(artifact: ToolArtifactResource): ISuperCta | null {
    if (this.getArtifactPreviewType(artifact) !== 'zip') {
      return null;
    }

    if (!this.zipPreviewViewerState.canSaveArchive && !this.zipPreviewViewerState.archiveSaving) {
      return null;
    }

    return {
      label: 'Save',
      loadingLabel: 'Saving...',
      icon: 'fa-regular fa-floppy-disk',
      color: 'primary',
      style: 'filled',
      loading: this.zipPreviewViewerState.archiveSaving,
      disabled: !this.zipPreviewViewerState.canSaveArchive,
      emit: 'save-zip-archive',
      tooltip: 'Save archive changes',
      action: async () => {
        await this.artifactZipPreviewComponent?.saveArchive();
      }
    };
  }

  private getArtifactPreviewCtas(): ICta[] {
    if (!this.canEditArtifactRaw() || this.artifactPreviewMode !== 'raw') {
      return [];
    }

    return [{
      label: this.artifactRawSaving ? 'Saving...' : 'Save',
      icon: this.artifactRawSaving ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-floppy-disk',
      emit: 'save-artifact-raw',
      color: 'primary',
      style: 'filled',
      disabled: !this.canSaveArtifactRaw(),
      action: () => {
        void this.saveArtifactRaw();
      }
    }];
  }

  private async handleArtifactPreviewUtilityChange(artifact: ToolArtifactResource, change: IUtilityChange): Promise<void> {
    switch (change.utility.id) {
      case 'preview-mode':
        if (this.getArtifactPreviewType(artifact) === 'zip') {
          await this.artifactZipPreviewComponent?.togglePreviewMode(change.newValue === 'code' ? 'code' : 'preview');
        } else {
          await this.setArtifactPreviewMode(change.newValue === 'raw' ? 'raw' : 'preview');
        }
        this.refreshDetachedPreviewOutlet();
        break;
      case 'preview-open':
        this.openHrefInNewTab(this.getPrimaryArtifactOpenHref(artifact));
        break;
      case 'artifact-delete':
        await this.deleteArtifactResource(artifact, null, null);
        break;
      case 'preview-close':
        this.closeDetachedPreview();
        break;
    }
  }

  private buildGeneratedImagePreviewOutlet(media: GeneratedImageMedia): ChatPreviewOutlet {
    return {
      id: this.getGeneratedImagePreviewId(media),
      kind: 'generated-image',
      title: media.name || 'Generated image',
      subtitle: this.getGeneratedImagePreviewSubtitle(media),
      bodyTemplate: this.generatedImagePreviewTemplate!,
      ...(this.generatedImagePreviewActionsTemplate ? { headerActionsTemplate: this.generatedImagePreviewActionsTemplate } : {}),
      panelPadding: this.getPreviewPanelPadding(),
      utilities: [
        {
          id: 'preview-open',
          label: '',
          icon: 'fa-regular fa-up-right-from-square',
          type: 'button',
          essential: true,
          tooltip: 'Open generated image in a new tab'
        },
        {
          id: 'preview-close',
          label: '',
          icon: 'fa-solid fa-xmark',
          type: 'button',
          essential: true,
          tooltip: 'Close side preview'
        }
      ],
      onUtilityChange: (change) => {
        this.handleGeneratedImagePreviewUtilityChange(media, change);
      },
      onClose: () => {
        this.closeDetachedPreview();
      }
    };
  }

  private buildGeneratedAudioPreviewOutlet(media: GeneratedAudioMedia): ChatPreviewOutlet {
    return {
      id: this.getGeneratedAudioPreviewId(media),
      kind: 'generated-audio',
      title: media.name || 'Generated audio',
      subtitle: 'Preview',
      bodyTemplate: this.generatedAudioPreviewTemplate!,
      ...(this.generatedAudioPreviewActionsTemplate ? { headerActionsTemplate: this.generatedAudioPreviewActionsTemplate } : {}),
      panelPadding: this.getPreviewPanelPadding(),
      utilities: [
        {
          id: 'preview-open',
          label: '',
          icon: 'fa-regular fa-up-right-from-square',
          type: 'button',
          essential: true,
          tooltip: 'Open generated audio in a new tab'
        },
        {
          id: 'preview-close',
          label: '',
          icon: 'fa-solid fa-xmark',
          type: 'button',
          essential: true,
          tooltip: 'Close side preview'
        }
      ],
      onUtilityChange: (change) => {
        this.handleGeneratedAudioPreviewUtilityChange(media, change);
      },
      onClose: () => {
        this.closeDetachedPreview();
      }
    };
  }

  private handleGeneratedImagePreviewUtilityChange(media: GeneratedImageMedia, change: IUtilityChange): void {
    switch (change.utility.id) {
      case 'preview-open':
        this.openGeneratedImageInNewTab(media);
        break;
      case 'preview-close':
        this.closeDetachedPreview();
        break;
    }
  }

  private handleGeneratedAudioPreviewUtilityChange(media: GeneratedAudioMedia, change: IUtilityChange): void {
    switch (change.utility.id) {
      case 'preview-open':
        this.openGeneratedAudioInNewTab(media);
        break;
      case 'preview-close':
        this.closeDetachedPreview();
        break;
    }
  }

  private getPrimaryArtifactOpenHref(artifact: ToolArtifactResource): string | null {
    if (this.canLaunchArtifact(artifact)) {
      return this.getArtifactLaunchHref(artifact);
    }

    return this.getArtifactHostedHref(artifact) || this.getArtifactRawHref(artifact);
  }

  private getArtifactPopoverKey(artifacts: ToolArtifactResource[]): string {
    return artifacts.map((artifact) => artifact.id).join('|');
  }

  openArtifactSelectorPopover(artifacts: ToolArtifactResource[], event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (artifacts.length < 2) {
      return;
    }

    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor || !this.artifactSelectorPopoverTemplate) {
      return;
    }

    const popoverKey = this.getArtifactPopoverKey(artifacts);
    if (this.activeArtifactPopoverKey === popoverKey) {
      this.closeArtifactSelectorPopover();
      return;
    }

    this.closeToolPopover();
    this.closeGeneratedImageOptionsPopover();
    this.closeGeneratedAudioOptionsPopover();

    this.activeArtifactPopoverResources = artifacts;
    this.activeArtifactPopoverKey = popoverKey;
    this.uiPopoverService.open({
      element: anchor,
      placement: 'bottom-right',
      align: 'end',
      offset: 8,
      bodyTemplate: this.artifactSelectorPopoverTemplate,
      backdrop: true,
      backdropDismiss: true,
      noPadding: true,
      showCloseButton: false,
      dimensions: {
        width: 320,
        maxWidth: 'calc(100vw - 16px)'
      },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetArtifactPopoverState();
        this.cdr.markForCheck();
      }
    });

    this.cdr.markForCheck();
  }

  closeArtifactSelectorPopover(): void {
    if (this.activeArtifactPopoverKey === null) {
      this.resetArtifactPopoverState();
      this.cdr.markForCheck();
      return;
    }

    this.uiPopoverService.close();
  }

  private resetArtifactPopoverState(): void {
    this.activeArtifactPopoverResources = [];
    this.activeArtifactPopoverKey = null;
  }

  private openHrefInNewTab(href: string | null): void {
    if (!href || typeof window === 'undefined') {
      return;
    }

    window.open(this.normalizeHrefForNewTab(href), '_blank', 'noopener,noreferrer');
  }

  private normalizeHrefForNewTab(href: string): string {
    try {
      const parsed = new URL(href);
      parsed.searchParams.delete('previewShell');
      parsed.searchParams.delete('contentOnly');
      return parsed.toString();
    } catch {
      return href;
    }
  }

  openPreviewModal(preview: ToolPreviewResource, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.previewExpandedTemplate) {
      return;
    }

    if (this.usesDetachedPreviewSurface() && this.toggleDetachedPreviewIfActive('tool-preview', preview.id)) {
      return;
    }

    this.resetArtifactPreviewState();
    this.resetGeneratedImagePreviewState();
    this.resetGeneratedAudioPreviewState();
    this.activePreviewResource = preview;
    this.previewModalZoom = this.getPreviewZoom(preview);

    if (this.usesDetachedPreviewSurface()) {
      this.emitDetachedPreview(this.buildToolPreviewOutlet(preview));
      this.cdr.markForCheck();
      return;
    }

    this.uiModalService.open({
      title: preview.title,
      subtitle: preview.subtitle,
      headerRightTemplate: this.previewHeaderActionsTemplate,
      bodyTemplate: this.previewExpandedTemplate,
      bodyClass: 'chat-preview-modal-body',
      size: 'full',
      hideFooter: true,
      onClose: () => {
        this.resetPreviewModalState();
      }
    });

    this.cdr.markForCheck();
  }

  private resetPreviewModalState(): void {
    this.activePreviewResource = null;
    this.previewModalZoom = 1;
    this.cdr.markForCheck();
  }

  private resetGeneratedImagePreviewState(): void {
    this.closeGeneratedImageOptionsPopover();
    this.activeGeneratedImage = null;
    this.cdr.markForCheck();
  }

  private resetGeneratedImagePopoverState(): void {
    this.activeGeneratedImagePopover = null;
    this.activeGeneratedImagePopoverKey = null;
  }

  private resetGeneratedAudioPreviewState(): void {
    this.closeGeneratedAudioOptionsPopover();
    this.activeGeneratedAudio = null;
    this.cdr.markForCheck();
  }

  private resetGeneratedAudioPopoverState(): void {
    this.activeGeneratedAudioPopover = null;
    this.activeGeneratedAudioPopoverElement = null;
    this.activeGeneratedAudioPopoverKey = null;
  }

  getActivePreviewZoomPercent(): number {
    return Math.round(this.previewModalZoom * 100);
  }

  getPreviewModalFrameWidth(): string {
    return `${(100 / this.previewModalZoom).toFixed(3)}%`;
  }

  getPreviewModalFrameHeight(): string {
    return `calc(100% / ${this.previewModalZoom})`;
  }

  decreasePreviewModalZoom(): void {
    if (!this.activePreviewResource) {
      return;
    }
    this.setPreviewZoom(this.activePreviewResource, this.previewModalZoom - this.previewZoomStep);
  }

  increasePreviewModalZoom(): void {
    if (!this.activePreviewResource) {
      return;
    }
    this.setPreviewZoom(this.activePreviewResource, this.previewModalZoom + this.previewZoomStep);
  }

  resetPreviewModalZoom(): void {
    if (!this.activePreviewResource) {
      return;
    }
    this.setPreviewZoom(this.activePreviewResource, this.getDefaultPreviewZoom(this.activePreviewResource));
  }

  private getDefaultPreviewZoom(preview: ToolPreviewResource): number {
    if (typeof preview.defaultZoom === 'number' && Number.isFinite(preview.defaultZoom)) {
      return this.clampPreviewZoom(preview.defaultZoom);
    }

    return 1;
  }

  private clampPreviewZoom(value: number): number {
    if (!Number.isFinite(value)) {
      return 1;
    }

    return Math.min(this.maxPreviewZoom, Math.max(this.minPreviewZoom, Math.round(value * 100) / 100));
  }

  private setPreviewZoom(preview: ToolPreviewResource, zoom: number): void {
    const normalizedZoom = this.clampPreviewZoom(zoom);
    this.previewZoomById.set(preview.id, normalizedZoom);

    if (this.activePreviewResource?.id === preview.id) {
      this.previewModalZoom = normalizedZoom;
      this.refreshDetachedPreviewOutlet();
    }

    this.cdr.markForCheck();
  }

  private normalizeEmulatorMode(value: unknown): ToolPreviewResource['emulatorMode'] | undefined {
    return value === 'twocol'
      || value === 'manager'
      || value === 'demo'
      ? value
      : undefined;
  }

  getArtifactResources(message: any): ToolArtifactResource[] {
    if (this.shouldHideToolArtifacts(message)) {
      return [];
    }

    const payload = this.getToolPayload(message);
    if (!payload) {
      return [];
    }

    const resources = new Map<string, ToolArtifactResource>();
    const artifacts = Array.isArray(payload?.structuredContent?.artifacts)
      ? payload.structuredContent.artifacts
      : [];
    const preferStructuredArtifacts = artifacts.length > 0;

    if (!preferStructuredArtifacts) {
      const uiBlocks = Array.isArray(payload.ui) ? payload.ui : [];
      for (const block of uiBlocks) {
        if (block?.type !== 'resource') {
          continue;
        }

        const uri = typeof block.uri === 'string' ? block.uri : undefined;
        const metadata = block?.metadata && typeof block.metadata === 'object'
          ? block.metadata as Record<string, unknown>
          : null;
        const relativePath = typeof metadata?.relativePath === 'string' ? metadata.relativePath : undefined;
        const persistedPath = typeof metadata?.persistedPath === 'string'
          ? metadata.persistedPath
          : uri;
        const storageUri = typeof metadata?.storageUri === 'string' ? metadata.storageUri : undefined;
        const downloadUrl = typeof metadata?.downloadUrl === 'string' ? metadata.downloadUrl : uri;
        const artifactId = typeof metadata?.artifactId === 'string' ? metadata.artifactId : undefined;
        const resourceKey = normalizeArtifactResourceKey(artifactId)
          || normalizeArtifactResourceKey(relativePath)
          || normalizeArtifactResourceKey(persistedPath)
          || normalizeArtifactResourceKey(storageUri)
          || normalizeArtifactResourceKey(downloadUrl)
          || normalizeArtifactResourceKey(block.title)
          || `ui:${resources.size}`;
        const id = artifactId || relativePath || persistedPath || downloadUrl || resourceKey;

        resources.set(resourceKey, mergeArtifactResource(resources.get(resourceKey), {
          id,
          title: typeof block.title === 'string' && block.title.trim().length > 0 ? block.title : 'Artifact',
          subtitle: typeof block.subtitle === 'string' && block.subtitle.trim().length > 0 ? block.subtitle : 'Artifact',
          ...(artifactId ? { artifactId } : {}),
          ...(downloadUrl ? { uri: downloadUrl } : {}),
          ...(storageUri ? { storageUri } : {}),
          ...(persistedPath ? { persistedPath } : {}),
          ...(typeof metadata?.kind === 'string' ? { kind: metadata.kind } : {}),
          ...(typeof metadata?.archiveFormat === 'string' ? { archiveFormat: metadata.archiveFormat } : {}),
          ...(relativePath ? { relativePath } : {}),
          ...(typeof block?.mimeType === 'string'
            ? { mimeType: block.mimeType }
            : typeof metadata?.mimeType === 'string' ? { mimeType: metadata.mimeType } : {})
        }));
      }
    }

    for (const artifact of artifacts) {
      const title = typeof artifact?.name === 'string' && artifact.name.trim().length > 0
        ? artifact.name
        : typeof artifact?.relativePath === 'string' && artifact.relativePath.trim().length > 0
          ? artifact.relativePath
          : 'Artifact';
      const id = typeof artifact?.id === 'string' && artifact.id.trim().length > 0
        ? artifact.id
        : typeof artifact?.relativePath === 'string' && artifact.relativePath.trim().length > 0
          ? artifact.relativePath
          : `${title}:${resources.size}`;
      const downloadUrl = typeof artifact?.downloadUrl === 'string' ? artifact.downloadUrl : undefined;
      const storageUri = typeof artifact?.storageUri === 'string' ? artifact.storageUri : undefined;
      const persistedPath = typeof artifact?.persistedPath === 'string'
        ? artifact.persistedPath
        : typeof artifact?.path === 'string'
          ? artifact.path
          : undefined;
      const subtitleParts = [artifact?.projectType, artifact?.mimeType].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

      const resourceKey = normalizeArtifactResourceKey(artifact?.relativePath)
        || normalizeArtifactResourceKey(persistedPath)
        || normalizeArtifactResourceKey(storageUri)
        || normalizeArtifactResourceKey(downloadUrl)
        || normalizeArtifactResourceKey(title)
        || id;

      resources.set(resourceKey, mergeArtifactResource(resources.get(resourceKey), {
        id,
        title,
        subtitle: subtitleParts.length > 0 ? subtitleParts.join(' • ') : 'Artifact',
        ...(typeof artifact?.sizeBytes === 'number' ? { sizeBytes: artifact.sizeBytes } : {}),
        ...(typeof artifact?.id === 'string' ? { artifactId: artifact.id } : {}),
        ...(typeof artifact?.relativePath === 'string' ? { relativePath: artifact.relativePath } : {}),
        ...(typeof artifact?.kind === 'string' ? { kind: artifact.kind } : {}),
        ...(typeof artifact?.archiveFormat === 'string' ? { archiveFormat: artifact.archiveFormat } : {}),
        ...(typeof artifact?.mimeType === 'string' ? { mimeType: artifact.mimeType } : {}),
        ...(storageUri ? { storageUri } : {}),
        ...(downloadUrl ? { uri: downloadUrl } : storageUri && /^https?:\/\//i.test(storageUri) ? { uri: storageUri } : {}),
        ...(persistedPath ? { persistedPath } : storageUri ? { persistedPath: storageUri } : {})
      }));
    }

    return Array.from(resources.values());
  }

  getVisibleArtifactResources(message: any, messageIndex: number): ToolArtifactResource[] {
    return this.getArtifactResources(message).filter((artifact) => this.isLatestArtifactResource(artifact, messageIndex));
  }

  private findArtifactMessageIndex(artifact: ToolArtifactResource): number | null {
    if (!Array.isArray(this.history)) {
      return null;
    }

    const artifactKeys = this.getArtifactResourceDedupeKeys(artifact);
    for (let index = 0; index < this.history.length; index += 1) {
      const hasArtifact = this.getArtifactResources(this.history[index]).some((candidate) =>
        this.resourceDedupeKeysIntersect(artifactKeys, this.getArtifactResourceDedupeKeys(candidate))
      );
      if (hasArtifact) {
        return index;
      }
    }

    return null;
  }

  private buildArtifactDeletedMessageContent(message: any, artifact: ToolArtifactResource): string | null {
    const rawContent = this.getRawMessageContent(message);
    const payload = rawContent ? this.tryParseJson(rawContent) : null;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }

    const nextPayload = { ...payload };

    if (payload.structuredContent && typeof payload.structuredContent === 'object') {
      const structuredContent = { ...payload.structuredContent };
      if (Array.isArray(payload.structuredContent.artifacts)) {
        structuredContent.artifacts = payload.structuredContent.artifacts.filter((candidate: any) =>
          !this.artifactDescriptorMatches(artifact, candidate)
        );
      }
      nextPayload.structuredContent = structuredContent;
    }

    if (Array.isArray(payload.ui)) {
      nextPayload.ui = payload.ui.filter((block: any) =>
        block?.type !== 'resource' || !this.artifactUiResourceMatches(artifact, block)
      );
    }

    return JSON.stringify(nextPayload);
  }

  private artifactDescriptorMatches(artifact: ToolArtifactResource, candidate: any): boolean {
    const candidateKeys = [
      normalizeArtifactResourceKey(candidate?.id),
      normalizeArtifactResourceKey(candidate?.relativePath),
      normalizeArtifactResourceKey(candidate?.persistedPath),
      normalizeArtifactResourceKey(candidate?.path),
      normalizeArtifactResourceKey(candidate?.storageUri),
      normalizeArtifactResourceKey(candidate?.downloadUrl),
      this.getResourceTitleDedupeKey(candidate?.name)
    ].filter((key): key is string => typeof key === 'string' && key.length > 0);

    return this.resourceDedupeKeysIntersect(this.getArtifactResourceDedupeKeys(artifact), candidateKeys);
  }

  private artifactUiResourceMatches(artifact: ToolArtifactResource, block: any): boolean {
    const metadata = block?.metadata && typeof block.metadata === 'object'
      ? block.metadata as Record<string, unknown>
      : {};
    const candidateKeys = [
      normalizeArtifactResourceKey(typeof metadata['artifactId'] === 'string' ? metadata['artifactId'] : undefined),
      normalizeArtifactResourceKey(typeof metadata['relativePath'] === 'string' ? metadata['relativePath'] : undefined),
      normalizeArtifactResourceKey(typeof metadata['persistedPath'] === 'string' ? metadata['persistedPath'] : undefined),
      normalizeArtifactResourceKey(typeof metadata['storageUri'] === 'string' ? metadata['storageUri'] : undefined),
      normalizeArtifactResourceKey(typeof metadata['downloadUrl'] === 'string' ? metadata['downloadUrl'] : undefined),
      normalizeArtifactResourceKey(typeof block?.uri === 'string' ? block.uri : undefined),
      this.getResourceTitleDedupeKey(block?.title)
    ].filter((key): key is string => typeof key === 'string' && key.length > 0);

    return this.resourceDedupeKeysIntersect(this.getArtifactResourceDedupeKeys(artifact), candidateKeys);
  }

  private isLatestPreviewResource(preview: ToolPreviewResource, messageIndex: number): boolean {
    const keys = this.getPreviewResourceDedupeKeys(preview);
    if (keys.length === 0 || !Array.isArray(this.history)) {
      return true;
    }

    for (let index = this.history.length - 1; index >= 0; index -= 1) {
      const hasMatchingPreview = this.getPreviewBlocks(this.history[index]).some((candidate) => this.resourceDedupeKeysIntersect(keys, this.getPreviewResourceDedupeKeys(candidate)));
      if (hasMatchingPreview) {
        return index === messageIndex;
      }
    }

    return true;
  }

  private getPreviewResourceDedupeKeys(preview: ToolPreviewResource): string[] {
    const keys = new Set<string>();
    const href = this.getPreviewHrefWithoutPreviewShell(preview.url || '').trim();
    if (href) {
      try {
        const parsed = new URL(href);
        parsed.hash = '';
        keys.add(`${parsed.origin}${parsed.pathname}`);
      } catch {
        keys.add(href);
      }
    }

    const titleKey = this.getResourceTitleDedupeKey(preview.title);
    if (titleKey) {
      keys.add(titleKey);
    }

    if (keys.size === 0 && preview.id) {
      keys.add(preview.id);
    }

    return Array.from(keys);
  }

  private isLatestArtifactResource(artifact: ToolArtifactResource, messageIndex: number): boolean {
    const keys = this.getArtifactResourceDedupeKeys(artifact);
    if (keys.length === 0 || !Array.isArray(this.history)) {
      return true;
    }

    for (let index = this.history.length - 1; index >= 0; index -= 1) {
      const hasMatchingArtifact = this.getArtifactResources(this.history[index]).some((candidate) => this.resourceDedupeKeysIntersect(keys, this.getArtifactResourceDedupeKeys(candidate)));
      if (hasMatchingArtifact) {
        return index === messageIndex;
      }
    }

    return true;
  }

  private getArtifactResourceDedupeKeys(artifact: ToolArtifactResource): string[] {
    const keys = [
      normalizeArtifactResourceKey(artifact.artifactId),
      normalizeArtifactResourceKey(artifact.relativePath),
      normalizeArtifactResourceKey(artifact.persistedPath),
      normalizeArtifactResourceKey(artifact.storageUri),
      normalizeArtifactResourceKey(artifact.uri),
      this.getResourceTitleDedupeKey(artifact.title),
      artifact.id
    ].filter((key): key is string => typeof key === 'string' && key.length > 0);

    return Array.from(new Set(keys));
  }

  private getResourceTitleDedupeKey(title: string | undefined): string | null {
    const normalizedTitle = normalizeArtifactResourceKey(title);
    if (!normalizedTitle || normalizedTitle === 'preview' || normalizedTitle === 'artifact') {
      return null;
    }

    return `title:${normalizedTitle}`;
  }

  private resourceDedupeKeysIntersect(left: string[], right: string[]): boolean {
    const rightSet = new Set(right);
    return left.some((key) => rightSet.has(key));
  }

  private shouldHideToolArtifacts(message: any): boolean {
    const toolName = String(message?.name || message?.display_name || '')
      .trim()
      .toLowerCase();

    return toolName === 'initialize_computer' || toolName === 'initialize computer';
  }

  getArtifactLaunchHref(artifact: ToolArtifactResource): string | null {
    return this.getArtifactHostedHref(artifact);
  }

  getArtifactHostedHref(artifact: ToolArtifactResource): string | null {
    return artifact.uri && /^https?:\/\//i.test(artifact.uri)
      ? artifact.uri
      : null;
  }

  getArtifactRawHref(artifact: ToolArtifactResource): string | null {
    const previewType = this.getArtifactPreviewType(artifact);
    if (previewType === 'markdown' || previewType === 'csv' || previewType === 'html' || previewType === 'svg' || previewType === 'text') {
      return this.getArtifactHostedHref(artifact);
    }

    return null;
  }

  canLaunchArtifact(artifact: ToolArtifactResource): boolean {
    const previewType = this.getArtifactPreviewType(artifact);
    if (!previewType || !this.getArtifactLaunchHref(artifact)) {
      return false;
    }

    return previewType === 'html'
      || previewType === 'svg'
      || previewType === 'image'
      || previewType === 'audio'
      || previewType === 'video';
  }

  private getArtifactPreviewModalSize(artifact: ToolArtifactResource): 'lg' | 'full' {
    const previewType = this.getArtifactPreviewType(artifact);
    return previewType === 'html' || previewType === 'zip' ? 'full' : 'lg';
  }

  getArtifactLocationLabel(artifact: ToolArtifactResource): string {
    return artifact.relativePath || artifact.persistedPath || artifact.uri || 'Stored with this thread';
  }

  canPreviewArtifact(artifact: ToolArtifactResource): boolean {
    const previewType = this.getArtifactPreviewType(artifact);
    if (previewType === null) {
      return false;
    }

    if (previewType === 'markdown' || previewType === 'csv' || previewType === 'html' || previewType === 'svg' || previewType === 'text') {
      return (!!artifact.artifactId && !!this.currentThreadId && this.getArtifactApiBaseCandidates().length > 0)
        || !!this.getArtifactStorageReferenceTarget(artifact)
        || !!this.getArtifactHostedHref(artifact);
    }

    return !!this.getArtifactPreviewSourceUrl(artifact)
      || (!!artifact.artifactId && !!this.currentThreadId && this.getArtifactApiBaseCandidates().length > 0);
  }

  async openArtifactPreview(artifact: ToolArtifactResource, event?: Event): Promise<void> {
    event?.stopPropagation();

    if (!this.canPreviewArtifact(artifact) || !this.artifactPreviewTemplate) {
      return;
    }

    if (this.usesDetachedPreviewSurface() && this.toggleDetachedPreviewIfActive('artifact', artifact.id)) {
      return;
    }

    this.activeArtifact = artifact;
    this.artifactPreview = null;
    this.artifactPreviewError = null;
    this.artifactPreviewLoading = false;
    this.artifactPreviewMode = 'preview';
    this.artifactRawDraft = '';
    this.artifactRawOriginal = '';
    this.artifactRawContentLoaded = false;
    this.artifactRawLoading = false;
    this.artifactRawSaving = false;
    this.artifactRawSaveError = null;
    this.artifactRawLastSavedAt = null;
    this.artifactDeleteError = null;
    this.artifactApiBaseUrl = null;

    this.resetPreviewModalState();
    this.resetGeneratedImagePreviewState();
    this.resetGeneratedAudioPreviewState();

    if (this.usesDetachedPreviewSurface()) {
      this.emitDetachedPreview(this.buildArtifactPreviewOutlet(artifact));
    } else {
      this.uiModalService.open({
        title: artifact.title,
        subtitle: artifact.subtitle,
        bodyTemplate: this.artifactPreviewTemplate,
        size: this.getArtifactPreviewModalSize(artifact),
        hideFooter: false,
        buttons: this.getArtifactModalButtons(),
        onClose: () => {
          this.resetArtifactPreviewState();
        }
      });
    }

    this.cdr.markForCheck();

    const previewType = this.getArtifactPreviewType(artifact);
    let previewSourceUrl = this.getArtifactPreviewSourceUrl(artifact, undefined, Date.now());

    if (!previewSourceUrl && previewType && this.getArtifactStorageReferenceTarget(artifact)) {
      try {
        previewSourceUrl = this.appendCacheBust(await this.resolveArtifactDownloadUrl(artifact), Date.now());
      } catch {
        previewSourceUrl = null;
      }
    }

    if (artifact.artifactId && this.currentThreadId) {
      void this.resolveArtifactPreviewBaseUrl(artifact);
    }

    if ((previewType === 'html' || previewType === 'svg' || previewType === 'image' || previewType === 'audio' || previewType === 'video' || previewType === 'zip' || previewType === 'file') && previewSourceUrl) {
      this.artifactPreview = this.buildArtifactPreviewState(artifact, '', {
        truncated: false,
        sizeBytes: typeof artifact.sizeBytes === 'number' ? artifact.sizeBytes : 0
      }, {
        preferRemoteUrl: previewSourceUrl
      });
    } else {
      this.artifactPreviewLoading = true;
    }

    this.refreshArtifactModalButtons();
    this.cdr.markForCheck();

    try {
      if (!artifact.artifactId || !this.currentThreadId || this.getArtifactApiBaseCandidates().length === 0) {
        if (!this.artifactPreview) {
          throw new Error('Artifact preview is not available for this file yet.');
        }
        return;
      }

      const shouldFetchImmediately = previewType === 'markdown'
        || previewType === 'csv'
        || previewType === 'html'
        || previewType === 'svg'
        || previewType === 'text';

      if (!shouldFetchImmediately) {
        return;
      }

      const { response, baseUrl } = await this.loadArtifactText(artifact);
      this.artifactApiBaseUrl = baseUrl;
      const responsePreviewType = this.getArtifactPreviewType({
        ...artifact,
        ...(typeof response?.artifact?.mimeType === 'string' ? { mimeType: response.artifact.mimeType } : {}),
        ...(typeof response?.artifact?.relativePath === 'string' ? { relativePath: response.artifact.relativePath } : {}),
        ...(typeof response?.artifact?.storageUri === 'string' ? { storageUri: response.artifact.storageUri } : {})
      });
      const content = typeof response?.content?.text === 'string' ? response.content.text : '';

      if (!responsePreviewType) {
        throw new Error('Preview is not available for this artifact type.');
      }

      const resolvedArtifact: ToolArtifactResource = {
        ...artifact,
        ...(typeof response?.artifact?.downloadUrl === 'string' ? { uri: response.artifact.downloadUrl } : {}),
        ...(typeof response?.artifact?.mimeType === 'string' ? { mimeType: response.artifact.mimeType } : {}),
        ...(typeof response?.artifact?.kind === 'string' ? { kind: response.artifact.kind } : {}),
        ...(typeof response?.artifact?.archiveFormat === 'string' ? { archiveFormat: response.artifact.archiveFormat } : {}),
        ...(typeof response?.artifact?.relativePath === 'string' ? { relativePath: response.artifact.relativePath } : {}),
        ...(typeof response?.artifact?.storageUri === 'string' ? { storageUri: response.artifact.storageUri } : {})
      };

      this.artifactRawOriginal = content;
      this.artifactRawDraft = content;
      this.artifactRawContentLoaded = true;
      this.artifactRawLoading = false;
      this.artifactRawSaveError = null;
      this.activeArtifact = resolvedArtifact;

      this.artifactPreview = this.buildArtifactPreviewState(resolvedArtifact, content, {
        truncated: response?.content?.truncated === true,
        sizeBytes: typeof response?.content?.sizeBytes === 'number' ? response.content.sizeBytes : content.length
      });
    } catch (error) {
      if (this.artifactPreview) {
        this.artifactPreviewError = null;
      } else {
        this.artifactPreviewError = this.getArtifactPreviewErrorMessage(error);
      }
    } finally {
      this.artifactPreviewLoading = false;
      this.artifactRawLoading = false;
      this.refreshArtifactModalButtons();
      this.refreshDetachedPreviewOutlet();
      this.cdr.markForCheck();
    }
  }

  async onArtifactRawInput(event: Event): Promise<void> {
    const target = event.target as HTMLTextAreaElement | null;
    this.artifactRawDraft = target?.value || '';
    this.artifactRawSaveError = null;
    this.refreshArtifactModalButtons();
    this.refreshDetachedPreviewOutlet();
    this.cdr.markForCheck();
  }

  async setArtifactPreviewMode(mode: ArtifactPreviewMode): Promise<void> {
    if (this.artifactPreviewMode === mode || this.artifactPreviewLoading || this.artifactRawSaving) {
      return;
    }

    this.artifactPreviewMode = mode;
    this.artifactRawSaveError = null;
    this.refreshDetachedPreviewOutlet();
    this.cdr.markForCheck();

    if (mode === 'raw') {
      await this.ensureArtifactRawContentLoaded();
    }

    if (mode === 'preview' && this.activeArtifact) {
      this.artifactPreview = this.buildArtifactPreviewState(this.activeArtifact, this.artifactRawOriginal, {
        truncated: false,
        sizeBytes: this.getTextByteSize(this.artifactRawOriginal)
      }, {
        ...((this.getArtifactPreviewType(this.activeArtifact) === 'html' || this.getArtifactPreviewType(this.activeArtifact) === 'svg')
          ? { preferRemoteUrl: this.getArtifactPreviewSourceUrl(this.activeArtifact, this.artifactApiBaseUrl, Date.now()) }
          : {})
      });
    }

    this.refreshArtifactModalButtons();
    this.refreshDetachedPreviewOutlet();
    this.cdr.markForCheck();
  }

  canEditArtifactRaw(): boolean {
    if (!this.activeArtifact) {
      return false;
    }

    const previewType = this.getArtifactPreviewType(this.activeArtifact);
    return previewType === 'markdown' || previewType === 'csv' || previewType === 'html' || previewType === 'svg' || previewType === 'text';
  }

  canShowRawArtifactEditor(): boolean {
    return this.canEditArtifactRaw() && !this.artifactPreviewLoading;
  }

  canSaveArtifactRaw(): boolean {
    return this.canEditArtifactRaw()
      && this.artifactPreviewMode === 'raw'
      && !!this.activeArtifact?.artifactId
      && !!this.currentThreadId
      && !this.artifactRawLoading
      && this.getArtifactApiBaseCandidates().length > 0
      && !this.artifactRawSaving
      && this.artifactRawContentLoaded
      && this.artifactRawDraft !== this.artifactRawOriginal;
  }

  getArtifactRawStatusLabel(): string {
    if (this.artifactDeleteError) {
      return this.artifactDeleteError;
    }

    if (this.artifactRawSaving) {
      return 'Saving changes...';
    }

    if (this.artifactRawLoading) {
      return 'Loading full file...';
    }

    if (this.artifactRawSaveError) {
      return this.artifactRawSaveError;
    }

    if (this.artifactPreviewMode === 'raw' && this.artifactRawDraft !== this.artifactRawOriginal) {
      return 'Unsaved changes';
    }

    if (this.artifactRawLastSavedAt) {
      return this.artifactRawLastSavedAt;
    }

    return 'Edit the raw file and save to update the artifact.';
  }

  getArtifactRawStatusClass(): string | null {
    if (this.artifactRawSaveError || this.artifactDeleteError) {
      return 'artifact-preview-toolbar__status--error';
    }

    if (this.artifactPreviewMode === 'raw' && this.artifactRawDraft !== this.artifactRawOriginal) {
      return 'artifact-preview-toolbar__status--dirty';
    }

    return null;
  }

  async saveArtifactRaw(): Promise<void> {
    if (!this.canSaveArtifactRaw() || !this.activeArtifact?.artifactId || !this.currentThreadId) {
      return;
    }

    this.artifactRawSaving = true;
    this.artifactRawSaveError = null;
    this.refreshArtifactModalButtons();
    this.refreshDetachedPreviewOutlet();
    this.cdr.markForCheck();

    try {
      const preferredBaseUrl = await this.ensureArtifactApiBaseResolved(this.activeArtifact);
      let saveResult: { response: ArtifactUpdateResponse; baseUrl: string | null };

      try {
        saveResult = await this.updateArtifactViaBackend(
          this.activeArtifact,
          this.artifactRawDraft,
          preferredBaseUrl
        );
      } catch (backendError) {
        const storageTarget = this.getArtifactStorageReferenceTarget(this.activeArtifact);
        const hostedHref = this.getArtifactHostedHref(this.activeArtifact);
        if (!storageTarget && !hostedHref) {
          throw backendError;
        }

        saveResult = await this.updateArtifactViaStorage(this.activeArtifact, this.artifactRawDraft);
      }

      const { response, baseUrl } = saveResult;
      this.artifactApiBaseUrl = baseUrl;

      const savedText = typeof response?.content?.text === 'string' ? response.content.text : this.artifactRawDraft;
      const sizeBytes = typeof response?.content?.sizeBytes === 'number'
        ? response.content.sizeBytes
        : this.getTextByteSize(savedText);

      this.artifactRawOriginal = savedText;
      this.artifactRawDraft = savedText;
      this.artifactRawContentLoaded = true;
      this.artifactRawLoading = false;
      this.artifactRawLastSavedAt = 'Saved just now';
      this.artifactRawSaveError = null;

      this.activeArtifact = {
        ...this.activeArtifact,
        ...(typeof response?.artifact?.downloadUrl === 'string' ? { uri: response.artifact.downloadUrl } : {}),
        ...(typeof response?.artifact?.relativePath === 'string' ? { relativePath: response.artifact.relativePath } : {}),
        ...(typeof response?.artifact?.mimeType === 'string' ? { mimeType: response.artifact.mimeType } : {}),
        ...(typeof response?.artifact?.kind === 'string' ? { kind: response.artifact.kind } : {}),
        ...(typeof response?.artifact?.archiveFormat === 'string' ? { archiveFormat: response.artifact.archiveFormat } : {}),
        ...(typeof response?.artifact?.storageUri === 'string' ? { storageUri: response.artifact.storageUri } : {}),
        sizeBytes
      };

      this.artifactPreview = this.buildArtifactPreviewState(this.activeArtifact, savedText, {
        truncated: false,
        sizeBytes
      }, {
        ...((this.getArtifactPreviewType(this.activeArtifact) === 'html' || this.getArtifactPreviewType(this.activeArtifact) === 'svg')
          ? { preferRemoteUrl: this.getArtifactPreviewSourceUrl(this.activeArtifact, baseUrl, Date.now()) }
          : {})
      });
    } catch (error) {
      console.error('[artifact-save] Failed', {
        threadId: this.currentThreadId,
        artifactId: this.activeArtifact?.artifactId,
        relativePath: this.activeArtifact?.relativePath,
        preferredBaseUrl: this.artifactApiBaseUrl,
        candidateBaseUrls: this.getArtifactApiBaseCandidates(this.artifactApiBaseUrl || undefined)
      }, error);
      this.artifactRawSaveError = this.getArtifactPreviewErrorMessage(error);
    } finally {
      this.artifactRawSaving = false;
      this.refreshArtifactModalButtons();
      this.refreshDetachedPreviewOutlet();
      this.cdr.markForCheck();
    }
  }

  getArtifactPreviewRows(): string[][] {
    return this.artifactPreview?.rows || [];
  }

  getArtifactPreviewHeader(): string[] {
    return this.getArtifactPreviewRows()[0] || [];
  }

  getArtifactPreviewBody(): string[][] {
    return this.getArtifactPreviewRows().slice(1);
  }

  onZipArtifactSaved(event: ZipArtifactSavedEvent): void {
    if (!this.activeArtifact || !this.artifactPreview || this.artifactPreview.type !== 'zip') {
      return;
    }

    const timestamp = Date.now();
    this.activeArtifact = {
      ...this.activeArtifact,
      uri: event.downloadUrl,
      sizeBytes: event.sizeBytes
    };
    this.artifactPreview = {
      ...this.artifactPreview,
      fileUrl: this.appendCacheBust(event.downloadUrl, timestamp),
      sizeBytes: event.sizeBytes,
      truncated: false
    };
    this.refreshDetachedPreviewOutlet();
    this.cdr.markForCheck();
  }

  onZipArtifactDeleted(
    artifact: ToolArtifactResource | null,
    message: any | null,
    messageIndex: number | null,
    event: ZipArtifactDeletedEvent
  ): void {
    this.removeDeletedArtifactFromThread(artifact, message, messageIndex, event);
  }

  async deleteArtifactResource(
    artifact: ToolArtifactResource | null,
    message: any | null,
    messageIndex: number | null,
    event?: Event
  ): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (!artifact) {
      return;
    }

    const storageTarget = this.getArtifactStorageReferenceTarget(artifact);
    if (!storageTarget || this.isArtifactResourceDeleting(artifact)) {
      return;
    }

    const confirmation = await this.alertService.open({
      heading: `Delete ${artifact.title || 'artifact'}?`,
      description: 'This deletes the storage object and removes it from this thread. This action cannot be undone.',
      buttons: [
        { id: 'cancel', label: 'Cancel', icon: 'fa-xmark', color: 'neutral' },
        { id: 'delete', label: 'Delete', icon: 'fa-trash-can', color: 'danger' }
      ]
    });
    if (!confirmation.button || confirmation.button.id !== 'delete') {
      return;
    }

    const deletingKey = this.getArtifactDeletingKey(artifact, storageTarget);
    this.artifactDeletingKeys.add(deletingKey);
    this.artifactDeleteError = null;
    this.refreshDetachedPreviewOutlet();
    this.cdr.markForCheck();

    try {
      const storageRef = this.afStorage.storage.refFromURL(storageTarget);
      await storageRef.delete();
      this.removeDeletedArtifactFromThread(artifact, message, messageIndex, { storageTarget });
    } catch (error: any) {
      if (error?.code === 'storage/object-not-found') {
        this.removeDeletedArtifactFromThread(artifact, message, messageIndex, { storageTarget });
        return;
      }

      this.artifactDeleteError = error?.message || 'Failed to delete artifact from storage.';
      console.error('[artifact-delete] Failed', {
        threadId: this.currentThreadId,
        artifactId: artifact.artifactId,
        relativePath: artifact.relativePath,
        storageTarget
      }, error);
    } finally {
      this.artifactDeletingKeys.delete(deletingKey);
      this.refreshDetachedPreviewOutlet();
      this.cdr.markForCheck();
    }
  }

  canDeleteArtifactResource(artifact: ToolArtifactResource | null | undefined): boolean {
    return !!artifact
      && !!this.getArtifactStorageReferenceTarget(artifact)
      && !this.isArtifactResourceDeleting(artifact);
  }

  shouldShowStandaloneArtifactDelete(artifact: ToolArtifactResource | null | undefined): boolean {
    return !!artifact
      && this.getArtifactPreviewType(artifact) !== 'zip'
      && (!!this.getArtifactStorageReferenceTarget(artifact) || this.isArtifactResourceDeleting(artifact));
  }

  shouldShowArtifactSelectorDelete(artifact: ToolArtifactResource | null | undefined): boolean {
    return !!artifact
      && (!!this.getArtifactStorageReferenceTarget(artifact) || this.isArtifactResourceDeleting(artifact));
  }

  isArtifactResourceDeleting(artifact: ToolArtifactResource | null | undefined): boolean {
    if (!artifact) {
      return false;
    }

    return this.artifactDeletingKeys.has(this.getArtifactDeletingKey(artifact));
  }

  getArtifactDeleteLabel(artifact: ToolArtifactResource): string {
    return this.isArtifactResourceDeleting(artifact)
      ? `Deleting ${artifact.title || 'artifact'}`
      : `Delete ${artifact.title || 'artifact'}`;
  }

  private removeDeletedArtifactFromThread(
    artifact: ToolArtifactResource | null,
    message: any | null,
    messageIndex: number | null,
    event: ArtifactDeletedEvent
  ): void {
    if (!artifact) {
      return;
    }

    const deletedArtifact: ToolArtifactResource = {
      ...artifact,
      storageUri: artifact.storageUri || event.storageTarget,
      persistedPath: artifact.persistedPath || event.storageTarget
    };
    const targetMessageIndex = typeof messageIndex === 'number'
      ? messageIndex
      : this.findArtifactMessageIndex(deletedArtifact);
    const targetMessage = message || (typeof targetMessageIndex === 'number' ? this.history[targetMessageIndex] : null);

    if (!targetMessage || typeof targetMessageIndex !== 'number' || targetMessageIndex < 0) {
      return;
    }

    const updatedContent = this.buildArtifactDeletedMessageContent(targetMessage, deletedArtifact);
    if (!updatedContent) {
      return;
    }

    if (this.activeArtifact && this.resourceDedupeKeysIntersect(
      this.getArtifactResourceDedupeKeys(this.activeArtifact),
      this.getArtifactResourceDedupeKeys(deletedArtifact)
    )) {
      this.uiModalService.close();
      this.closeDetachedPreview();
    }

    this.inlineArtifactPreviewStates.delete(this.getInlineArtifactPreviewKey(deletedArtifact));
    this.collapsedInlinePreviewKeys.delete(this.getArtifactInlinePreviewCollapseKey(deletedArtifact));
    if (this.activeArtifactPopoverResources.length > 0) {
      const deletedKeys = this.getArtifactResourceDedupeKeys(deletedArtifact);
      this.activeArtifactPopoverResources = this.activeArtifactPopoverResources.filter((candidate) =>
        !this.resourceDedupeKeysIntersect(deletedKeys, this.getArtifactResourceDedupeKeys(candidate))
      );
    }
    this.updateMessage.emit({
      messageIndex: targetMessageIndex,
      messageId: targetMessage.id,
      content: updatedContent
    });
    this.cdr.markForCheck();
  }

  private getArtifactDeletingKey(artifact: ToolArtifactResource, storageTarget?: string | null): string {
    return storageTarget
      || this.getArtifactStorageReferenceTarget(artifact)
      || this.getInlineArtifactPreviewKey(artifact);
  }

  onZipPreviewViewerStateChange(state: ZipPreviewViewerState): void {
    this.zipPreviewViewerState = state;
    this.refreshDetachedPreviewOutlet();
  }

  trackByArtifact(_index: number, artifact: ToolArtifactResource): string {
    return artifact.id;
  }

  getMessageRoleLabel(message: any): string {
    if (this.isCompactionMarker(message)) {
      return 'Compaction';
    }

    if (this.shouldUseToolLayout(message)) {
      return 'Tool';
    }

    return this.getRoleDisplayName(message.role);
  }

  shouldShowMessageFooter(message?: any): boolean {
    return this.displayMode !== 'minimal' && !this.shouldUseToolLayout(message);
  }

  shouldShowMessageOptions(message: any): boolean {
    return this.displayMode === 'debug' && this.hasMessageDetails(message);
  }

  shouldShowInlineToolOptions(message: any): boolean {
    return this.displayMode === 'debug' && this.shouldUseToolLayout(message) && this.hasMessageDetails(message);
  }

  shouldShowDeleteButton(): boolean {
    return this.displayMode === 'debug';
  }

  shouldShowTimestamp(message: any): boolean {
    return this.displayMode === 'debug' && !!message.createdAt;
  }

  getSuggestedFollowUps(message: any): string[] {
    const rawSuggestedFollowUps = Array.isArray(message?.suggestedFollowUps)
      ? message.suggestedFollowUps
      : Array.isArray(message?.message?.suggestedFollowUps)
        ? message.message.suggestedFollowUps
        : [];

    const unique = new Set<string>();
    const followUps: string[] = [];

    for (const item of rawSuggestedFollowUps) {
      if (typeof item !== 'string') {
        continue;
      }

      const normalized = item.replace(/\s+/g, ' ').trim();
      if (!normalized) {
        continue;
      }

      const dedupeKey = normalized.toLowerCase();
      if (unique.has(dedupeKey)) {
        continue;
      }

      unique.add(dedupeKey);
      followUps.push(normalized);

      if (followUps.length === 3) {
        break;
      }
    }

    return followUps;
  }

  shouldShowSuggestedFollowUps(message: any, index: number): boolean {
    if (this.displayMode === 'minimal' || this.loading) {
      return false;
    }

    const threadStatus = typeof this.currentThread?.status === 'string' ? this.currentThread.status : '';
    if (threadStatus === 'running' || threadStatus === 'continuing') {
      return false;
    }

    const followUps = this.getSuggestedFollowUps(message);
    if (followUps.length !== 3) {
      return false;
    }

    return index === this.getLastRenderedMessageIndex();
  }

  shouldShowMessageAnnotationControls(message: any, index: number): boolean {
    if (this.displayMode !== 'debug' || this.loading || this.shouldUseToolLayout(message) || this.isCompactionMarker(message)) {
      return false;
    }

    if (!(message?.role === 'assistant' || message?.role === 'model')) {
      return false;
    }

    const threadStatus = typeof this.currentThread?.status === 'string' ? this.currentThread.status : '';
    if (threadStatus === 'running' || threadStatus === 'continuing') {
      return false;
    }

    return index === this.getLastRenderedMessageIndex();
  }

  getMessageAnnotationKey(message: any, index: number): string {
    return getOptionalString(message?.id) || `index-${index}`;
  }

  isMessageAnnotationSaving(message: any, index: number): boolean {
    return this.annotationSavingKeys.has(this.getMessageAnnotationKey(message, index));
  }

  isMessageAnnotationSaved(message: any, index: number): boolean {
    return this.annotationSavedKey === this.getMessageAnnotationKey(message, index);
  }

  hasMessageAnnotationError(message: any, index: number): boolean {
    return this.annotationErrorKey === this.getMessageAnnotationKey(message, index);
  }

  isMessageThumbAnnotationActive(message: any, index: number, rating: EvalAnnotationThumbRating): boolean {
    return this.getMessageAnnotationSummary(message, index).thumbRating === rating;
  }

  isMessageStarAnnotationActive(message: any, index: number, rating: number): boolean {
    return rating <= (this.getMessageAnnotationSummary(message, index).starRating || 0);
  }

  hasMessageNoteAnnotation(message: any, index: number): boolean {
    return this.getMessageAnnotationSummary(message, index).hasNote;
  }

  getMessageNoteAnnotationIcon(message: any, index: number): string {
    return this.hasMessageNoteAnnotation(message, index) ? 'fa-solid fa-note-sticky' : 'fa-regular fa-note-sticky';
  }

  async onMessageThumbAnnotation(message: any, index: number, rating: EvalAnnotationThumbRating, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    await this.saveMessageAnnotation(message, index, { thumbRating: rating });
  }

  async onMessageStarAnnotation(message: any, index: number, rating: number, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    await this.saveMessageAnnotation(message, index, { starRating: rating });
  }

  async openMessageNoteAnnotation(message: any, index: number, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    const key = this.getMessageAnnotationKey(message, index);
    const target = await this.resolveMessageAnnotationTarget(message, key);
    const anchor = event?.currentTarget instanceof HTMLElement ? event.currentTarget : undefined;
    const agencyId = this.getActiveThreadAgencyId();

    if (!target || !agencyId) {
      return;
    }

    const summary = await this.ensureMessageAnnotationSummary(key, agencyId, target);

    this.evalAnnotationService.requestQuickAnnotation({
      agencyId,
      target,
      anchor,
      position: anchor ? 'anchor' : 'bottom-right',
      systems: ['note', 'thumbs', 'stars'],
      title: 'Annotate response',
      subtitle: 'Attach a note to this trace.',
      source: 'agent-debug-message',
      initialValues: {
        note: summary.note,
        thumbRating: summary.thumbRating || null,
        starRating: summary.starRating
      },
      metadata: {
        threadId: this.currentThreadId,
        messageId: message?.id || null,
        role: message?.role || null
      }
    });
  }

  onSuggestedFollowUpClick(followUp: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const normalized = followUp.replace(/\s+/g, ' ').trim();
    if (!normalized || this.loading) {
      return;
    }

    this.suggestedFollowUpSelected.emit(normalized);
  }

  private async saveMessageAnnotation(
    message: any,
    index: number,
    rating: { thumbRating?: EvalAnnotationThumbRating; starRating?: number }
  ): Promise<void> {
    const key = this.getMessageAnnotationKey(message, index);
    if (this.annotationSavingKeys.has(key)) {
      return;
    }

    const target = await this.resolveMessageAnnotationTarget(message, key);
    const agencyId = this.getActiveThreadAgencyId();
    if (!target || !agencyId) {
      return;
    }

    this.annotationSavingKeys.add(key);
    this.annotationErrorKey = '';
    this.cdr.markForCheck();

    try {
      await this.evalAnnotationService.createAnnotation({
        agencyId,
        target,
        note: '',
        golden: false,
        thumbRating: rating.thumbRating || null,
        starRating: rating.starRating || null,
        metadata: {
          source: 'agent-debug-message-inline',
          threadId: this.currentThreadId,
          messageId: message?.id || null,
          role: message?.role || null
        }
      });
      this.annotationSavedKey = key;
      this.applyAnnotationToMessageSummary(key, {
        thumbRating: rating.thumbRating || null,
        starRating: rating.starRating || null,
        note: ''
      });
      window.setTimeout(() => {
        if (this.annotationSavedKey === key) {
          this.annotationSavedKey = '';
          this.cdr.markForCheck();
        }
      }, 1800);
    } catch (error) {
      console.error('Failed to save message annotation:', error);
      this.annotationErrorKey = key;
    } finally {
      this.annotationSavingKeys.delete(key);
      this.cdr.markForCheck();
    }
  }

  private async resolveMessageAnnotationTarget(message: any, key: string, showError = true): Promise<EvalAnnotationTarget | null> {
    const agencyId = this.getActiveThreadAgencyId();
    const threadId = String(this.currentThreadId || '').trim();

    if (!agencyId || !threadId) {
      if (showError) {
        this.annotationErrorKey = key;
        this.cdr.markForCheck();
      }
      return null;
    }

    try {
      const usageEventId = await this.findMessageCreditUsageEventId(message, agencyId, threadId);
      const target = this.evalAnnotationService.buildThreadMessageTarget({
        agencyId,
        threadId,
        messageId: getOptionalString(message?.id),
        usageEventId
      });

      if (!target) {
        if (showError) {
          this.annotationErrorKey = key;
          this.cdr.markForCheck();
        }
      } else {
        this.annotationTargetKeysByMessageKey.set(key, this.evalAnnotationService.getTargetKeys(target));
      }

      return target;
    } catch (error) {
      console.error('Failed to resolve message annotation target:', error);
      if (showError) {
        this.annotationErrorKey = key;
        this.cdr.markForCheck();
      }
      return null;
    }
  }

  private getMessageAnnotationSummary(message: any, index: number): MessageAnnotationSummary {
    return this.messageAnnotationSummaries.get(this.getMessageAnnotationKey(message, index)) || {
      thumbRating: '',
      starRating: null,
      hasNote: false,
      note: ''
    };
  }

  private async ensureMessageAnnotationSummary(
    key: string,
    agencyId: string,
    target: EvalAnnotationTarget
  ): Promise<MessageAnnotationSummary> {
    try {
      const annotations = await this.evalAnnotationService.loadAnnotationsForTarget(agencyId, target);
      const summary = this.buildMessageAnnotationSummary(annotations);
      this.messageAnnotationSummaries.set(key, summary);
      this.cdr.markForCheck();
      return summary;
    } catch (error) {
      console.warn('Failed to load message annotations before opening annotation popover:', error);
      return this.messageAnnotationSummaries.get(key) || {
        thumbRating: '',
        starRating: null,
        hasNote: false,
        note: ''
      };
    }
  }

  private async syncVisibleMessageAnnotationSummary(): Promise<void> {
    if (this.displayMode !== 'debug') {
      this.annotationSummaryLoadKey = '';
      return;
    }

    const index = this.getLastRenderedMessageIndex();
    if (index < 0) {
      this.annotationSummaryLoadKey = '';
      return;
    }

    const message = this.history[index];
    if (!this.shouldShowMessageAnnotationControls(message, index)) {
      this.annotationSummaryLoadKey = '';
      return;
    }

    const key = this.getMessageAnnotationKey(message, index);
    const agencyId = this.getActiveThreadAgencyId();
    const loadKey = `${agencyId}:${this.currentThreadId || ''}:${key}`;
    if (this.annotationSummaryLoadKey === loadKey) {
      return;
    }

    this.annotationSummaryLoadKey = loadKey;
    const target = await this.resolveMessageAnnotationTarget(message, key, false);
    if (!target || this.annotationSummaryLoadKey !== loadKey) {
      return;
    }

    try {
      const annotations = await this.evalAnnotationService.loadAnnotationsForTarget(agencyId, target);
      if (this.annotationSummaryLoadKey !== loadKey) {
        return;
      }

      this.messageAnnotationSummaries.set(key, this.buildMessageAnnotationSummary(annotations));
      this.cdr.markForCheck();
    } catch (error) {
      console.warn('Failed to load message annotations:', error);
    }
  }

  private buildMessageAnnotationSummary(annotations: IEvalAnnotation[]): MessageAnnotationSummary {
    const summary: MessageAnnotationSummary = {
      thumbRating: '',
      starRating: null,
      hasNote: false,
      note: ''
    };

    const sortedAnnotations = [...annotations].sort((a, b) =>
      this.getAnnotationTimestampMs(a) - this.getAnnotationTimestampMs(b)
    );

    for (const annotation of sortedAnnotations) {
      if (annotation.thumbRating === 'up' || annotation.thumbRating === 'down') {
        summary.thumbRating = annotation.thumbRating;
      }

      if (typeof annotation.starRating === 'number' && annotation.starRating > 0) {
        summary.starRating = annotation.starRating;
      }

      const annotationNote = String(annotation.note || '').trim();
      if (annotationNote) {
        summary.hasNote = true;
        summary.note = annotationNote;
      }
    }

    return summary;
  }

  private getAnnotationTimestampMs(annotation: IEvalAnnotation): number {
    const value = annotation.updatedAt || annotation.createdAt;
    if (!value) {
      return 0;
    }

    if (typeof value.toMillis === 'function') {
      return value.toMillis();
    }

    if (typeof value.toDate === 'function') {
      return value.toDate().getTime();
    }

    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private applyAnnotationToMessageSummary(
    key: string,
    annotation: Pick<IEvalAnnotation, 'thumbRating' | 'starRating' | 'note'>
  ): void {
    const summary = this.messageAnnotationSummaries.get(key) || {
      thumbRating: '',
      starRating: null,
      hasNote: false,
      note: ''
    };

    if (annotation.thumbRating === 'up' || annotation.thumbRating === 'down') {
      summary.thumbRating = annotation.thumbRating;
    }

    if (typeof annotation.starRating === 'number' && annotation.starRating > 0) {
      summary.starRating = annotation.starRating;
    }

    const annotationNote = String(annotation.note || '').trim();
    if (annotationNote) {
      summary.hasNote = true;
      summary.note = annotationNote;
    }

    this.messageAnnotationSummaries.set(key, summary);
    this.cdr.markForCheck();
  }

  private onAnnotationSaved(event: EvalAnnotationSavedEvent): void {
    const eventTargetKeys = this.evalAnnotationService.getTargetKeys(event.target);
    if (!eventTargetKeys.length) {
      return;
    }

    for (const [messageKey, targetKeys] of this.annotationTargetKeysByMessageKey.entries()) {
      if (!targetKeys.some(targetKey => eventTargetKeys.includes(targetKey))) {
        continue;
      }

      this.applyAnnotationToMessageSummary(messageKey, event.annotation);
      break;
    }
  }

  getToolCallDisplayLabel(message: any): string {
    if (message?.display_name) {
      return this.normalizeToolLabel(message.display_name);
    }

    if (!Array.isArray(message?.tool_calls) || message.tool_calls.length === 0) {
      return 'Using tool';
    }

    if (message.tool_calls.length === 1) {
      const toolName = this.getFriendlyToolName(message.tool_calls[0]?.function?.name);
      return `Using ${toolName}`;
    }

    return `Using ${message.tool_calls.length} tools`;
  }

  getToolResultDisplayLabel(message: any): string {
    const derivedLabel = this.getDerivedToolResultLabel(message);
    if (derivedLabel) {
      return derivedLabel;
    }

    return message.display_name ? this.normalizeToolLabel(message.display_name) : 'Tool Succeeded';
  }

  isToolResultFailure(message: any): boolean {
    if (!this.shouldShowToolResult(message)) {
      return false;
    }

    const payload = this.getToolPayload(message);
    if (payload && typeof payload === 'object') {
      return payload.success === false || payload.error === true || payload.status === 'error' || payload.status === 'failed';
    }

    const label = this.getToolResultDisplayLabel(message).toLowerCase();
    return label.includes('failed') || label.includes('error');
  }

  getToolMessageIconClass(message: any): string {
    if (this.shouldShowToolCallSummary(message)) {
      return 'fa-regular fa-arrow-right';
    }

    return this.isToolResultFailure(message)
      ? 'fa-regular fa-circle-exclamation'
      : 'fa-regular fa-circle-check';
  }

  getRoleDisplayName(role: string): string {
    switch (role) {
      case 'user':
        return 'You';
      case 'model':
      case 'assistant':
        return 'AI';
      case 'tool':
        return 'Tool';
      case 'system':
        return 'System';
      default:
        return role;
    }
  }

  toggleToolPopover(messageIndex: number, event: MouseEvent): void {
    event.stopPropagation();

    if (this.openPopoverIndex === messageIndex) {
      this.closeToolPopover();
      return;
    }

    this.openToolPopoverByIndex(messageIndex);
  }

  openMinimalToolHistoryPopover(messageIndex: number, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor || !this.minimalToolHistoryPopoverTemplate) {
      return;
    }

    const items = this.getMinimalToolHistoryItems(messageIndex);
    if (items.length <= 1) {
      return;
    }

    if (this.isMinimalToolHistoryPopoverOpen) {
      this.closeMinimalToolHistoryPopover();
      return;
    }

    this.closeToolPopover();
    this.closeGeneratedImageOptionsPopover();
    this.closeGeneratedAudioOptionsPopover();
    this.closeArtifactSelectorPopover();

    this.activeMinimalToolHistoryItems = items;
    this.isMinimalToolHistoryPopoverOpen = true;
    this.uiPopoverService.open({
      element: anchor,
      placement: 'bottom-right',
      align: 'end',
      offset: 8,
      bodyTemplate: this.minimalToolHistoryPopoverTemplate,
      backdrop: true,
      backdropDismiss: true,
      noPadding: true,
      showCloseButton: false,
      dimensions: {
        width: 340,
        maxWidth: 'calc(100vw - 16px)'
      },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetMinimalToolHistoryPopoverState();
        this.cdr.markForCheck();
      }
    });

    this.cdr.markForCheck();
  }

  private openToolPopoverByIndex(messageIndex: number): void {
    this.closeGeneratedImageOptionsPopover();
    this.closeGeneratedAudioOptionsPopover();
    this.closeArtifactSelectorPopover();
    this.closeMinimalToolHistoryPopover();

    this.openPopoverIndex = messageIndex;
    this.activeMessage = this.history[messageIndex];
    this.activeMessageTraceLoading = false;
    this.activeMessageTraceError = '';
    this.refreshActiveMessageDetailTabs();
    this.activeMessageDetailsTab = this.getInitialActiveMessageDetailsTab(this.activeMessage);
    this.syncComputeMonitoring();

    this.uiModalService.open({
      title: this.getPopoverTitle(this.activeMessage),
      bodyTemplate: this.messageDetailsTemplate,
      bodyClass: 'message-details-modal-body',
      size: 'lg',
      hideFooter: true,
      onClose: () => {
        this.openPopoverIndex = null;
        this.activeMessage = null;
        this.activeMessageDetailTabs = [];
        this.activeMessageTraceLoading = false;
        this.activeMessageTraceError = '';
        this.syncComputeMonitoring();
      }
    });
  }

  closeToolPopover(): void {
    if (this.openPopoverIndex !== null) {
      this.uiModalService.close();
    }
    this.openPopoverIndex = null;
    this.activeMessage = null;
    this.activeMessageDetailsTab = 'details';
    this.activeMessageDetailTabs = [];
    this.activeMessageTraceLoading = false;
    this.activeMessageTraceError = '';
    this.syncComputeMonitoring();
  }

  closeMinimalToolHistoryPopover(): void {
    if (!this.isMinimalToolHistoryPopoverOpen) {
      this.resetMinimalToolHistoryPopoverState();
      this.cdr.markForCheck();
      return;
    }

    this.uiPopoverService.close();
  }

  private resetMinimalToolHistoryPopoverState(): void {
    this.activeMinimalToolHistoryItems = [];
    this.isMinimalToolHistoryPopoverOpen = false;
  }

  trackByMinimalToolHistoryItem(_index: number, item: MinimalToolHistoryItem): number {
    return item.index;
  }

  getActiveMessageDetailTabs(): MessageDetailsTab[] {
    return this.activeMessageDetailTabs;
  }

  isActiveMessageDetailsTab(tabId: MessageDetailsTabId): boolean {
    return this.activeMessageDetailsTab === tabId;
  }

  selectActiveMessageDetailsTab(tabId: MessageDetailsTabId, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.activeMessageDetailsTab === tabId) {
      return;
    }

    this.activeMessageDetailsTab = tabId;
    this.syncComputeMonitoring();

    if (this.hasViewInitialized) {
      this.cdr.detectChanges();
    } else {
      this.cdr.markForCheck();
    }
  }

  getActiveComputeMonitoring(): ComputeMonitoringSession | null {
    if (!this.isActiveCurrentLiveComputeMessageOpen() || !this.hasLiveComputeMonitoring()) {
      return null;
    }

    return this.computeMonitoring;
  }

  shouldShowActiveComputeMonitoringLoadingState(): boolean {
    return this.isActiveCurrentLiveComputeMessageOpen() && this.computeMonitoringLoading && !this.hasLiveComputeMonitoring();
  }

  shouldShowActiveComputeMonitoringError(): boolean {
    return this.isActiveCurrentLiveComputeMessageOpen() && !!this.computeMonitoringError;
  }

  getComputeMonitoringStatusLabel(session: ComputeMonitoringSession | null): string {
    if (!session) {
      return this.computeMonitoringLoading ? 'Connecting to compute' : 'Compute monitoring unavailable';
    }

    const foreground = session.foregroundCommand;
    if (foreground?.status === 'running') {
      return 'Running terminal command';
    }

    if (foreground?.status === 'completed') {
      return 'Terminal command completed';
    }

    if (foreground?.status === 'timed_out') {
      return 'Terminal command timed out';
    }

    if (foreground?.status === 'failed') {
      return 'Terminal command failed';
    }

    if (session.activeBackgroundProcesses.some(processInfo => processInfo.status === 'running')) {
      return 'Background process running';
    }

    if (session.status === 'paused') {
      return 'Compute paused';
    }

    return 'Compute ready';
  }

  getComputeMonitoringProgressLabel(session: ComputeMonitoringSession | null): string {
    if (!session) {
      return this.computeMonitoringLoading ? 'Loading' : 'Idle';
    }

    const foreground = session.foregroundCommand;
    if (foreground) {
      const totalCommands = Math.max(1, foreground.totalCommands || 1);
      return `${Math.min(foreground.completedCommands || 0, totalCommands)}/${totalCommands} commands`;
    }

    const runningBackgroundCount = session.activeBackgroundProcesses.filter(processInfo => processInfo.status === 'running').length;
    return runningBackgroundCount > 0
      ? `${runningBackgroundCount} background ${runningBackgroundCount === 1 ? 'process' : 'processes'}`
      : 'Idle';
  }

  getComputeMonitoringCommandLabel(session: ComputeMonitoringSession | null): string {
    if (!session) {
      return 'Polling this thread for compute status.';
    }

    const foregroundCommand = session.foregroundCommand?.command;
    if (foregroundCommand) {
      return this.truncateComputeMonitoringLine(foregroundCommand, 160);
    }

    const backgroundCommand = session.activeBackgroundProcesses.find(processInfo => processInfo.status === 'running')?.command;
    if (backgroundCommand) {
      return this.truncateComputeMonitoringLine(backgroundCommand, 160);
    }

    return session.currentWorkingDirectory || 'The compute session is ready for commands.';
  }

  getComputeMonitoringUpdatedLabel(session: ComputeMonitoringSession | null): string {
    const timestamp = session?.foregroundCommand?.updatedAt || session?.updatedAt;
    if (!timestamp) {
      return 'Awaiting compute status';
    }

    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) {
      return 'Awaiting compute status';
    }

    return `Updated ${parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;
  }

  getComputeMonitoringOutputTail(session: ComputeMonitoringSession | null): string {
    const output = String(session?.foregroundCommand?.outputTail || '').trim();
    if (output) {
      return output;
    }

    if (session?.foregroundCommand?.status === 'running') {
      return 'Waiting for terminal output...';
    }

    if (session?.activeBackgroundProcesses.some(processInfo => processInfo.status === 'running')) {
      return 'Background process is still running.';
    }

    return 'No terminal output has been captured for this compute session yet.';
  }

  getActiveMessageTerminalSections(): TerminalHistorySection[] {
    if (!this.activeMessage) {
      return [];
    }

    const sections: TerminalHistorySection[] = [];
    const liveSession = this.getActiveComputeMonitoring();
    if (liveSession) {
      sections.push({
        id: `live:${liveSession.threadId}`,
        eyebrow: 'Live stream',
        title: this.getComputeMonitoringStatusLabel(liveSession),
        subtitle: [
          this.getComputeMonitoringUpdatedLabel(liveSession),
          liveSession.currentWorkingDirectory || ''
        ].filter(Boolean).join(' · '),
        output: this.getComputeMonitoringOutputTail(liveSession),
        tone: 'default'
      });
    }

    const storedSections = this.buildStoredTerminalSections(this.activeMessage);
    if (storedSections.length > 0) {
      sections.push(...storedSections);
    }

    return sections;
  }

  getActiveMessageTerminalEmptyState(): string {
    if (this.computeMonitoringLoading && this.isActiveCurrentLiveComputeMessageOpen()) {
      return 'Checking live terminal output...';
    }

    if (this.computeMonitoringError && this.isActiveCurrentLiveComputeMessageOpen()) {
      return 'Live terminal monitoring is temporarily unavailable. Stored terminal output will appear here when the tool result is saved.';
    }

    return 'No terminal output is available for this tool.';
  }

  trackByTerminalHistorySection(_index: number, section: TerminalHistorySection): string {
    return section.id;
  }

  canOpenActiveToolResultDeviceCommand(): boolean {
    return !!this.getActiveToolResultDeviceCommandTarget();
  }

  openActiveToolResultDeviceCommand(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const target = this.getActiveToolResultDeviceCommandTarget();
    if (!target) {
      return;
    }

    this.closeToolPopover();

    setTimeout(() => {
      this.deviceCommandRequested.emit(target);
    }, 0);
  }

  onDeleteMessage(messageIndex: number, messageId?: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.deleteMessage.emit({ messageIndex, messageId });
  }

  canDeleteActiveCompactionMarker(): boolean {
    return this.displayMode === 'debug'
      && this.openPopoverIndex !== null
      && this.isCompactionMarker(this.activeMessage);
  }

  deleteActiveCompactionMarker(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.canDeleteActiveCompactionMarker() || this.openPopoverIndex === null) {
      return;
    }

    const messageIndex = this.openPopoverIndex;
    const messageId = this.activeMessage?.id;
    this.closeToolPopover();
    this.deleteMessage.emit({ messageIndex, messageId });
  }

  onStartEditMessage(messageIndex: number, message: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.shouldShowEditButton(message)) {
      return;
    }

    this.closeToolPopover();
    this.editingMessageKey = this.getMessageEditKey(message, messageIndex);
    this.editingMessageDraft = this.getMessageText(message);
    this.cdr.markForCheck();
  }

  onEditingMessageDraftInput(event: Event): void {
    this.editingMessageDraft = (event.target as HTMLTextAreaElement).value;
  }

  onCancelEditMessage(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.editingMessageKey = null;
    this.editingMessageDraft = '';
    this.cdr.markForCheck();
  }

  onSaveEditMessage(messageIndex: number, message: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isEditingMessage(message, messageIndex) || !this.canSaveEditedMessage(message)) {
      return;
    }

    this.updateMessage.emit({
      messageIndex,
      messageId: message.id,
      content: this.editingMessageDraft
    });
    this.editingMessageKey = null;
    this.editingMessageDraft = '';
    this.cdr.markForCheck();
  }

  isEditingMessage(message: any, messageIndex: number): boolean {
    return this.editingMessageKey === this.getMessageEditKey(message, messageIndex);
  }

  canSaveEditedMessage(message: any): boolean {
    return this.editingMessageDraft !== this.getMessageText(message);
  }

  shouldShowEditButton(message: any): boolean {
    return this.displayMode === 'debug' && !this.shouldUseToolLayout(message) && !this.isCompactionMarker(message) && !!this.getMessageText(message);
  }

  private getMessageEditKey(message: any, messageIndex: number): string {
    return message?.id || `index-${messageIndex}`;
  }

  isToolMessage(message: any): boolean {
    return message.role === 'tool' || !!(message.tool_calls && message.tool_calls.length > 0);
  }

  isToolSummaryMessage(message: any): boolean {
    return this.hasToolSummaryMetadata(message);
  }

  getActiveMessageCostBreakdown() {
    return getMessageCostBreakdown(this.activeMessage?.tokenUsage, this.availableModels);
  }

  formatCost(value: number, currency: string = 'USD'): string {
    return formatUsd(value, currency);
  }

  formatCredits(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: value >= 100 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  formatComputeDuration(seconds: number, milliseconds?: number): string {
    if (milliseconds !== undefined && milliseconds > 0 && milliseconds < 1000) {
      return `${milliseconds}ms`;
    }

    if (seconds < 60) {
      const precision = seconds >= 10 ? 1 : 2;
      return `${seconds.toFixed(precision)}s`;
    }

    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`;
  }

  canShowActiveMessageTraceAction(): boolean {
    return !!this.activeMessage?.creditUsage && !!this.currentThreadId && !!this.getActiveThreadAgencyId();
  }

  async openActiveMessageTraceDetail(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.activeMessageTraceLoading) {
      return;
    }

    const agencyId = this.getActiveThreadAgencyId();
    const threadId = String(this.currentThreadId || '').trim();
    const returnTo = this.router.url;

    if (!agencyId || !threadId || !this.activeMessage?.creditUsage) {
      this.activeMessageTraceError = 'This message is missing the trace metadata needed to open details.';
      this.cdr.markForCheck();
      return;
    }

    this.activeMessageTraceLoading = true;
    this.activeMessageTraceError = '';
    this.cdr.markForCheck();

    try {
      const usageEventId = await this.findMessageCreditUsageEventId(this.activeMessage, agencyId, threadId);

      if (!usageEventId) {
        this.activeMessageTraceError = 'Could not find the saved credit trace for this message.';
        return;
      }

      this.closeToolPopover();
      await this.router.navigate(['/myagency/thread-messages', usageEventId], {
        queryParams: {
          agencyId,
          returnTo
        }
      });
    } catch (error) {
      console.error('Failed to open message trace detail', error);
      this.activeMessageTraceError = 'Could not load the trace detail for this message.';
    } finally {
      this.activeMessageTraceLoading = false;
      this.cdr.markForCheck();
    }
  }

  getActiveMessageCreditDebug(): ActiveMessageCreditDebug | null {
    if (!this.activeMessage) {
      return null;
    }

    const creditUsage = this.activeMessage?.creditUsage;
    const tokenUsage = this.activeMessage?.tokenUsage;
    const modelKey = String(tokenUsage?.model || '');
    const role = this.activeMessage?.role || this.activeMessage?.originalRole;

    if (!creditUsage) {
      return {
        iteration: null,
        associationLabel: role === 'assistant' || role === 'model' ? 'No recorded message credits' : 'Message has no direct credits',
        associationHint: role === 'assistant' || role === 'model'
          ? 'This assistant message was created before per-message credit auditing was saved, or the backend has not written it yet.'
          : 'Only assistant/model messages carry direct per-iteration credit usage.' ,
        modelKey,
        modelDisplayName: modelKey ? formatModelDisplayName(modelKey) : 'Unknown model',
        inputTokens: Number(tokenUsage?.promptTokens || 0),
        outputTokens: Number(tokenUsage?.completionTokens || 0),
        totalTokens: Number(tokenUsage?.totalTokens || 0),
        computeMilliseconds: 0,
        computeSeconds: 0,
        modelCredits: 0,
        computeCredits: 0,
        totalCredits: 0,
        modelCostUsd: Number(tokenUsage?.totalCost || 0),
        computeCostUsd: 0,
        totalCostUsd: Number(tokenUsage?.totalCost || 0),
        hasCreditEvent: false
      };
    }

    return {
      iteration: Number(creditUsage.iteration || 0) || null,
      associationLabel: Number(creditUsage.iteration || 0) > 0 ? `Iteration ${creditUsage.iteration} message audit` : 'Message audit',
      associationHint: 'Saved directly on the assistant message so you can audit credits without loading separate credit event records.',
      modelKey,
      modelDisplayName: modelKey ? formatModelDisplayName(modelKey) : 'Unknown model',
      inputTokens: Number(tokenUsage?.promptTokens || 0),
      outputTokens: Number(tokenUsage?.completionTokens || 0),
      totalTokens: Number(tokenUsage?.totalTokens || 0),
      computeMilliseconds: Number(creditUsage.computeMilliseconds || 0),
      computeSeconds: Number(creditUsage.computeSeconds || 0),
      modelCredits: Number(creditUsage.modelCredits || 0),
      computeCredits: Number(creditUsage.computeCredits || 0),
      totalCredits: Number(creditUsage.totalCredits || 0),
      modelCostUsd: Number(creditUsage.modelCostUsd || tokenUsage?.totalCost || 0),
      computeCostUsd: Number(creditUsage.computeCostUsd || 0),
      totalCostUsd: Number(creditUsage.totalCostUsd || tokenUsage?.totalCost || 0),
      hasCreditEvent: true
    };
  }

  private async findMessageCreditUsageEventId(message: any, agencyId: string, threadId: string): Promise<string | null> {
    const creditUsage = message?.creditUsage;
    if (!creditUsage) {
      return null;
    }

    const directId = getOptionalString(creditUsage?.creditUsageEventId)
      || getOptionalString(creditUsage?.usageEventId)
      || getOptionalString(creditUsage?.eventId)
      || getOptionalString(creditUsage?.id);

    if (directId) {
      return directId;
    }

    const snapshot = await firstValueFrom(
      this.afs.collection<ICreditUsageEvent>(
        `users/${agencyId}/creditUsageEvents`,
        ref => ref.where('threadId', '==', threadId).limit(50)
      ).get()
    );

    const candidates = snapshot.docs
      .map(doc => ({ ...(doc.data() as ICreditUsageEvent), id: doc.id }))
      .map(usageEvent => ({
        usageEvent,
        score: this.scoreCreditUsageEventMatch(message, usageEvent),
        createdAtMs: this.getCreditUsageEventCreatedAtMs(usageEvent)
      }))
      .filter(candidate => candidate.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return b.createdAtMs - a.createdAtMs;
      });

    return candidates[0]?.usageEvent.id || null;
  }

  private scoreCreditUsageEventMatch(message: any, usageEvent: ICreditUsageEvent): number {
    const creditUsage = message?.creditUsage;
    const tokenUsage = message?.tokenUsage;
    const messageIteration = Number(creditUsage?.iteration || 0);
    const eventIteration = Number(usageEvent.iteration || 0);
    const messageModel = String(tokenUsage?.model || '').trim().toLowerCase();
    const eventModel = String(usageEvent.model || '').trim().toLowerCase();
    const messageCredits = Number(creditUsage?.totalCredits || 0);
    const eventCredits = Number(usageEvent.totalCredits || 0);
    const messageTokens = Number(tokenUsage?.totalTokens || 0);
    const eventTokens = Number(usageEvent.totalTokens || 0);
    let score = 0;

    if (messageIteration > 0) {
      score += eventIteration === messageIteration ? 8 : -10;
    }

    if (messageModel && eventModel && messageModel === eventModel) {
      score += 4;
    }

    if (messageCredits > 0 && Math.abs(eventCredits - messageCredits) < 0.001) {
      score += 3;
    }

    if (messageTokens > 0 && eventTokens === messageTokens) {
      score += 2;
    }

    if (usageEvent.resourceType === 'agentThread' || !usageEvent.resourceType) {
      score += 1;
    }

    return score;
  }

  private getCreditUsageEventCreatedAtMs(usageEvent: ICreditUsageEvent): number {
    const value = usageEvent.createdAt as any;

    if (!value) {
      return 0;
    }

    if (typeof value.toMillis === 'function') {
      return value.toMillis();
    }

    if (typeof value.toDate === 'function') {
      return value.toDate().getTime();
    }

    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getActiveThreadAgencyId(): string {
    return getOptionalString(this.currentThread?.agencyId)
      || getOptionalString(this.currentThread?.uid)
      || '';
  }

  private hasToolSummaryMetadata(message: any): boolean {
    if (!(message?.role === 'assistant' || message?.role === 'model')) {
      return false;
    }

    const hasToolCalls = Array.isArray(message?.tool_calls) && message.tool_calls.length > 0;
    const hasDisplayName = typeof message?.display_name === 'string' && message.display_name.trim().length > 0;

    return hasToolCalls || hasDisplayName;
  }

  private getLastRenderedMessageIndex(): number {
    for (let index = this.history.length - 1; index >= 0; index--) {
      if (this.shouldRenderMessage(this.history[index])) {
        return index;
      }
    }

    return -1;
  }

  private hasRenderableBody(message: any): boolean {
    if (!message) {
      return false;
    }

    if (this.isCompactionMarker(message)) {
      return true;
    }

    if (message.role === 'system') {
      return false;
    }

    if (this.shouldShowToolResult(message) || this.shouldShowToolCallSummary(message)) {
      return true;
    }

    if (this.getMessageGeneratedMedia(message).length > 0) {
      return true;
    }

    if (this.getMessageContextAttachments(message).length > 0) {
      return true;
    }

    if (this.getMessageImageAttachments(message).length > 0) {
      return true;
    }

    return !this.shouldUseToolLayout(message) && this.getMessageText(message).trim().length > 0;
  }

  getMessageContextAttachments(message: any): Array<{
    id: string;
    type: string;
    displayName: string;
    entityId?: string;
    filePath?: string;
    content?: string;
    url?: string;
    mimeType?: string;
    mediaType?: string;
    sizeBytes?: number;
    parentId?: string;
    parentType?: string;
    parentDisplayName?: string;
    icon?: string;
  }> {
    const rawAttachments = Array.isArray(message?.contextAttachments)
      ? message.contextAttachments
      : Array.isArray(message?.message?.contextAttachments)
        ? message.message.contextAttachments
        : [];

    return rawAttachments
      .filter((attachment: any) => attachment && typeof attachment === 'object')
      .map((attachment: any, index: number) => ({
        id: typeof attachment.id === 'string' && attachment.id.trim().length > 0 ? attachment.id : `context-${index}`,
        type: typeof attachment.type === 'string' && attachment.type.trim().length > 0 ? attachment.type : 'file',
        displayName: typeof attachment.displayName === 'string' && attachment.displayName.trim().length > 0 ? attachment.displayName : 'Untitled attachment',
        entityId: typeof attachment.entityId === 'string' ? attachment.entityId : undefined,
        filePath: typeof attachment.filePath === 'string' ? attachment.filePath : undefined,
        content: typeof attachment.content === 'string' ? attachment.content : undefined,
        url: typeof attachment.url === 'string' ? attachment.url : undefined,
        mimeType: typeof attachment.mimeType === 'string' ? attachment.mimeType : undefined,
        mediaType: typeof attachment.mediaType === 'string' ? attachment.mediaType : undefined,
        sizeBytes: typeof attachment.sizeBytes === 'number' ? attachment.sizeBytes : undefined,
        parentId: typeof attachment.parentId === 'string' ? attachment.parentId : undefined,
        parentType: typeof attachment.parentType === 'string' ? attachment.parentType : undefined,
        parentDisplayName: typeof attachment.parentDisplayName === 'string' ? attachment.parentDisplayName : undefined,
        icon: typeof attachment.icon === 'string' ? attachment.icon : undefined
      }));
  }

  getContextAttachmentTypeLabel(attachment: { type?: string }): string {
    switch (attachment.type) {
      case 'business':
        return 'Business';
      case 'partner':
        return 'Partner';
      case 'funnel':
        return 'Funnel';
      case 'page':
        return 'Page';
      case 'form':
        return 'Form';
      case 'plan':
        return 'Plan';
      case 'note':
        return 'Note';
      case 'proposal':
        return 'Proposal';
      case 'task':
        return 'Task';
      case 'ticket':
        return 'Ticket';
      case 'app':
        return 'App';
      case 'selection':
        return 'Selection';
      case 'media':
        return 'Media';
      case 'file':
      default:
        return 'File';
    }
  }

  getContextAttachmentIconClass(attachment: { type?: string; icon?: string }): string {
    const icon = String(attachment.icon || '').trim();
    if (icon) {
      if (icon.includes('fa-regular') || icon.includes('fa-solid') || icon.includes('fas ') || icon.includes('far ')) {
        return icon;
      }

      return `fa-regular ${icon}`;
    }

    switch (attachment.type) {
      case 'business':
        return 'fa-regular fa-building';
      case 'partner':
        return 'fa-regular fa-handshake';
      case 'funnel':
        return 'fa-regular fa-filter';
      case 'page':
        return 'fa-regular fa-file-lines';
      case 'form':
        return 'fa-regular fa-rectangle-list';
      case 'plan':
        return 'fa-regular fa-list-check';
      case 'note':
        return 'fa-regular fa-note-sticky';
      case 'proposal':
        return 'fa-regular fa-file-signature';
      case 'task':
        return 'fa-regular fa-square-check';
      case 'ticket':
        return 'fa-regular fa-headset';
      case 'app':
        return 'fa-regular fa-rocket';
      case 'selection':
        return 'fa-regular fa-text-slash';
      case 'media':
        return 'fa-regular fa-file';
      case 'file':
      default:
        return 'fa-regular fa-paperclip';
    }
  }

  getContextAttachmentTooltip(attachment: {
    displayName?: string;
    entityId?: string;
    parentDisplayName?: string;
    parentId?: string;
    filePath?: string;
    url?: string;
    mimeType?: string;
    mediaType?: string;
    type?: string;
  }): string {
    const lines: string[] = [attachment.displayName || 'Attachment', this.getContextAttachmentTypeLabel(attachment)];

    if (attachment.entityId) {
      lines.push(`ID: ${attachment.entityId}`);
    }

    if (attachment.parentDisplayName) {
      lines.push(`Parent: ${attachment.parentDisplayName}`);
    }

    if (attachment.parentId) {
      lines.push(`Parent ID: ${attachment.parentId}`);
    }

    if (attachment.filePath) {
      lines.push(`Path: ${attachment.filePath}`);
    }

    if (attachment.url) {
      lines.push(`URL: ${attachment.url}`);
    }

    if (attachment.mimeType) {
      lines.push(`MIME: ${attachment.mimeType}`);
    } else if (attachment.mediaType) {
      lines.push(`Media: ${attachment.mediaType}`);
    }

    return lines.join('\n');
  }

  getMessageImageAttachments(message: any): Array<{ id?: string; url: string; name?: string }> {
    const rawAttachments = Array.isArray(message?.attachments)
      ? message.attachments
      : Array.isArray(message?.message?.attachments)
        ? message.message.attachments
        : [];

    return rawAttachments
      .filter((attachment: any) => attachment && typeof attachment.url === 'string' && attachment.url.trim().length > 0)
      .map((attachment: any) => ({
        id: typeof attachment.id === 'string' ? attachment.id : undefined,
        url: attachment.url,
        name: typeof attachment.name === 'string' ? attachment.name : undefined
      }));
  }

  getMessageGeneratedMedia(message: any): Array<{
    id?: string;
    kind: 'image' | 'audio';
    url: string;
    name?: string;
    mimeType?: string;
    transcript?: string;
    width?: number;
    height?: number;
    provider?: string;
  }> {
    const rawGeneratedMedia = Array.isArray(message?.generatedMedia)
      ? message.generatedMedia
      : Array.isArray(message?.message?.generatedMedia)
        ? message.message.generatedMedia
        : [];

    return rawGeneratedMedia
      .filter((media: any) => media && (media.kind === 'image' || media.kind === 'audio') && typeof media.url === 'string' && media.url.trim().length > 0)
      .map((media: any) => ({
        id: typeof media.id === 'string' ? media.id : undefined,
        kind: media.kind,
        url: media.url,
        name: typeof media.name === 'string' ? media.name : undefined,
        mimeType: typeof media.mimeType === 'string' ? media.mimeType : undefined,
        transcript: typeof media.transcript === 'string' ? media.transcript : undefined,
        width: typeof media.width === 'number' ? media.width : undefined,
        height: typeof media.height === 'number' ? media.height : undefined,
        provider: typeof media.provider === 'string' ? media.provider : undefined
      }));
  }

  getMessageGeneratedImages(message: any): GeneratedImageMedia[] {
    const generatedImages = this.getMessageGeneratedMedia(message)
      .filter(media => media.kind === 'image')
      .map(media => ({
        id: media.id,
        url: media.url,
        name: media.name,
        width: typeof media.width === 'number' ? media.width : undefined,
        height: typeof media.height === 'number' ? media.height : undefined,
        sourceLabel: media.provider ? this.getFriendlyToolName(media.provider) : undefined
      }));

    const assets = new Map<string, GeneratedImageMedia>();

    [...generatedImages, ...this.getToolPayloadGeneratedImages(message)].forEach((media) => {
      const previewId = this.getGeneratedImagePreviewId(media);
      if (previewId && !assets.has(previewId)) {
        assets.set(previewId, media);
      }
    });

    return Array.from(assets.values());
  }

  getGeneratedImageCardDetail(media: GeneratedImageMedia): string | null {
    const details = [media.sourceLabel, media.photographerName].filter((value): value is string => !!value);
    return details.length > 0 ? details.join(' • ') : null;
  }

  getGeneratedImagePreviewSubtitle(media: GeneratedImageMedia): string {
    return media.sourceLabel ? `${media.sourceLabel} image` : 'Created in this thread';
  }

  getGeneratedImagePreviewDetail(media: GeneratedImageMedia): string | null {
    const details: string[] = [];

    if (media.photographerName) {
      const username = media.photographerUsername ? `@${media.photographerUsername}` : null;
      details.push(username ? `Photo by ${media.photographerName} (${username})` : `Photo by ${media.photographerName}`);
    }

    if (typeof media.width === 'number' && typeof media.height === 'number') {
      details.push(`${media.width} x ${media.height}`);
    }

    return details.length > 0 ? details.join(' • ') : null;
  }

  getGeneratedImageAttribution(media: GeneratedImageMedia): string | null {
    return media.attribution || null;
  }

  hasGeneratedImageSource(media: GeneratedImageMedia): boolean {
    return typeof media.sourceHref === 'string' && media.sourceHref.trim().length > 0;
  }

  openGeneratedImageSource(media: GeneratedImageMedia): void {
    this.openHrefInNewTab(media.sourceHref || null);
  }

  getGeneratedImagePreviewHint(media: GeneratedImageMedia): string {
    if (!this.usesDetachedPreviewSurface()) {
      return 'Click to preview';
    }

    return this.isDetachedPreviewActive('generated-image', this.getGeneratedImagePreviewId(media))
      ? 'Click to close side panel'
      : 'Open in side panel';
  }

  isGeneratedImageMenuOpen(media: GeneratedImageMedia): boolean {
    return this.activeGeneratedImagePopoverKey === this.getGeneratedImagePreviewId(media);
  }

  getGeneratedImagePopoverPanelActionLabel(media: GeneratedImageMedia): string {
    return this.isDetachedPreviewActive('generated-image', this.getGeneratedImagePreviewId(media))
      ? 'Close side panel'
      : 'Open side panel';
  }

  openGeneratedImageOptionsPopover(media: GeneratedImageMedia, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor || !this.generatedImageOptionsPopoverTemplate) {
      return;
    }

    this.closeGeneratedAudioOptionsPopover();
  this.closeArtifactSelectorPopover();
    this.activeGeneratedImagePopover = media;
    this.activeGeneratedImagePopoverKey = this.getGeneratedImagePreviewId(media);
    this.uiPopoverService.open({
      element: anchor,
      placement: 'bottom-right',
      align: 'end',
      offset: 8,
      bodyTemplate: this.generatedImageOptionsPopoverTemplate,
      backdrop: true,
      backdropDismiss: true,
      noPadding: true,
      showCloseButton: false,
      dimensions: {
        width: 320,
        maxWidth: 'calc(100vw - 16px)'
      },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetGeneratedImagePopoverState();
        this.cdr.markForCheck();
      }
    });

    this.cdr.markForCheck();
  }

  closeGeneratedImageOptionsPopover(): void {
    if (this.activeGeneratedImagePopoverKey === null) {
      this.resetGeneratedImagePopoverState();
      this.cdr.markForCheck();
      return;
    }

    this.uiPopoverService.close();
  }

  openGeneratedImagePreview(media: GeneratedImageMedia, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.generatedImagePreviewTemplate) {
      return;
    }

    if (this.usesDetachedPreviewSurface() && this.toggleDetachedPreviewIfActive('generated-image', this.getGeneratedImagePreviewId(media))) {
      return;
    }

    this.closeGeneratedImageOptionsPopover();
    this.resetPreviewModalState();
    this.resetArtifactPreviewState();
    this.resetGeneratedAudioPreviewState();
    this.activeGeneratedImage = media;

    if (this.usesDetachedPreviewSurface()) {
      this.emitDetachedPreview(this.buildGeneratedImagePreviewOutlet(media));
      this.cdr.markForCheck();
      return;
    }

    this.uiModalService.open({
      title: media.name || 'Generated image',
      subtitle: 'Preview',
      bodyTemplate: this.generatedImagePreviewTemplate,
      bodyClass: 'generated-image-preview-modal-body',
      size: 'lg',
      hideFooter: false,
      buttons: this.getGeneratedImageModalButtons(),
      onClose: () => {
        this.resetGeneratedImagePreviewState();
      }
    });

    this.cdr.markForCheck();
  }

  getGeneratedAudioPreviewId(media: GeneratedAudioMedia): string {
    return media.stateKey || media.url || media.id || '';
  }

  openGeneratedAudioPreview(media: GeneratedAudioMedia, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.generatedAudioPreviewTemplate) {
      return;
    }

    if (this.usesDetachedPreviewSurface() && this.toggleDetachedPreviewIfActive('generated-audio', this.getGeneratedAudioPreviewId(media))) {
      return;
    }

    this.closeGeneratedAudioOptionsPopover();
    this.resetPreviewModalState();
    this.resetArtifactPreviewState();
    this.resetGeneratedImagePreviewState();
    this.activeGeneratedAudio = media;

    if (this.usesDetachedPreviewSurface()) {
      this.emitDetachedPreview(this.buildGeneratedAudioPreviewOutlet(media));
      this.cdr.markForCheck();
      return;
    }

    this.uiModalService.open({
      title: media.name || 'Generated audio',
      subtitle: 'Preview',
      bodyTemplate: this.generatedAudioPreviewTemplate,
      bodyClass: 'generated-audio-preview-modal-body',
      size: 'lg',
      hideFooter: false,
      buttons: this.getGeneratedAudioModalButtons(),
      onClose: () => {
        this.resetGeneratedAudioPreviewState();
      }
    });

    this.cdr.markForCheck();
  }

  getMessageGeneratedAudio(message: any): GeneratedAudioMedia[] {
    const messageKey = this.getGeneratedAudioMessageKey(message);

    return this.getMessageGeneratedMedia(message)
      .filter(media => media.kind === 'audio')
      .map((media, mediaIndex) => ({
        id: media.id,
        url: media.url,
        name: media.name,
        mimeType: media.mimeType,
        transcript: media.transcript,
        stateKey: `${messageKey}:audio:${media.id || media.url}:${mediaIndex}`
      }));
  }

  isGeneratedAudioPlaying(media: GeneratedAudioMedia): boolean {
    return this.getGeneratedAudioState(media).isPlaying;
  }

  hasGeneratedAudioDuration(media: GeneratedAudioMedia): boolean {
    const duration = this.getGeneratedAudioState(media).duration;
    return typeof duration === 'number' && Number.isFinite(duration) && duration > 0;
  }

  getGeneratedAudioCurrentTime(media: GeneratedAudioMedia): number {
    return this.getGeneratedAudioState(media).currentTime;
  }

  getGeneratedAudioDuration(media: GeneratedAudioMedia): number {
    const duration = this.getGeneratedAudioState(media).duration;
    return typeof duration === 'number' && Number.isFinite(duration) && duration > 0 ? duration : 0;
  }

  getGeneratedAudioDurationLabel(media: GeneratedAudioMedia): string {
    const state = this.getGeneratedAudioState(media);

    if (state.loadFailed) {
      return 'Unavailable';
    }

    return this.hasGeneratedAudioDuration(media)
      ? this.formatGeneratedAudioTime(state.duration)
      : 'Loading...';
  }

  getGeneratedAudioProgressPercent(media: GeneratedAudioMedia): number {
    const duration = this.getGeneratedAudioDuration(media);
    const currentTime = this.getGeneratedAudioCurrentTime(media);

    if (!duration) {
      return 0;
    }

    return Math.max(0, Math.min(100, (currentTime / duration) * 100));
  }

  getGeneratedAudioPlaybackRateLabel(media: GeneratedAudioMedia): string {
    return `${this.getGeneratedAudioState(media).playbackRate}x`;
  }

  isGeneratedAudioPlaybackRateSelected(media: GeneratedAudioMedia, playbackRate: number): boolean {
    return this.getGeneratedAudioState(media).playbackRate === playbackRate;
  }

  getGeneratedAudioPopoverPanelActionLabel(media: GeneratedAudioMedia): string {
    return this.isDetachedPreviewActive('generated-audio', this.getGeneratedAudioPreviewId(media))
      ? 'Close side panel'
      : 'Open side panel';
  }

  isGeneratedAudioMenuOpen(media: GeneratedAudioMedia): boolean {
    return this.activeGeneratedAudioPopoverKey === this.getGeneratedAudioStateKey(media);
  }

  openGeneratedAudioOptionsPopover(media: GeneratedAudioMedia, audio: HTMLAudioElement, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor || !this.generatedAudioOptionsPopoverTemplate) {
      return;
    }

    this.closeGeneratedImageOptionsPopover();
  this.closeArtifactSelectorPopover();
    this.activeGeneratedAudioPopover = media;
    this.activeGeneratedAudioPopoverElement = audio;
    this.activeGeneratedAudioPopoverKey = this.getGeneratedAudioStateKey(media);
    this.uiPopoverService.open({
      element: anchor,
      placement: 'bottom-right',
      align: 'end',
      offset: 8,
      bodyTemplate: this.generatedAudioOptionsPopoverTemplate,
      backdrop: true,
      backdropDismiss: true,
      noPadding: true,
      showCloseButton: false,
      dimensions: {
        width: 320,
        maxWidth: 'calc(100vw - 16px)'
      },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetGeneratedAudioPopoverState();
        this.cdr.markForCheck();
      }
    });

    this.cdr.markForCheck();
  }

  closeGeneratedAudioOptionsPopover(): void {
    if (this.activeGeneratedAudioPopoverKey === null) {
      this.resetGeneratedAudioPopoverState();
      this.cdr.markForCheck();
      return;
    }

    this.uiPopoverService.close();
  }

  formatGeneratedAudioTime(value: number | null | undefined): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
      return '--:--';
    }

    const totalSeconds = Math.floor(value);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  async toggleGeneratedAudioPlayback(media: GeneratedAudioMedia, audio: HTMLAudioElement): Promise<void> {
    const state = this.getGeneratedAudioState(media);

    if (audio.paused) {
      if (this.activeGeneratedAudioElement && this.activeGeneratedAudioElement !== audio) {
        this.activeGeneratedAudioElement.pause();
      }

      if (this.hasGeneratedAudioDuration(media) && Math.abs(audio.currentTime - this.getGeneratedAudioDuration(media)) < 0.05) {
        audio.currentTime = 0;
        state.currentTime = 0;
      }

      audio.playbackRate = state.playbackRate;

      try {
        await audio.play();
      } catch {
        state.isPlaying = false;
        this.cdr.markForCheck();
      }

      return;
    }

    audio.pause();
  }

  onGeneratedAudioMetadataLoaded(media: GeneratedAudioMedia, audio: HTMLAudioElement): void {
    const state = this.getGeneratedAudioState(media);
    const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null;

    state.duration = duration;
    state.currentTime = Number.isFinite(audio.currentTime) ? audio.currentTime : state.currentTime;
    state.loadFailed = false;

    if (Number.isFinite(audio.playbackRate) && audio.playbackRate > 0) {
      state.playbackRate = audio.playbackRate;
    } else {
      audio.playbackRate = state.playbackRate;
    }

    this.cdr.markForCheck();
  }

  onGeneratedAudioTimeUpdate(media: GeneratedAudioMedia, audio: HTMLAudioElement): void {
    const state = this.getGeneratedAudioState(media);
    state.currentTime = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;

    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      state.duration = audio.duration;
    }

    this.cdr.markForCheck();
  }

  onGeneratedAudioPlaybackStateChange(media: GeneratedAudioMedia, audio: HTMLAudioElement, isPlaying: boolean): void {
    const state = this.getGeneratedAudioState(media);
    state.isPlaying = isPlaying;
    state.currentTime = Number.isFinite(audio.currentTime) ? audio.currentTime : state.currentTime;

    if (isPlaying) {
      if (this.activeGeneratedAudioElement && this.activeGeneratedAudioElement !== audio) {
        this.activeGeneratedAudioElement.pause();
      }

      this.activeGeneratedAudioElement = audio;
    } else if (this.activeGeneratedAudioElement === audio) {
      this.activeGeneratedAudioElement = null;
    }

    this.cdr.markForCheck();
  }

  onGeneratedAudioEnded(media: GeneratedAudioMedia, audio: HTMLAudioElement): void {
    const state = this.getGeneratedAudioState(media);
    state.isPlaying = false;
    state.currentTime = Number.isFinite(audio.duration) ? audio.duration : state.currentTime;

    if (this.activeGeneratedAudioElement === audio) {
      this.activeGeneratedAudioElement = null;
    }

    this.cdr.markForCheck();
  }

  onGeneratedAudioRateChange(media: GeneratedAudioMedia, audio: HTMLAudioElement): void {
    const state = this.getGeneratedAudioState(media);

    if (Number.isFinite(audio.playbackRate) && audio.playbackRate > 0) {
      state.playbackRate = audio.playbackRate;
      this.cdr.markForCheck();
    }
  }

  onGeneratedAudioLoadError(media: GeneratedAudioMedia): void {
    const state = this.getGeneratedAudioState(media);
    state.isPlaying = false;
    state.loadFailed = true;
    this.cdr.markForCheck();
  }

  onGeneratedAudioSeek(media: GeneratedAudioMedia, audio: HTMLAudioElement, value: string): void {
    const seconds = Number(value);

    if (!Number.isFinite(seconds)) {
      return;
    }

    audio.currentTime = seconds;
    this.getGeneratedAudioState(media).currentTime = seconds;
    this.cdr.markForCheck();
  }

  cycleGeneratedAudioPlaybackRate(media: GeneratedAudioMedia, audio: HTMLAudioElement): void {
    const state = this.getGeneratedAudioState(media);
    const currentIndex = this.generatedAudioPlaybackRates.findIndex(rate => rate === state.playbackRate);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % this.generatedAudioPlaybackRates.length : 0;
    const nextRate = this.generatedAudioPlaybackRates[nextIndex];

    state.playbackRate = nextRate;
    audio.playbackRate = nextRate;
    this.cdr.markForCheck();
  }

  setGeneratedAudioPlaybackRate(media: GeneratedAudioMedia, audio: HTMLAudioElement, playbackRate: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.generatedAudioPlaybackRates.includes(playbackRate)) {
      return;
    }

    const state = this.getGeneratedAudioState(media);
    state.playbackRate = playbackRate;
    audio.playbackRate = playbackRate;
    this.cdr.markForCheck();
  }

  setActiveGeneratedAudioPlaybackRate(playbackRate: number, event?: Event): void {
    const media = this.activeGeneratedAudioPopover;
    const audio = this.activeGeneratedAudioPopoverElement;

    if (!media || !audio) {
      return;
    }

    this.setGeneratedAudioPlaybackRate(media, audio, playbackRate, event);
  }

  downloadActiveGeneratedImage(event?: Event): void {
    const media = this.activeGeneratedImagePopover;
    if (!media) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    this.downloadGeneratedImage(media);
    this.closeGeneratedImageOptionsPopover();
  }

  openActiveGeneratedImageInNewTab(event?: Event): void {
    const media = this.activeGeneratedImagePopover;
    if (!media) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    this.openGeneratedImageInNewTab(media);
    this.closeGeneratedImageOptionsPopover();
  }

  openActiveGeneratedImageSource(event?: Event): void {
    const media = this.activeGeneratedImagePopover;
    if (!media) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    this.openGeneratedImageSource(media);
    this.closeGeneratedImageOptionsPopover();
  }

  openActiveGeneratedImageInSidePanel(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const media = this.activeGeneratedImagePopover;
    if (!media || !this.usesDetachedPreviewSurface()) {
      return;
    }

    this.openGeneratedImagePreview(media);
  }

  downloadActiveGeneratedAudio(event?: Event): void {
    const media = this.activeGeneratedAudioPopover;
    if (!media) {
      return;
    }

    this.downloadGeneratedAudio(media, event);
    this.closeGeneratedAudioOptionsPopover();
  }

  openActiveGeneratedAudioInSidePanel(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const media = this.activeGeneratedAudioPopover;
    if (!media || !this.usesDetachedPreviewSurface()) {
      return;
    }

    this.openGeneratedAudioPreview(media);
  }

  hasMessageDetails(message: any): boolean {
    if (this.isCompactionMarker(message)) {
      return true;
    }

    // Show ellipsis for messages that have additional details available
    return message.role === 'system' ||
           (message.role === 'user' && (this.getMessageContextAttachments(message).length > 0 || this.getMessageImageAttachments(message).length > 0)) ||
           message.role === 'tool' ||
           message.role === 'model' ||
           message.role === 'assistant' ||
           !!(message.tool_calls && message.tool_calls.length > 0);
  }

  // Extract text content from responses API format
  getMessageText(message: any): string {
    if (message.message?.content) {
      return this.getMessageText({ ...message, content: message.message.content });
    }

    if (!message.content) return '';
    
    // For tool messages, don't show content - show display_name instead via the template
    if (message.role === 'tool') {
      return '';
    }
    
    if (typeof message.content === 'string') {
      return message.content;
    } else if (Array.isArray(message.content)) {
      // Extract text from content blocks (responses API format)
      return message.content
        .map((block: any) => {
          if (typeof block === 'string') {
            return block;
          } else if (block.type === 'text' && block.text) {
            return block.text;
          } else if (block.text) {
            return block.text;
          }
          return '';
        })
        .filter((text: string) => text)
        .join('\n');
    } else if (typeof message.content === 'object') {
      if (typeof message.content.text === 'string') {
        return message.content.text;
      }

      if (typeof message.content.output_text === 'string') {
        return message.content.output_text;
      }
    }
    
    return '';
  }

  getToolDetails(message: any): string {
    if (!message) {
      return 'No message data available';
    }

    let details = '';

    if (this.isCompactionMarker(message)) {
      const content = this.getMessageText(message) || 'No compaction summary stored';
      const compactedMessageCount = Number(message?.ui?.compactedMessageCount || 0);
      const compactedThroughMessageId = typeof message?.ui?.compactedThroughMessageId === 'string' ? message.ui.compactedThroughMessageId : 'Unknown';
      const summaryModel = typeof message?.ui?.summaryModel === 'string' ? message.ui.summaryModel : 'Unknown';
      const preservedRecentUserTurns = Number(message?.ui?.preservedRecentUserTurns || 0);

      details = `Context Compaction:
Compacted Messages: ${compactedMessageCount || 'Unknown'}
Compacted Through Message ID: ${compactedThroughMessageId}
Preserved Recent User Turns: ${preservedRecentUserTurns || 'Unknown'}
Summary Model: ${summaryModel}

Compaction Summary:
${content}`;
    } else if (message.role === 'system') {
      // System message - show full content
      const content = this.getMessageText(message) || 'No content';
      details = `System Prompt:
${content}`;
    } else if (message.role === 'user') {
      const content = this.getMessageText(message) || '';
      const contextAttachments = this.getMessageContextAttachments(message);
      const imageAttachments = this.getMessageImageAttachments(message);

      details = `User Message Details:
Content Length: ${content.length} characters`;

      if (contextAttachments.length > 0) {
        details += `

Attached Context:
${contextAttachments.map((attachment, index) => `${index + 1}. ${this.getContextAttachmentTypeLabel(attachment)}
Name: ${attachment.displayName}${attachment.entityId ? `
ID: ${attachment.entityId}` : ''}${attachment.parentDisplayName ? `
Parent: ${attachment.parentDisplayName}` : ''}${attachment.parentId ? `
Parent ID: ${attachment.parentId}` : ''}${attachment.filePath ? `
Path: ${attachment.filePath}` : ''}${attachment.url ? `
URL: ${attachment.url}` : ''}${attachment.mimeType ? `
MIME Type: ${attachment.mimeType}` : ''}${attachment.mediaType ? `
Media Type: ${attachment.mediaType}` : ''}${attachment.content ? `
Content: ${attachment.content}` : ''}`).join('\n\n')}`;
      }

      if (imageAttachments.length > 0) {
        details += `

Attached Images:
${imageAttachments.map((attachment, index) => `${index + 1}. ${attachment.name || 'Untitled image'}
URL: ${attachment.url}`).join('\n\n')}`;
      }
    } else if (message.role === 'tool') {
      // Tool result
      const content = this.getToolResultDetailsContent(message);
      details = `Tool Result:
Tool Call ID: ${message.tool_call_id || 'Unknown'}
Name: ${message.name || 'Unknown'}
Content: ${content}`;
    } else if (message.role === 'assistant' || message.role === 'model') {
      // AI message - could have tool calls AND message metadata
      
      // First, show tool calls if they exist
      if (message.tool_calls && message.tool_calls.length > 0) {
        details += 'Tool Calls:\n';
        details += message.tool_calls.map((toolCall: any, index: number) => {
          const args = this.tryParseJson(toolCall.function.arguments);
          return `Tool Call ${index + 1}:
Function: ${toolCall.function.name}
ID: ${toolCall.id}
Arguments: ${JSON.stringify(args, null, 2)}`;
        }).join('\n\n');
      }
      
      // Then show AI message details (always for AI messages)
      if (details) details += '\n\n'; // Add separator if we had tool calls
      
      const messageText = this.getMessageText(message);
      details += `AI Message Details:
Content Length: ${messageText.length} characters`;
      
      if (message.tokenUsage) {
        details += `

Token Usage:
{
  "promptTokens": ${message.tokenUsage.promptTokens},
  "completionTokens": ${message.tokenUsage.completionTokens},
  "totalTokens": ${message.tokenUsage.totalTokens},
  "model": "${message.tokenUsage.model}"
}`;
      } else {
        details += `

Token Usage: Not available`;
      }

  const generatedMedia = this.getMessageGeneratedMedia(message);
  if (generatedMedia.length > 0) {
    details += `

Generated Media:
${generatedMedia.map((media, index) => `${index + 1}. ${media.kind.toUpperCase()}\n+URL: ${media.url}\n+Name: ${media.name || 'Untitled'}${media.mimeType ? `\n+MIME Type: ${media.mimeType}` : ''}${media.transcript ? `\n+Transcript: ${media.transcript}` : ''}`).join('\n\n')}`;
  }
    }

    return details || 'No details available';
  }

  private getToolResultDetailsContent(message: any): string {
    const rawContent = this.getRawMessageContent(message);

    if (!rawContent) {
      return 'No content';
    }

    const parsedContent = this.tryParseJson(rawContent);
    if (typeof parsedContent === 'string') {
      return parsedContent;
    }

    return JSON.stringify(parsedContent, null, 2);
  }

  private getRawMessageContent(message: any): string {
    if (!message) {
      return '';
    }

    if (typeof message.content === 'string') {
      return message.content;
    }

    if (Array.isArray(message.content)) {
      return message.content
        .map((block: any) => {
          if (typeof block === 'string') {
            return block;
          }

          if (typeof block?.text === 'string') {
            return block.text;
          }

          return '';
        })
        .filter((value: string) => !!value)
        .join('\n');
    }

    if (message.content && typeof message.content === 'object') {
      return JSON.stringify(message.content);
    }

    return '';
  }

  private tryParseJson(jsonString: string): any {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return jsonString; // Return as string if not valid JSON
    }
  }

  private getFriendlyToolName(toolName?: string): string {
    if (!toolName) {
      return 'Tool';
    }

    return toolName
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, value => value.toUpperCase())
      .trim();
  }

  private normalizeToolLabel(label: string): string {
    return label.replace(/"/g, '').replace(/\s+/g, ' ').trim();
  }

  private getDerivedToolResultLabel(message: any): string | null {
    const toolName = this.getFriendlyToolName(message?.name);
    if (!message?.name) {
      return null;
    }

    const rawContent = this.getRawMessageContent(message);
    const parsed = rawContent ? this.tryParseJson(rawContent) : null;
    const artifactCount = this.getArtifactResources(message).length;

    if (parsed && typeof parsed === 'object') {
      if (parsed.success === false || parsed.error === true || parsed.status === 'error' || parsed.status === 'failed') {
        return `${toolName} Failed`;
      }

      if (parsed.success === true || parsed.status === 'success' || parsed.status === 'ok') {
        return artifactCount > 0
          ? `${toolName} Succeeded • ${artifactCount} artifact${artifactCount === 1 ? '' : 's'}`
          : `${toolName} Succeeded`;
      }
    }

    if (artifactCount > 0) {
      return `${toolName} Completed • ${artifactCount} artifact${artifactCount === 1 ? '' : 's'}`;
    }

    return message.display_name ? this.normalizeToolLabel(message.display_name) : `${toolName} Succeeded`;
  }

  private getToolPayload(message: any): any {
    const rawContent = this.getRawMessageContent(message);
    if (!rawContent) {
      return null;
    }

    const parsed = this.tryParseJson(rawContent);
    return parsed && typeof parsed === 'object' ? parsed : null;
  }

  private getActiveToolResultDeviceCommandTarget(): ChatDeviceCommandTarget | null {
    return this.getToolResultDeviceCommandTarget(this.activeMessage);
  }

  private getToolResultDeviceCommandTarget(message: any): ChatDeviceCommandTarget | null {
    if (!message || message.role !== 'tool') {
      return null;
    }

    const payload = this.getToolPayload(message);
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const deviceId = typeof payload.deviceId === 'string' ? payload.deviceId.trim() : '';
    if (!deviceId) {
      return null;
    }

    const commands = Array.isArray(payload.deviceCommands) ? payload.deviceCommands : [];
    const matchingCommand = commands.find((entry: any) => entry && typeof entry.commandId === 'string' && entry.commandId.trim());
    const commandId = typeof matchingCommand?.commandId === 'string' ? matchingCommand.commandId.trim() : '';

    if (!commandId) {
      return null;
    }

    return { deviceId, commandId };
  }

  private getGeneratedAudioState(media: GeneratedAudioMedia): GeneratedAudioState {
    const key = this.getGeneratedAudioStateKey(media);
    let state = this.generatedAudioStateByKey.get(key);

    if (!state) {
      state = {
        currentTime: 0,
        duration: null,
        isPlaying: false,
        playbackRate: 1,
        loadFailed: false
      };
      this.generatedAudioStateByKey.set(key, state);
    }

    return state;
  }

  private getGeneratedAudioStateKey(media: GeneratedAudioMedia): string {
    return media.stateKey || media.id || media.url;
  }

  private getGeneratedAudioMessageKey(message: any): string {
    const messageId = getOptionalString(message?.id) || getOptionalString(message?.message?.id);

    if (messageId) {
      return messageId;
    }

    const historyIndex = Array.isArray(this.history) ? this.history.indexOf(message) : -1;
    return `${message?.role || 'message'}:${historyIndex >= 0 ? historyIndex : 'unknown'}`;
  }

  private normalizePreviewUrl(rawUrl: string, options: { rewriteForLocalBackend?: boolean } = { rewriteForLocalBackend: true }): string | null {
    if (!rawUrl) {
      return null;
    }

    try {
      const parsed = new URL(rawUrl);
      const hostname = parsed.hostname.toLowerCase();
      if (!['https:', 'http:'].includes(parsed.protocol)) {
        return null;
      }

      if (!this.allowedPreviewHosts.has(hostname)) {
        return null;
      }

      if ((hostname === 'localhost' || hostname === '127.0.0.1') && parsed.port && parsed.port !== '4200') {
        return null;
      }

      const isAllowedPreviewPath = parsed.pathname.startsWith('/myagency/')
        || parsed.pathname.startsWith('/project/')
        || parsed.pathname.startsWith('/ticket/')
        || parsed.pathname.startsWith('/funnel/');

      if (!isAllowedPreviewPath) {
        return null;
      }

      if (options.rewriteForLocalBackend !== false && this.shouldUseLocalPreviewHost() && (hostname === 'account.platfio.com' || hostname === 'test.account.platfio.com')) {
        return this.toLocalPreviewUrl(parsed);
      }

      return parsed.toString();
    } catch (_error) {
      return null;
    }
  }

  private shouldUseLocalPreviewHost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const pageHost = window.location.hostname.toLowerCase();
    if (pageHost !== 'localhost' && pageHost !== '127.0.0.1') {
      return false;
    }

    const backendUrl = String(this.backendUrl || '').toLowerCase();
    return backendUrl.includes('localhost:8080')
      || backendUrl.includes('127.0.0.1:8080')
      || backendUrl.includes('localhost:8081')
      || backendUrl.includes('127.0.0.1:8081');
  }

  private toLocalPreviewUrl(parsed: URL): string {
    const localUrl = new URL(`${parsed.pathname}${parsed.search}${parsed.hash}`, window.location.origin);
    return localUrl.toString();
  }

  private getSafePreviewUrl(url: string): SafeResourceUrl {
    const cached = this.previewUrlCache.get(url);
    if (cached) {
      return cached;
    }

    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.previewUrlCache.set(url, safeUrl);
    return safeUrl;
  }

  private getArtifactPreviewType(artifact: ToolArtifactResource): 'markdown' | 'csv' | 'html' | 'svg' | 'text' | 'image' | 'audio' | 'video' | 'zip' | 'file' | null {
    const mimeType = (artifact.mimeType || '').toLowerCase().trim();
    const candidates = [artifact.relativePath, artifact.title, artifact.persistedPath, artifact.uri]
      .map(value => String(value || '').trim().toLowerCase())
      .filter(Boolean)
      .map(value => value.split('?')[0]?.split('#')[0] || value);
    const hasExtension = (...extensions: string[]): boolean => candidates.some(candidate => extensions.some(extension => candidate.endsWith(extension)));

    if (mimeType.startsWith('text/markdown') || hasExtension('.md', '.markdown')) {
      return 'markdown';
    }

    if (mimeType.startsWith('text/csv') || hasExtension('.csv')) {
      return 'csv';
    }

    if (mimeType.startsWith('text/html') || hasExtension('.html', '.htm')) {
      return 'html';
    }

    if (mimeType.startsWith('image/svg+xml') || hasExtension('.svg')) {
      return 'svg';
    }

    if (
      mimeType.startsWith('image/png')
      || mimeType.startsWith('image/jpeg')
      || mimeType.startsWith('image/gif')
      || mimeType.startsWith('image/webp')
      || hasExtension('.png', '.jpg', '.jpeg', '.gif', '.webp')
    ) {
      return 'image';
    }

    if (mimeType.startsWith('audio/mpeg') || hasExtension('.mp3')) {
      return 'audio';
    }

    if (mimeType.startsWith('video/mp4') || hasExtension('.mp4')) {
      return 'video';
    }

    if (mimeType.startsWith('application/zip') || hasExtension('.zip')) {
      return 'zip';
    }

    if (
      mimeType.startsWith('text/')
      || mimeType.includes('json')
      || mimeType.includes('javascript')
      || mimeType.includes('typescript')
      || mimeType.includes('xml')
      || hasExtension(
        '.txt', '.text', '.log',
        '.css', '.scss', '.sass', '.less',
        '.js', '.mjs', '.cjs', '.jsx',
        '.ts', '.tsx',
        '.json', '.jsonl', '.xml', '.yml', '.yaml',
        '.sh', '.bash', '.zsh',
        '.py', '.rb', '.go', '.rs', '.java', '.kt',
        '.c', '.h', '.cpp', '.hpp', '.cs',
        '.php', '.sql', '.toml', '.ini', '.env'
      )
    ) {
      return 'text';
    }

    if (
      artifact.artifactId
      || this.getArtifactStorageReferenceTarget(artifact)
      || this.getArtifactHostedHref(artifact)
      || artifact.relativePath
    ) {
      return 'file';
    }

    return null;
  }

  private buildArtifactPreviewState(
    artifact: ToolArtifactResource,
    content: string,
    metadata: { truncated: boolean; sizeBytes: number },
    options?: { preferRemoteUrl?: string | null }
  ): ArtifactPreviewState {
    const previewType = this.getArtifactPreviewType(artifact);
    if (!previewType) {
      throw new Error('Preview is not available for this artifact type.');
    }

    if (previewType === 'html' || previewType === 'svg') {
      const remoteUrl = String(options?.preferRemoteUrl || this.getArtifactPreviewSourceUrl(artifact) || '').trim();
      return {
        type: previewType,
        content,
        truncated: metadata.truncated,
        sizeBytes: metadata.sizeBytes,
        ...(remoteUrl ? { fileUrl: remoteUrl } : {})
      };
    }

    if (previewType === 'image' || previewType === 'audio' || previewType === 'video' || previewType === 'zip' || previewType === 'file') {
      const fileUrl = String(options?.preferRemoteUrl || this.getArtifactPreviewSourceUrl(artifact) || '').trim();
      return {
        type: previewType,
        content,
        truncated: metadata.truncated,
        sizeBytes: metadata.sizeBytes,
        ...(fileUrl ? { fileUrl } : {})
      };
    }

    return {
      type: previewType,
      content,
      truncated: metadata.truncated,
      sizeBytes: metadata.sizeBytes,
      ...(previewType === 'csv' ? { rows: this.parseCsvRows(content) } : {})
    };
  }

  private queueArtifactHtmlPreviewRender(): void {
    // HTML preview is now handled by the dedicated HtmlPreviewer component.
  }

  private resetArtifactPreviewState(): void {
    this.activeArtifact = null;
    this.artifactPreview = null;
    this.artifactPreviewError = null;
    this.artifactPreviewLoading = false;
    this.artifactPreviewMode = 'preview';
    this.artifactRawDraft = '';
    this.artifactRawOriginal = '';
    this.artifactRawContentLoaded = false;
    this.artifactRawLoading = false;
    this.artifactRawSaving = false;
    this.artifactRawSaveError = null;
    this.artifactRawLastSavedAt = null;
    this.artifactDeleteError = null;
    this.artifactApiBaseUrl = null;
    this.zipPreviewViewerState = {
      canTogglePreviewMode: false,
      canSaveArchive: false,
      archiveSaving: false,
      previewMode: null,
      explorerVisibility: 'expanded'
    };
    this.cdr.markForCheck();
  }

  getGeneratedImagePreviewId(media: GeneratedImageMedia): string {
    return media.id || media.url || '';
  }

  private getGeneratedImageAssetSubtitle(media: GeneratedImageMedia): string {
    return media.sourceLabel ? `${media.sourceLabel} image` : 'Generated image';
  }

  private getGeneratedImageAssetDetail(media: GeneratedImageMedia): string | undefined {
    if (media.photographerName) {
      return media.sourceLabel ? `${media.sourceLabel} • ${media.photographerName}` : media.photographerName;
    }

    return this.getThreadAssetHrefDetail(media.url);
  }

  private getToolPayloadGeneratedImages(message: any): GeneratedImageMedia[] {
    if (message?.role !== 'tool' || message?.name !== 'searchUnsplashImages') {
      return [];
    }

    const payload = this.getToolPayload(message);
    const results = Array.isArray(payload?.results) ? payload.results : [];

    return results.flatMap((result: any, index: number) => {
      const imageUrl = [
        getOptionalString(result?.urls?.regular),
        getOptionalString(result?.urls?.small),
        getOptionalString(result?.urls?.full),
        getOptionalString(result?.urls?.raw)
      ].find((value): value is string => !!value);

      if (!imageUrl) {
        return [];
      }

      const id = getOptionalString(result?.id)
        || getOptionalString(result?.slug)
        || `${message?.id || message?.tool_call_id || 'unsplash'}:${index}`;
      const description = getOptionalString(result?.description)
        || getOptionalString(result?.altDescription)
        || getOptionalString(result?.alt_description);
      const photographerName = getOptionalString(result?.user?.name);
      const photographerUsername = getOptionalString(result?.user?.username);
      const attribution = getOptionalString(result?.attribution)
        || (photographerName ? `Photo by ${photographerName} on Unsplash` : undefined);

      return [{
        id,
        url: imageUrl,
        name: description || (photographerName ? `${photographerName} on Unsplash` : 'Unsplash image'),
        altText: getOptionalString(result?.altDescription) || description,
        width: getOptionalNumber(result?.width),
        height: getOptionalNumber(result?.height),
        sourceLabel: 'Unsplash',
        sourceHref: getOptionalString(result?.links?.html),
        attribution,
        photographerName,
        photographerUsername
      }];
    });
  }

  private refreshDetachedPreviewOutlet(): void {
    if (!this.usesDetachedPreviewSurface()) {
      return;
    }

    if (this.activeDetachedPreviewKind === 'tool-preview' && this.activePreviewResource && this.previewExpandedTemplate) {
      this.emitDetachedPreview(this.buildToolPreviewOutlet(this.activePreviewResource));
      return;
    }

    if (this.activeDetachedPreviewKind === 'artifact' && this.activeArtifact && this.artifactPreviewTemplate) {
      this.emitDetachedPreview(this.buildArtifactPreviewOutlet(this.activeArtifact));
      return;
    }

    if (this.activeDetachedPreviewKind === 'generated-image' && this.activeGeneratedImage && this.generatedImagePreviewTemplate) {
      this.emitDetachedPreview(this.buildGeneratedImagePreviewOutlet(this.activeGeneratedImage));
      return;
    }

    if (this.activeDetachedPreviewKind === 'generated-audio' && this.activeGeneratedAudio && this.generatedAudioPreviewTemplate) {
      this.emitDetachedPreview(this.buildGeneratedAudioPreviewOutlet(this.activeGeneratedAudio));
    }
  }

  private emitDetachedPreview(previewOutlet: ChatPreviewOutlet): void {
    this.activeDetachedPreviewId = previewOutlet.id;
    this.activeDetachedPreviewKind = previewOutlet.kind;
    this.previewOutletChange.emit(previewOutlet);
  }

  private toggleDetachedPreviewIfActive(kind: ChatPreviewKind, id: string): boolean {
    if (!this.isDetachedPreviewActive(kind, id)) {
      return false;
    }

    this.closeDetachedPreview();
    return true;
  }

  private clearDetachedPreviewState(): void {
    this.activeDetachedPreviewId = null;
    this.activeDetachedPreviewKind = null;
    this.previewOutletChange.emit(null);
  }

  private closeDetachedPreview(): void {
    this.resetPreviewModalState();
    this.resetArtifactPreviewState();
    this.resetGeneratedImagePreviewState();
    this.resetGeneratedAudioPreviewState();
    this.clearDetachedPreviewState();
  }

  private getArtifactModalButtons(): UiModalButton[] {
    const buttons: UiModalButton[] = [];

    if (this.artifactPreviewMode === 'raw') {
      buttons.push({
        label: 'Save',
        color: 'primary',
        style: 'filled',
        icon: 'fa-regular fa-floppy-disk',
        disabled: () => !this.canSaveArtifactRaw(),
        loading: () => this.artifactRawSaving,
        onClick: () => {
          void this.saveArtifactRaw();
        }
      });
    }

    return buttons;
  }

  private getGeneratedImageModalButtons(): UiModalButton[] {
    if (!this.activeGeneratedImage?.url) {
      return [];
    }

    return [
      {
        label: 'Download',
        color: 'secondary',
        style: 'outline',
        icon: 'fa-regular fa-arrow-down-to-line',
        onClick: () => {
          this.downloadGeneratedImage(this.activeGeneratedImage!);
        }
      },
      {
        label: 'Open In New Tab',
        color: 'primary',
        style: 'filled',
        icon: 'fa-regular fa-up-right-from-square',
        onClick: () => {
          this.openGeneratedImageInNewTab(this.activeGeneratedImage!);
        }
      }
    ];
  }

  private getGeneratedAudioModalButtons(): UiModalButton[] {
    if (!this.activeGeneratedAudio?.url) {
      return [];
    }

    return [
      {
        label: 'Download',
        color: 'secondary',
        style: 'outline',
        icon: 'fa-regular fa-arrow-down-to-line',
        onClick: () => {
          this.downloadGeneratedAudio(this.activeGeneratedAudio!);
        }
      },
      {
        label: 'Open In New Tab',
        color: 'primary',
        style: 'filled',
        icon: 'fa-regular fa-up-right-from-square',
        onClick: () => {
          this.openGeneratedAudioInNewTab(this.activeGeneratedAudio!);
        }
      }
    ];
  }

  downloadGeneratedImage(media: GeneratedImageMedia): void {
    if (typeof document === 'undefined') {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = media.url;
    if (media.name) {
      anchor.download = media.name;
    }
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  }

  openGeneratedImageInNewTab(media: GeneratedImageMedia): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.open(media.url, '_blank', 'noopener,noreferrer');
  }

  downloadGeneratedAudio(media: GeneratedAudioMedia, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (typeof document === 'undefined') {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = media.url;
    if (media.name) {
      anchor.download = media.name;
    }
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  }

  openGeneratedAudioInNewTab(media: GeneratedAudioMedia, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (typeof window === 'undefined') {
      return;
    }

    window.open(media.url, '_blank', 'noopener,noreferrer');
  }

  private refreshArtifactModalButtons(): void {
    this.uiModalService.updateButtons(this.getArtifactModalButtons());
  }

  private getArtifactApiBaseCandidates(preferredBaseUrl?: string | null): string[] {
    const preferred = String(preferredBaseUrl || this.artifactApiBaseUrl || this.backendUrl || '').trim().replace(/\/$/, '');
    const candidates = new Set<string>();

    if (preferred) {
      candidates.add(preferred);
    }

    if (this.localAgentBackendUrl !== preferred) {
      candidates.add(this.localAgentBackendUrl);
    }

    if (this.productionAgentBackendUrl !== preferred) {
      candidates.add(this.productionAgentBackendUrl);
    }

    if (this.stagingAgentBackendUrl !== preferred) {
      candidates.add(this.stagingAgentBackendUrl);
    }

    return Array.from(candidates);
  }

  private isThreadActivelyRunning(): boolean {
    const threadStatus = typeof this.currentThread?.status === 'string' ? this.currentThread.status.trim().toLowerCase() : '';
    return threadStatus === 'running' || threadStatus === 'continuing';
  }

  private hasLiveComputeMonitoring(session: ComputeMonitoringSession | null = this.computeMonitoring): boolean {
    if (!session) {
      return false;
    }

    if (session.foregroundCommand?.status === 'running') {
      return true;
    }

    return session.activeBackgroundProcesses.some(processInfo => processInfo.status === 'running');
  }

  private isComputeToolName(toolName: string | undefined): boolean {
    const normalized = String(toolName || '').trim().toLowerCase();
    return normalized === 'initialize_computer'
      || normalized === 'run_terminal_command'
      || normalized === 'read_artifact'
      || normalized === 'save_and_pause_computer'
      || normalized === 'initialize computer'
      || normalized === 'run terminal command'
      || normalized === 'read artifact'
      || normalized === 'save and pause computer';
  }

  private isComputeToolMessage(message: any): boolean {
    if (!message) {
      return false;
    }

    if ((message.role === 'tool' || message.originalRole === 'tool') && this.isComputeToolName(message.name)) {
      return true;
    }

    if (
      (message.role === 'assistant'
        || message.role === 'model'
        || message.originalRole === 'assistant'
        || message.originalRole === 'model')
      && Array.isArray(message.tool_calls)
    ) {
      return message.tool_calls.some((toolCall: any) => this.isComputeToolName(toolCall?.function?.name));
    }

    return false;
  }

  private hasComputeMessageInHistory(): boolean {
    return Array.isArray(this.history) && this.history.some(message => this.isComputeToolMessage(message));
  }

  private getLatestComputeMessageIndex(): number {
    for (let index = this.history.length - 1; index >= 0; index -= 1) {
      if (this.shouldRenderMessage(this.history[index]) && this.isComputeToolMessage(this.history[index])) {
        return index;
      }
    }

    return -1;
  }

  private isLatestComputeMessageIndex(messageIndex: number): boolean {
    return messageIndex >= 0 && messageIndex === this.getLatestComputeMessageIndex();
  }

  private isCurrentLiveComputeMessage(message: any, messageIndex: number): boolean {
    return this.isComputeToolMessage(message) && this.isLatestComputeMessageIndex(messageIndex);
  }

  private isActiveCurrentLiveComputeMessageOpen(): boolean {
    return this.openPopoverIndex !== null
      && this.isLatestComputeMessageIndex(this.openPopoverIndex)
      && !!this.activeMessage
      && this.isComputeToolMessage(this.activeMessage);
  }

  private shouldPollComputeMonitoring(): boolean {
    if (typeof window === 'undefined' || !this.currentThreadId) {
      return false;
    }

    return this.activeMessageDetailsTab === 'terminal' && this.isActiveCurrentLiveComputeMessageOpen();
  }

  private syncComputeMonitoring(): void {
    if (!this.currentThreadId) {
      this.stopComputeMonitoringPolling(true);
      return;
    }

    if (!this.shouldPollComputeMonitoring()) {
      this.stopComputeMonitoringPolling(true);
      return;
    }

    if (!this.computeMonitoringPollTimer) {
      void this.refreshComputeMonitoring();
      this.computeMonitoringPollTimer = setInterval(() => {
        void this.refreshComputeMonitoring();
      }, this.computeMonitoringPollIntervalMs);
      return;
    }

    void this.refreshComputeMonitoring();
  }

  private stopComputeMonitoringPolling(clearState: boolean): void {
    if (this.computeMonitoringPollTimer) {
      clearInterval(this.computeMonitoringPollTimer);
      this.computeMonitoringPollTimer = null;
    }

    this.computeMonitoringLoading = false;
    this.computeMonitoringRequestInFlight = false;

    if (clearState) {
      this.computeMonitoring = null;
      this.computeMonitoringError = null;
    }
  }

  private async refreshComputeMonitoring(): Promise<void> {
    const threadId = this.currentThreadId;
    if (!threadId || this.computeMonitoringRequestInFlight) {
      return;
    }

    this.computeMonitoringRequestInFlight = true;
    this.computeMonitoringLoading = true;
    this.cdr.markForCheck();

    try {
      const { response, baseUrl } = await this.readComputeMonitoringFromBackend(threadId);

      if (this.currentThreadId !== threadId) {
        return;
      }

      if (!this.artifactApiBaseUrl) {
        this.artifactApiBaseUrl = baseUrl;
      }

      this.computeMonitoring = response.available ? response.session : null;
      this.computeMonitoringError = null;

      if (!this.hasLiveComputeMonitoring(this.computeMonitoring)) {
        this.stopComputeMonitoringPolling(true);
      }
    } catch (error) {
      if (this.currentThreadId !== threadId) {
        return;
      }

      console.error('[compute-monitor] Failed to poll compute status', {
        threadId,
        candidateBaseUrls: this.getArtifactApiBaseCandidates(),
        error
      });
      this.computeMonitoringError = this.getArtifactPreviewErrorMessage(error);
    } finally {
      if (this.currentThreadId === threadId) {
        this.computeMonitoringLoading = false;
        this.computeMonitoringRequestInFlight = false;
        this.cdr.markForCheck();
      }
    }
  }

  private async readComputeMonitoringFromBackend(threadId: string): Promise<{ response: ComputeMonitoringResponse; baseUrl: string }> {
    const candidateRequests = this.getArtifactApiBaseCandidates().map(async (baseUrl) => ({
      response: await firstValueFrom(this.http.get<ComputeMonitoringResponse>(
        `${baseUrl}/agent/thread/${encodeURIComponent(threadId)}/compute`,
        {
          params: {
            platformEnvironment: this.backendEnvironment
          }
        }
      ).pipe(timeout(this.artifactPreviewRequestTimeoutMs))),
      baseUrl
    }));

    try {
      return await Promise.any(candidateRequests);
    } catch (error: any) {
      const firstError = Array.isArray(error?.errors) && error.errors.length > 0 ? error.errors[0] : error;
      throw firstError instanceof Error ? firstError : new Error('Failed to load compute monitoring state.');
    }
  }

  private truncateComputeMonitoringLine(value: string, maxLength: number): string {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (normalized.length <= maxLength) {
      return normalized;
    }

    return `${normalized.slice(0, maxLength - 1)}…`;
  }

  private getInitialActiveMessageDetailsTab(message: any): MessageDetailsTabId {
    if (this.hasStoredTerminalHistory(message) || (this.isComputeToolMessage(message) && this.isLatestComputeMessageIndex(this.history.indexOf(message)))) {
      return 'terminal';
    }

    return 'details';
  }

  private refreshActiveMessageDetailTabs(): void {
    const tabs = this.buildActiveMessageDetailTabs(this.activeMessage);
    this.activeMessageDetailTabs = tabs;

    if (!tabs.some(tab => tab.id === this.activeMessageDetailsTab)) {
      this.activeMessageDetailsTab = tabs[0]?.id || 'details';
    }
  }

  private buildActiveMessageDetailTabs(message: any): MessageDetailsTab[] {
    if (!message) {
      return [];
    }

    const tabs: MessageDetailsTab[] = [
      { id: 'details', label: 'Details' },
      { id: 'usage', label: 'Usage' }
    ];

    if (this.hasStoredTerminalHistory(message) || this.isActiveCurrentLiveComputeMessageOpen()) {
      tabs.push({ id: 'terminal', label: 'Terminal' });
    }

    return tabs;
  }

  private hasStoredTerminalHistory(message: any): boolean {
    return this.buildStoredTerminalSections(message).length > 0;
  }

  private buildStoredTerminalSections(message: any): TerminalHistorySection[] {
    return this.getRelatedToolResultMessages(message)
      .flatMap((toolMessage, toolMessageIndex) => {
        const payload = this.getToolPayload(toolMessage);
        const results = Array.isArray(payload?.results) ? payload.results : [];

        return results.flatMap((result: any, resultIndex: number) => {
          const command = typeof result?.command === 'string' ? result.command.trim() : '';
          if (!command) {
            return [];
          }

          const stdout = typeof result?.stdout === 'string' ? result.stdout.trim() : '';
          const stderr = typeof result?.stderr === 'string' ? result.stderr.trim() : '';
          const output = this.formatStoredTerminalOutput(result, stdout, stderr);
          if (!output) {
            return [];
          }

          const toolLabel = this.getFriendlyToolName(toolMessage?.name);
          const exitCode = typeof result?.exitCode === 'number' ? result.exitCode : null;
          const statusLabel = typeof exitCode === 'number'
            ? `Exit ${exitCode}`
            : result?.background
              ? 'Background'
              : 'Completed';
          const subtitle = [
            statusLabel,
            typeof result?.cwd === 'string' && result.cwd.trim().length > 0 ? result.cwd.trim() : '',
            typeof result?.finishedAt === 'string' && result.finishedAt.trim().length > 0
              ? this.formatTerminalTimestamp(result.finishedAt)
              : ''
          ].filter(Boolean).join(' · ');

          return [{
            id: `${toolMessage?.tool_call_id || toolMessageIndex}:${resultIndex}`,
            eyebrow: toolLabel,
            title: this.truncateComputeMonitoringLine(command, 160),
            ...(subtitle ? { subtitle } : {}),
            output,
            tone: 'default' as const
          }];
        });
      });
  }

  private getRelatedToolResultMessages(message: any): any[] {
    if (!message) {
      return [];
    }

    if (message.role === 'tool') {
      return [message];
    }

    const toolCallIds = new Set(
      Array.isArray(message.tool_calls)
        ? message.tool_calls
            .map((toolCall: any) => typeof toolCall?.id === 'string' ? toolCall.id.trim() : '')
            .filter((value: string) => value.length > 0)
        : []
    );

    if (toolCallIds.size === 0) {
      return [];
    }

    return this.history.filter(candidate => (
      candidate?.role === 'tool'
      && typeof candidate?.tool_call_id === 'string'
      && toolCallIds.has(candidate.tool_call_id.trim())
    ));
  }

  private formatStoredTerminalOutput(result: any, stdout: string, stderr: string): string {
    const chunks: string[] = [];

    if (stdout) {
      chunks.push(`[stdout]\n${stdout}`);
    }

    if (stderr) {
      chunks.push(`[stderr]\n${stderr}`);
    }

    if (chunks.length > 0) {
      return chunks.join('\n\n');
    }

    if (result?.background) {
      return 'This command was started in the background. No terminal output was captured in the saved tool result.';
    }

    return '';
  }

  private formatTerminalTimestamp(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  }

  private buildArtifactFileRoute(baseUrl: string, threadId: string, relativePath: string, cacheBust?: string | number | null): string {
    const encodedPath = relativePath
      .split('/')
      .filter(segment => segment.length > 0)
      .map(segment => encodeURIComponent(segment))
      .join('/');
    const queryParams = new URLSearchParams({ platformEnvironment: this.backendEnvironment });
    if (cacheBust !== undefined && cacheBust !== null && String(cacheBust).length > 0) {
      queryParams.set('v', String(cacheBust));
    }
    const query = `?${queryParams.toString()}`;
    return `${baseUrl}/agent/thread/${encodeURIComponent(threadId)}/artifact-files/${encodedPath}${query}`;
  }

  private getArtifactPreviewSourceUrl(artifact: ToolArtifactResource, preferredBaseUrl?: string | null, cacheBust?: string | number | null): string | null {
    const hostedHref = this.getArtifactHostedHref(artifact);
    if (hostedHref) {
      return this.appendCacheBust(hostedHref, cacheBust);
    }

    const resolvedBaseUrl = String(preferredBaseUrl || this.artifactApiBaseUrl || '').trim().replace(/\/$/, '');
    if (artifact.relativePath && this.currentThreadId && resolvedBaseUrl) {
      return this.buildArtifactFileRoute(resolvedBaseUrl, this.currentThreadId, artifact.relativePath, cacheBust);
    }

    if (artifact.uri && /^https?:\/\//i.test(artifact.uri)) {
      return artifact.uri;
    }

    return null;
  }

  private async ensureArtifactApiBaseResolved(artifact: ToolArtifactResource): Promise<string | null> {
    if (this.artifactApiBaseUrl) {
      return this.artifactApiBaseUrl;
    }

    if (!artifact.artifactId || !this.currentThreadId) {
      return null;
    }

    try {
      const { baseUrl } = await this.readArtifactFromBackend(artifact, 1);
      this.artifactApiBaseUrl = baseUrl;
      return baseUrl;
    } catch (error) {
      console.error('[artifact-preview] Failed to resolve backend base URL', {
        threadId: this.currentThreadId,
        artifactId: artifact.artifactId,
        relativePath: artifact.relativePath,
        candidateBaseUrls: this.getArtifactApiBaseCandidates()
      }, error);
      return null;
    }
  }

  private async resolveArtifactPreviewBaseUrl(artifact: ToolArtifactResource): Promise<void> {
    const baseUrl = await this.ensureArtifactApiBaseResolved(artifact);
    if (!baseUrl || !this.activeArtifact || this.activeArtifact.id !== artifact.id) {
      return;
    }

    try {
      const previewType = this.getArtifactPreviewType(this.activeArtifact);
      if (!previewType) {
        return;
      }

      if (!this.artifactPreview && (previewType === 'image' || previewType === 'audio' || previewType === 'video' || previewType === 'zip' || previewType === 'file')) {
        this.artifactPreview = this.buildArtifactPreviewState(
          this.activeArtifact,
          '',
          {
            truncated: false,
            sizeBytes: typeof this.activeArtifact.sizeBytes === 'number' ? this.activeArtifact.sizeBytes : 0
          },
          { preferRemoteUrl: this.getArtifactPreviewSourceUrl(this.activeArtifact, baseUrl, Date.now()) }
        );
        this.artifactPreviewLoading = false;
        this.cdr.markForCheck();
        return;
      }

      if (!this.artifactPreview) {
        return;
      }

      if (previewType === 'html' || previewType === 'svg' || previewType === 'image' || previewType === 'audio' || previewType === 'video' || previewType === 'zip' || previewType === 'file') {
        this.artifactPreview = this.buildArtifactPreviewState(
          this.activeArtifact,
          this.artifactPreviewMode === 'preview' ? this.artifactPreview.content : this.artifactRawDraft,
          {
            truncated: this.artifactPreview.truncated,
            sizeBytes: this.artifactPreview.sizeBytes
          },
          { preferRemoteUrl: this.getArtifactPreviewSourceUrl(this.activeArtifact, baseUrl, Date.now()) }
        );

        this.cdr.markForCheck();
      }
    } catch {
      // Best effort only. Keep the preview already shown.
    }
  }

  private async ensureArtifactRawContentLoaded(): Promise<void> {
    if (this.artifactRawContentLoaded || this.artifactRawLoading || !this.activeArtifact) {
      return;
    }

    if (!this.activeArtifact.artifactId || !this.currentThreadId || this.getArtifactApiBaseCandidates().length === 0) {
      this.artifactRawSaveError = 'Full file contents are not available for editing.';
      this.cdr.markForCheck();
      return;
    }

    this.artifactRawLoading = true;
    this.artifactRawSaveError = null;
    this.refreshArtifactModalButtons();
    this.refreshDetachedPreviewOutlet();
    this.cdr.markForCheck();

    try {
      const { response, baseUrl } = await this.loadArtifactText(this.activeArtifact);
      this.artifactApiBaseUrl = baseUrl;
      const fullText = typeof response?.content?.text === 'string' ? response.content.text : '';
      this.artifactRawOriginal = fullText;
      this.artifactRawDraft = fullText;
      this.artifactRawContentLoaded = true;

      if (this.activeArtifact) {
        this.activeArtifact = {
          ...this.activeArtifact,
          ...(typeof response?.artifact?.downloadUrl === 'string' ? { uri: response.artifact.downloadUrl } : {}),
          ...(typeof response?.artifact?.relativePath === 'string' ? { relativePath: response.artifact.relativePath } : {}),
          ...(typeof response?.artifact?.mimeType === 'string' ? { mimeType: response.artifact.mimeType } : {}),
          ...(typeof response?.artifact?.kind === 'string' ? { kind: response.artifact.kind } : {}),
          ...(typeof response?.artifact?.archiveFormat === 'string' ? { archiveFormat: response.artifact.archiveFormat } : {}),
          ...(typeof response?.artifact?.storageUri === 'string' ? { storageUri: response.artifact.storageUri } : {}),
          sizeBytes: typeof response?.content?.sizeBytes === 'number' ? response.content.sizeBytes : this.getTextByteSize(fullText)
        };
      }
    } catch (error) {
      this.artifactRawSaveError = this.getArtifactPreviewErrorMessage(error);
    } finally {
      this.artifactRawLoading = false;
      this.refreshArtifactModalButtons();
      this.refreshDetachedPreviewOutlet();
      this.cdr.markForCheck();
    }
  }

  private async readArtifactFromBackend(artifact: ToolArtifactResource, maxBytes?: number): Promise<{ response: ArtifactPreviewResponse; baseUrl: string }> {
    if (!artifact.artifactId || !this.currentThreadId) {
      throw new Error('Artifact backend preview is not available.');
    }

    const artifactId = artifact.artifactId;
    const threadId = this.currentThreadId;
    const candidates = this.getArtifactApiBaseCandidates();
    const requests = candidates.map(async baseUrl => {
      const response = await firstValueFrom(this.http.get<ArtifactPreviewResponse>(
        `${baseUrl}/agent/thread/${encodeURIComponent(threadId)}/artifacts/${encodeURIComponent(artifactId)}`,
        {
          params: {
            platformEnvironment: this.backendEnvironment,
            ...(typeof maxBytes === 'number' ? { maxBytes } : {}),
            ...(artifact.relativePath ? { relativePath: artifact.relativePath } : {})
          }
        }
      ).pipe(timeout(this.artifactPreviewRequestTimeoutMs)));

      return { response, baseUrl };
    });

    try {
      return await Promise.any(requests);
    } catch (error) {
      const aggregate = error as AggregateError;
      const firstError = Array.isArray(aggregate?.errors) && aggregate.errors.length > 0
        ? aggregate.errors[0]
        : error;
      throw firstError instanceof Error ? firstError : new Error('Failed to load artifact preview.');
    }
  }

  private async loadArtifactText(artifact: ToolArtifactResource, maxBytes?: number): Promise<{ response: ArtifactPreviewResponse; baseUrl: string | null }> {
    if (artifact.artifactId && this.currentThreadId && this.getArtifactApiBaseCandidates().length > 0) {
      try {
        const { response, baseUrl } = await this.readArtifactFromBackend(artifact, maxBytes);
        return { response, baseUrl };
      } catch {
        // Fall back to storage below.
      }
    }

    if (this.getArtifactStorageReferenceTarget(artifact) || this.getArtifactHostedHref(artifact)) {
      const response = await this.readArtifactFromStorage(artifact, maxBytes);
      return { response, baseUrl: null };
    }

    const { response, baseUrl } = await this.readArtifactFromBackend(artifact, maxBytes);
    return { response, baseUrl };
  }

  private async readArtifactFromStorage(artifact: ToolArtifactResource, maxBytes?: number): Promise<ArtifactPreviewResponse> {
    const downloadUrl = await this.resolveArtifactDownloadUrl(artifact);
    const response = await fetch(this.appendCacheBust(downloadUrl, Date.now()), {
      credentials: 'omit',
      cache: 'no-store'
    });
    if (!response.ok) {
      throw new Error('Failed to load artifact from storage.');
    }

    const text = await response.text();
    const sizeBytes = this.getTextByteSize(text);
    const limit = typeof maxBytes === 'number' && Number.isFinite(maxBytes) ? Math.max(1, maxBytes) : null;
    const truncated = limit !== null && sizeBytes > limit;
    const visibleText = truncated ? this.truncateTextToBytes(text, limit) : text;

    return {
      artifact: {
        ...(artifact.artifactId ? { id: artifact.artifactId } : {}),
        ...(artifact.title ? { name: artifact.title } : {}),
        ...(artifact.relativePath ? { relativePath: artifact.relativePath } : {}),
        ...(artifact.mimeType ? { mimeType: artifact.mimeType } : {}),
        ...(artifact.kind ? { kind: artifact.kind } : {}),
        ...(artifact.archiveFormat ? { archiveFormat: artifact.archiveFormat } : {}),
        ...(artifact.uri ? { downloadUrl: artifact.uri } : {}),
        ...(artifact.storageUri ? { storageUri: artifact.storageUri } : {})
      },
      content: {
        text: visibleText,
        truncated,
        sizeBytes,
        encoding: 'utf8',
        source: 'storage'
      }
    };
  }

  private async updateArtifactViaStorage(artifact: ToolArtifactResource, text: string): Promise<{ response: ArtifactUpdateResponse; baseUrl: null }> {
    const storageTarget = this.getArtifactStorageReferenceTarget(artifact);
    if (!storageTarget) {
      throw new Error('Artifact is not backed by Firebase Storage, so it cannot be saved from the UI yet.');
    }

    const storageRef = this.afStorage.storage.refFromURL(storageTarget);
    const contentType = artifact.mimeType || this.getArtifactTextContentType(artifact);
    await storageRef.putString(text, 'raw', contentType ? { contentType } : undefined);
    const downloadUrl = await storageRef.getDownloadURL();

    return {
      baseUrl: null,
      response: {
        artifact: {
          ...(artifact.artifactId ? { id: artifact.artifactId } : {}),
          ...(artifact.title ? { name: artifact.title } : {}),
          ...(artifact.relativePath ? { relativePath: artifact.relativePath } : {}),
          ...(contentType ? { mimeType: contentType } : {}),
          ...(artifact.kind ? { kind: artifact.kind } : {}),
          ...(artifact.archiveFormat ? { archiveFormat: artifact.archiveFormat } : {}),
          ...(artifact.storageUri ? { storageUri: artifact.storageUri } : {}),
          downloadUrl
        },
        content: {
          text,
          truncated: false,
          sizeBytes: this.getTextByteSize(text),
          encoding: 'utf8',
          source: 'storage'
        },
        savedAt: new Date().toISOString()
      }
    };
  }

  private getArtifactStorageReferenceTarget(artifact: ToolArtifactResource): string | null {
    const storageUri = String(artifact.storageUri || '').trim();
    if (/^gs:\/\//i.test(storageUri)) {
      return storageUri;
    }

    const uri = String(artifact.uri || '').trim();
    if (/^https:\/\/firebasestorage\.googleapis\.com\//i.test(uri)) {
      return uri;
    }

    return null;
  }

  private async resolveArtifactDownloadUrl(artifact: ToolArtifactResource): Promise<string> {
    const directUrl = String(artifact.uri || '').trim();
    if (/^https?:\/\//i.test(directUrl)) {
      return directUrl;
    }

    const storageTarget = this.getArtifactStorageReferenceTarget(artifact);
    if (!storageTarget) {
      throw new Error('Artifact download URL is not available.');
    }

    return this.afStorage.storage.refFromURL(storageTarget).getDownloadURL();
  }

  private appendCacheBust(url: string, cacheBust?: string | number | null): string {
    if (cacheBust === undefined || cacheBust === null || String(cacheBust).length === 0) {
      return url;
    }

    try {
      const nextUrl = new URL(url);
      nextUrl.searchParams.set('v', String(cacheBust));
      return nextUrl.toString();
    } catch {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}v=${encodeURIComponent(String(cacheBust))}`;
    }
  }

  private getArtifactTextContentType(artifact: ToolArtifactResource): string {
    const previewType = this.getArtifactPreviewType(artifact);
    if (previewType === 'markdown') {
      return 'text/markdown';
    }

    if (previewType === 'csv') {
      return 'text/csv';
    }

    if (previewType === 'html') {
      return 'text/html';
    }

    if (previewType === 'svg') {
      return 'image/svg+xml';
    }

    return 'text/plain';
  }

  private truncateTextToBytes(value: string, maxBytes: number): string {
    if (maxBytes <= 0) {
      return '';
    }

    if (typeof TextEncoder === 'undefined' || typeof TextDecoder === 'undefined') {
      return value.slice(0, maxBytes);
    }

    const encoded = new TextEncoder().encode(value);
    if (encoded.length <= maxBytes) {
      return value;
    }

    return new TextDecoder('utf-8').decode(encoded.slice(0, maxBytes));
  }

  private async updateArtifactViaBackend(artifact: ToolArtifactResource, text: string, preferredBaseUrl?: string | null): Promise<{ response: ArtifactUpdateResponse; baseUrl: string }> {
    if (!artifact.artifactId || !this.currentThreadId) {
      throw new Error('Artifact backend save is not available.');
    }

    const artifactId = artifact.artifactId;
    const threadId = this.currentThreadId;
    const normalizedPreferredBaseUrl = String(preferredBaseUrl || '').trim().replace(/\/$/, '');
    const candidates = normalizedPreferredBaseUrl
      ? [normalizedPreferredBaseUrl]
      : this.getArtifactApiBaseCandidates();
    const requests = candidates.map(async baseUrl => {
      const response = await firstValueFrom(this.http.put<ArtifactUpdateResponse>(
        `${baseUrl}/agent/thread/${encodeURIComponent(threadId)}/artifacts/${encodeURIComponent(artifactId)}`,
        {
          platformEnvironment: this.backendEnvironment,
          text,
          ...(artifact.relativePath ? { relativePath: artifact.relativePath } : {})
        }
      ).pipe(timeout(this.artifactSaveRequestTimeoutMs)));

      return { response, baseUrl };
    });

    try {
      return await Promise.any(requests);
    } catch (error) {
      const aggregate = error as AggregateError;
      const firstError = Array.isArray(aggregate?.errors) && aggregate.errors.length > 0
        ? aggregate.errors[0]
        : error;
      throw firstError instanceof Error ? firstError : new Error('Failed to save artifact.');
    }
  }

  private getTextByteSize(value: string): number {
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(value).length;
    }

    return value.length;
  }

  private parseCsvRows(csv: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = '';
    let inQuotes = false;

    for (let index = 0; index < csv.length; index += 1) {
      const char = csv[index];
      const nextChar = csv[index + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentCell += '"';
          index += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === ',' && !inQuotes) {
        currentRow.push(currentCell);
        currentCell = '';
        continue;
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          index += 1;
        }

        currentRow.push(currentCell);
        if (currentRow.some(value => value.length > 0)) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentCell = '';
        continue;
      }

      currentCell += char;
    }

    currentRow.push(currentCell);
    if (currentRow.some(value => value.length > 0)) {
      rows.push(currentRow);
    }

    return rows;
  }

  private getArtifactPreviewErrorMessage(error: unknown): string {
    const httpError = error as HttpErrorResponse | null;
    const rawError = (httpError?.error ?? null) as { message?: unknown } | null;
    const backendMessage = typeof rawError?.message === 'string' ? rawError.message : '';
    if (backendMessage) {
      return backendMessage;
    }

    const httpMessage = typeof httpError?.message === 'string' ? httpError.message.trim() : '';
    if (httpMessage) {
      return httpMessage;
    }

    return 'Failed to load artifact preview.';
  }

  formatMessageTime(timestamp: any): string {
    if (!timestamp) return '';
    
    // Handle Firebase Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    // Format with milliseconds (e.g., "2:34:56.789 PM")
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${displayHours}:${minutes}:${seconds}.${milliseconds} ${ampm}`;
  }

  getTimeDifference(currentIndex: number): string | null {
    if (currentIndex === 0) return null; // No previous message
    
    const currentMessage = this.history[currentIndex];
    const previousMessage = this.history[currentIndex - 1];
    
    if (!currentMessage.createdAt || !previousMessage.createdAt) return null;
    
    // Handle Firebase Timestamps
    const currentDate = currentMessage.createdAt.toDate ? 
      currentMessage.createdAt.toDate() : new Date(currentMessage.createdAt);
    const previousDate = previousMessage.createdAt.toDate ? 
      previousMessage.createdAt.toDate() : new Date(previousMessage.createdAt);
    
    const diffMs = currentDate.getTime() - previousDate.getTime();
    
    if (diffMs < 1000) {
      return `+${diffMs}ms`;
    } else if (diffMs < 60000) {
      const seconds = (diffMs / 1000).toFixed(1);
      return `+${seconds}s`;
    } else {
      const minutes = Math.floor(diffMs / 60000);
      const seconds = ((diffMs % 60000) / 1000).toFixed(0);
      return `+${minutes}m ${seconds}s`;
    }
  }

  getPopoverTitle(message: TransformedChatMessage): string {
    if (this.isCompactionMarker(message)) {
      return 'Context Compaction';
    }

    if (message?.role === 'system' || message?.originalRole === 'system') {
      return 'System Prompt';
    } else if (message?.role === 'tool' || message?.originalRole === 'tool') {
      return 'Tool Result';
    } else if (message?.role === 'model' || message?.role === 'assistant') {
      if (message.tool_calls && message.tool_calls.length > 0) {
        return 'Tool Calls & Message Details';
      } else {
        return 'AI Message Details';
      }
    }
    return 'Details';
  }
}
